import React, { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import SiteManager from "./SiteManager";
import Mailbox from "./Mailbox";
import ProposalDiscussion from "./ProposalDiscussion";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Mail, 
  Briefcase, 
  Settings, 
  Image, 
  Share2, 
  FileText,
  MessageSquare,
  Eye,
  EyeOff,
  Users,
  FolderOpen,
  CreditCard,
  TrendingUp,
  Send,
  Lightbulb,
  Calendar,
  Clock,
  LogOut,
  ChevronRight,
  Home,
  DollarSign,
  UserPlus,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  PauseCircle,
  BarChart3,
  Building,
  Phone,
  Link as LinkIcon,
  FileCheck,
  Upload,
  Paperclip,
  Download,
  User,
  Lock,
  Globe,
  MapPin,
  Hash,
  FileSpreadsheet,
  Inbox,
  Reply,
  Server,
  FileDown,
  UserCheck,
  UserX,
  Video,
  Award,
  Bell,
  Layout,
  Edit,
  Archive,
  RotateCcw,
  Shield,
  Palette,
  ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  de: {
  
    dashboard: "Dashboard",
    projects: "Projekte",
    users: "Benutzer",
    payments: "Zahlungen",
    subscriptions: "Abonnements",
    proposals: "Vorschläge",
    messages: "Nachrichten",
    myProjects: "Meine Projekte",
    settings: "Einstellungen",
    summary: "Zusammenfassung",
    
    // Auth
    login: "Anmelden",
    logout: "Abmelden",
    email: "E-Mail",
    password: "Passwort",
    forgotPassword: "Passwort vergessen?",
    backToLogin: "Zurück zum Login",
    sendLink: "Link senden",
    backToWebsite: "Zurück zur Website",
    clientPortal: "Client Portal",
    loginToAccount: "Melden Sie sich an",
    resetPassword: "Passwort zurücksetzen",
    enterEmail: "Geben Sie Ihre E-Mail ein",
    
    // Roles
    admin: "Admin",
    employee: "Mitarbeiter",
    client: "Kunde",
    
    // Dashboard
    welcome: "Willkommen",
    overview: "Hier ist eine Übersicht Ihrer Projekte und Zahlungen.",
    activeProjects: "Aktive Projekte",
    pendingPayments: "Ausstehende Zahlungen",
    activeSubscriptions: "Aktive Abos",
    totalClients: "Kunden",
    completedProjects: "Abgeschlossene Projekte",
    revenue: "Umsatz",
    newClientsMonth: "Neue Kunden (Monat)",
    affiliateEarnings: "Affiliate-Einnahmen",
    recentProjects: "Neueste Projekte",
    viewAll: "Alle anzeigen",
    noProjects: "Keine Projekte vorhanden",
    
    // Projects
    newProject: "Neues Projekt",
    editProject: "Projekt bearbeiten",
    projectTitle: "Projekttitel",
    description: "Beschreibung",
    selectClient: "Kunde wählen...",
    status: "Status",
    budget: "Budget",
    deadline: "Enddatum",
    startDate: "Startdatum",
    created: "Erstellt",
    deleteProject: "Projekt wirklich löschen?",
    
    // Status labels
    draft: "Entwurf",
    in_progress: "In Bearbeitung",
    review: "Review",
    completed: "Abgeschlossen",
    on_hold: "Pausiert",
    active: "Aktiv",
    inactive: "Inaktiv",
    pending: "Ausstehend",
    paid: "Bezahlt",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    
    // Chat
    projectChat: "Projekt-Chat",
    writeMessage: "Nachricht schreiben...",
    noMessages: "Noch keine Nachrichten",
    attachFile: "Datei anhängen",
    
    // Users
    newUser: "Neuer Benutzer",
    username: "Benutzername",
    company: "Unternehmen",
    phone: "Telefon",
    taxNumber: "Steuernummer",
    vatId: "USt-IdNr.",
    address: "Adresse",
    city: "Stadt",
    country: "Land",
    deleteUser: "Benutzer wirklich löschen?",
    
    // Payments
    newPaymentLink: "Neuer Zahlungslink",
    paymentTitle: "Bezeichnung",
    amount: "Betrag",
    currency: "Währung",
    dueDate: "Fällig",
    paymentUrl: "Zahlungslink URL",
    markAsPaid: "Als bezahlt markieren",
    payNow: "Jetzt bezahlen",
    noPayments: "Keine Zahlungen vorhanden",
    
    // Subscriptions
    newSubscription: "Neues Abo",
    subscriptionName: "Abo-Name",
    interval: "Intervall",
    monthly: "Monatlich",
    yearly: "Jährlich",
    services: "Services",
    nextBilling: "Nächste Abrechnung",
    pause: "Pausieren",
    activate: "Aktivieren",
    noSubscriptions: "Keine Abonnements vorhanden",
    deleteSubscription: "Abonnement wirklich löschen?",
    exportClients: "Kunden exportieren",
    notFound: "Seite nicht gefunden",
    notFoundText: "Die angeforderte Seite existiert nicht.",
    goBack: "Zurück",
    
    // Proposals
    newProposal: "Neuer Vorschlag",
    proposalTitle: "Titel des Vorschlags",
    proposalContent: "Beschreiben Sie Ihren Vorschlag, Ihre Frage oder Änderungswunsch...",
    submit: "Einreichen",
    approve: "Genehmigen",
    reject: "Ablehnen",
    noProposals: "Keine Vorschläge vorhanden",
    submitProposal: "Vorschlag einreichen",
    
    // Affiliate
    affiliateProgram: "Affiliate-Programm",
    affiliateDescription: "Empfehlen Sie uns weiter und erhalten Sie 10% Provision auf jeden geworbenen Kunden!",
    yourAffiliateLink: "Ihr Affiliate-Link:",
    copy: "Kopieren",
    linkCopied: "Link kopiert!",
    
    // Settings
    profile: "Profil",
    changePassword: "Passwort ändern",
    currentPassword: "Aktuelles Passwort",
    newPassword: "Neues Passwort",
    confirmPassword: "Passwort bestätigen",
    saveChanges: "Änderungen speichern",
    passwordChanged: "Passwort erfolgreich geändert!",
    profileUpdated: "Profil aktualisiert!",
    incorrectPassword: "Aktuelles Passwort ist falsch",
    
    // Summary
    clientSummary: "Kunden-Zusammenfassung",
    totalPaid: "Gesamt bezahlt",
    totalPending: "Gesamt ausstehend",
    monthlySubscriptions: "Monatliche Abos",
    yearlySubscriptions: "Jährliche Abos",
    yourDetails: "Ihre Daten",
    projectOverview: "Projektübersicht",
    paymentHistory: "Zahlungshistorie",
    
    // General
    loading: "Laden...",
    save: "Speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    details: "Details",
    optional: "optional",
    selectProject: "Projekt wählen...",
    projectDescription: "Projektbeschreibung",
    yourProjects: "Ihre Projekte",
    chooseProject: "Wählen Sie ein Projekt, um den Chat zu öffnen.",
    
    // Alerts
    pendingPaymentsAlert: "Ausstehende Zahlungen",
    noProjectsYet: "Noch keine Projekte vorhanden",
    
    // Language
    language: "Sprache",
    german: "Deutsch",
    english: "English",
    
    // Contact Inquiries
    contactInquiries: "Kontaktanfragen",
    inquiryFrom: "Anfrage von",
    replyToInquiry: "Antworten",
    inquiryStatus: "Status",
    newInquiry: "Neu",
    inProgress: "In Bearbeitung",
    replied: "Beantwortet",
    noInquiries: "Keine Anfragen vorhanden",
    
    // Documents
    documents: "Dokumente",
    newDocument: "Neues Dokument",
    letter: "Brief",
    offer: "Angebot",
    invoice: "Rechnung",
    statement: "Mitteilung",
    recipient: "Empfänger",
    subject: "Betreff",
    content: "Inhalt",
    items: "Positionen",
    generatePdf: "PDF erstellen",
    downloadPdf: "PDF herunterladen",
    approveDocument: "Genehmigen",
    sendDocument: "Versenden",
    documentStatus: "Dokument-Status",
    
    // Email Settings
    emailSettings: "E-Mail-Einstellungen",
    smtpHost: "SMTP Server",
    smtpPort: "SMTP Port",
    smtpUser: "SMTP Benutzer",
    smtpPassword: "SMTP Passwort",
    smtpFromEmail: "Absender E-Mail",
    smtpFromName: "Absender Name",
    emailSignature: "E-Mail-Signatur",
    testEmail: "Test-E-Mail senden",
    
    // Job Portal
    jobPortal: "Stellenportal",
    newJob: "Neue Stelle",
    editJob: "Stelle bearbeiten",
    applications: "Bewerbungen",
    applicant: "Bewerber",
    applicationStatus: "Bewerbungsstatus",
    reviewing: "In Prüfung",
    interview: "Interview",
    offerSent: "Angebot gesendet",
    hired: "Eingestellt",
    rejected: "Abgelehnt",
    sendEmail: "E-Mail senden",
    coverLetter: "Anschreiben",
    resume: "Lebenslauf",
    
    // Export
    exportData: "Daten exportieren",
    exportKpis: "KPIs exportieren",
    exportPayments: "Zahlungen exportieren",
    
    // Payment Reminders
    paymentReminders: "Zahlungserinnerungen",
    sendReminders: "Erinnerungen senden",
    remindersDescription: "Sendet automatisch E-Mails an Kunden mit ausstehenden oder bald fälligen Zahlungen",
    remindersSent: "Erinnerungen gesendet",
    noReminders: "Keine Erinnerungen vorhanden",
    overdue: "Überfällig",
    upcoming: "Bald fällig",
    reminderHistory: "Erinnerungsverlauf",
    
    // Site Management
    mainSite: "Hauptseite",
    mailbox: "E-Mail-Center",
    
    // Permissions
    permissions: "Berechtigungen",
    permHr: "HR & Bewerbungen",
    permDocuments: "Dokumente",
    permChat: "Kunden-Chat",
    permAffiliate: "Affiliate-Programm",
    permInquiries: "Kontaktanfragen",
    permClients: "Kunden verwalten",
    permFinances: "Finanzen",
    permProjects: "Projekte",
    permSiteManager: "Website verwalten",
    editProposal: "Bearbeiten",
    deleteProposal: "Löschen",
    edited: "bearbeitet",
    
    // Sidebar Categories
    finances: "Finanzen",
    catOverview: "Übersicht",
    catProjects: "Projekte & Kommunikation",
    catFinances: "Finanzen",
    catHr: "Personal & Anfragen",
    catAdmin: "Verwaltung",
    // Invoice Approval
    pendingReview: "Prüfung ausstehend",
    approved: "Genehmigt",
    sent: "Gesendet",
    draft: "Entwurf",
    
    // Archive
    archived: "Archiviert",
    activeItems: "Aktiv",
    archivedItems: "Archiv",
    restore: "Wiederherstellen",
    noArchivedItems: "Keine archivierten Einträge",
    
    // Notification Settings
    chatSoundEnabled: "Chat-Sound aktivieren",
    chatSoundDescription: "Spielt einen sanften Ton ab, wenn neue Nachrichten eingehen"
  },
  en: {
  
    dashboard: "Dashboard",
    projects: "Projects",
    users: "Users",
    payments: "Payments",
    subscriptions: "Subscriptions",
    proposals: "Proposals",
    messages: "Messages",
    myProjects: "My Projects",
    settings: "Settings",
    summary: "Summary",
    
    // Auth
    login: "Login",
    logout: "Logout",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    backToLogin: "Back to login",
    sendLink: "Send link",
    backToWebsite: "Back to website",
    clientPortal: "Client Portal",
    loginToAccount: "Sign in to your account",
    resetPassword: "Reset password",
    enterEmail: "Enter your email",
    
    // Roles
    admin: "Admin",
    employee: "Employee",
    client: "Client",
    
    // Dashboard
    welcome: "Welcome",
    overview: "Here is an overview of your projects and payments.",
    activeProjects: "Active Projects",
    pendingPayments: "Pending Payments",
    activeSubscriptions: "Active Subscriptions",
    totalClients: "Clients",
    completedProjects: "Completed Projects",
    revenue: "Revenue",
    newClientsMonth: "New Clients (Month)",
    affiliateEarnings: "Affiliate Earnings",
    recentProjects: "Recent Projects",
    viewAll: "View all",
    noProjects: "No projects available",
    
    // Projects
    newProject: "New Project",
    editProject: "Edit Project",
    projectTitle: "Project title",
    description: "Description",
    selectClient: "Select client...",
    status: "Status",
    budget: "Budget",
    deadline: "End Date",
    startDate: "Start Date",
    created: "Created",
    deleteProject: "Delete project?",
    
    // Status labels
    draft: "Draft",
    in_progress: "In Progress",
    review: "Review",
    completed: "Completed",
    on_hold: "On Hold",
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    paid: "Paid",
    approved: "Approved",
    rejected: "Rejected",
    
    // Chat
    projectChat: "Project Chat",
    writeMessage: "Write a message...",
    noMessages: "No messages yet",
    attachFile: "Attach file",
    
    // Users
    newUser: "New User",
    username: "Username",
    company: "Company",
    phone: "Phone",
    taxNumber: "Tax Number",
    vatId: "VAT ID",
    address: "Address",
    city: "City",
    country: "Country",
    deleteUser: "Delete user?",
    
    // Payments
    newPaymentLink: "New Payment Link",
    paymentTitle: "Title",
    amount: "Amount",
    currency: "Currency",
    dueDate: "Due",
    paymentUrl: "Payment URL",
    markAsPaid: "Mark as paid",
    payNow: "Pay now",
    noPayments: "No payments available",
    
    // Subscriptions
    newSubscription: "New Subscription",
    subscriptionName: "Subscription name",
    interval: "Interval",
    monthly: "Monthly",
    yearly: "Yearly",
    services: "Services",
    nextBilling: "Next billing",
    pause: "Pause",
    activate: "Activate",
    noSubscriptions: "No subscriptions available",
    deleteSubscription: "Really delete subscription?",
    exportClients: "Export Clients",
    notFound: "Page not found",
    notFoundText: "The requested page does not exist.",
    goBack: "Go back",
    
    // Proposals
    newProposal: "New Proposal",
    proposalTitle: "Proposal title",
    proposalContent: "Describe your proposal, question or change request...",
    submit: "Submit",
    approve: "Approve",
    reject: "Reject",
    noProposals: "No proposals available",
    submitProposal: "Submit proposal",
    
    // Affiliate
    affiliateProgram: "Affiliate Program",
    affiliateDescription: "Refer us and earn 10% commission on every new client!",
    yourAffiliateLink: "Your affiliate link:",
    copy: "Copy",
    linkCopied: "Link copied!",
    
    // Settings
    profile: "Profile",
    changePassword: "Change Password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    saveChanges: "Save changes",
    passwordChanged: "Password changed successfully!",
    profileUpdated: "Profile updated!",
    incorrectPassword: "Current password is incorrect",
    
    // Summary
    clientSummary: "Client Summary",
    totalPaid: "Total paid",
    totalPending: "Total pending",
    monthlySubscriptions: "Monthly subscriptions",
    yearlySubscriptions: "Yearly subscriptions",
    yourDetails: "Your details",
    projectOverview: "Project overview",
    paymentHistory: "Payment history",
    
    // General
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    details: "Details",
    optional: "optional",
    selectProject: "Select project...",
    projectDescription: "Project description",
    yourProjects: "Your Projects",
    chooseProject: "Select a project to open the chat.",
    
    // Alerts
    pendingPaymentsAlert: "Pending Payments",
    noProjectsYet: "No projects yet",
    
    // Language
    language: "Language",
    german: "Deutsch",
    english: "English",
    
    // Contact Inquiries
    contactInquiries: "Contact Inquiries",
    inquiryFrom: "Inquiry from",
    replyToInquiry: "Reply",
    inquiryStatus: "Status",
    newInquiry: "New",
    inProgress: "In Progress",
    replied: "Replied",
    noInquiries: "No inquiries available",
    
    // Documents
    documents: "Documents",
    newDocument: "New Document",
    letter: "Letter",
    offer: "Offer",
    invoice: "Invoice",
    statement: "Statement",
    recipient: "Recipient",
    subject: "Subject",
    content: "Content",
    items: "Items",
    generatePdf: "Generate PDF",
    downloadPdf: "Download PDF",
    approveDocument: "Approve",
    sendDocument: "Send",
    documentStatus: "Document Status",
    
    // Email Settings
    emailSettings: "Email Settings",
    smtpHost: "SMTP Server",
    smtpPort: "SMTP Port",
    smtpUser: "SMTP User",
    smtpPassword: "SMTP Password",
    smtpFromEmail: "From Email",
    smtpFromName: "From Name",
    emailSignature: "Email Signature",
    testEmail: "Send Test Email",
    
    // Job Portal
    jobPortal: "Job Portal",
    newJob: "New Job",
    editJob: "Edit Job",
    applications: "Applications",
    applicant: "Applicant",
    applicationStatus: "Application Status",
    reviewing: "Reviewing",
    interview: "Interview",
    offerSent: "Offer Sent",
    hired: "Hired",
    rejected: "Rejected",
    sendEmail: "Send Email",
    coverLetter: "Cover Letter",
    resume: "Resume",
    
    // Export
    exportData: "Export Data",
    exportKpis: "Export KPIs",
    exportPayments: "Export Payments",
    
    // Payment Reminders
    paymentReminders: "Payment Reminders",
    sendReminders: "Send Reminders",
    remindersDescription: "Automatically sends emails to clients with pending or upcoming payments",
    remindersSent: "Reminders sent",
    noReminders: "No reminders yet",
    overdue: "Overdue",
    upcoming: "Upcoming",
    reminderHistory: "Reminder History",
    
    // Site Management
    mainSite: "Main Site",
    mailbox: "Email Center",
    
    // Permissions
    permissions: "Permissions",
    permHr: "HR & Applications",
    permDocuments: "Documents",
    permChat: "Client Chat",
    permAffiliate: "Affiliate Program",
    permInquiries: "Contact Inquiries",
    permClients: "Manage Clients",
    permFinances: "Finances",
    permProjects: "Projects",
    permSiteManager: "Site Manager",
    editProposal: "Edit",
    deleteProposal: "Delete",
    edited: "edited",
    
    // Sidebar Categories
    finances: "Finances",
    catOverview: "Overview",
    catProjects: "Projects & Communication",
    catFinances: "Finances",
    catHr: "HR & Inquiries",
    catAdmin: "Administration",

    // Invoice Approval
    pendingReview: "Pending Review",
    approved: "Approved",
    sent: "Sent",
    draft: "Draft",
    
    // Archive
    archived: "Archived",
    activeItems: "Active",
    archivedItems: "Archive",
    restore: "Restore",
    noArchivedItems: "No archived items",
    
    // Notification Settings
    chatSoundEnabled: "Enable Chat Sound",
    chatSoundDescription: "Plays a soft sound when new messages arrive"
  },
  ar: {
    dashboard: "\u0644\u0648\u062d\u0629 \u0627\u0644\u062a\u062d\u0643\u0645",
    projects: "\u0627\u0644\u0645\u0634\u0627\u0631\u064a\u0639",
    users: "\u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645\u0648\u0646",
    payments: "\u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a",
    subscriptions: "\u0627\u0644\u0627\u0634\u062a\u0631\u0627\u0643\u0627\u062a",
    proposals: "\u0627\u0644\u0645\u0642\u062a\u0631\u062d\u0627\u062a",
    messages: "\u0627\u0644\u0631\u0633\u0627\u0626\u0644",
    myProjects: "\u0645\u0634\u0627\u0631\u064a\u0639\u064a",
    settings: "\u0627\u0644\u0625\u0639\u062f\u0627\u062f\u0627\u062a",
    summary: "\u0627\u0644\u0645\u0644\u062e\u0635",
    login: "\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644",
    logout: "\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062e\u0631\u0648\u062c",
    email: "\u0627\u0644\u0628\u0631\u064a\u062f \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a",
    password: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
    forgotPassword: "\u0646\u0633\u064a\u062a \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631\u061f",
    backToLogin: "\u0627\u0644\u0639\u0648\u062f\u0629 \u0644\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644",
    sendLink: "\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u0627\u0628\u0637",
    backToWebsite: "\u0627\u0644\u0639\u0648\u062f\u0629 \u0644\u0644\u0645\u0648\u0642\u0639",
    clientPortal: "\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0639\u0645\u0644\u0627\u0621",
    loginToAccount: "\u0633\u062c\u0644 \u0627\u0644\u062f\u062e\u0648\u0644 \u0625\u0644\u0649 \u062d\u0633\u0627\u0628\u0643",
    resetPassword: "\u0625\u0639\u0627\u062f\u0629 \u062a\u0639\u064a\u064a\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
    enterEmail: "\u0623\u062f\u062e\u0644 \u0628\u0631\u064a\u062f\u0643 \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a",
    admin: "\u0645\u062f\u064a\u0631",
    employee: "\u0645\u0648\u0638\u0641",
    client: "\u0639\u0645\u064a\u0644",
    welcome: "\u0645\u0631\u062d\u0628\u0627\u064b",
    overview: "\u0625\u0644\u064a\u0643 \u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629 \u0639\u0644\u0649 \u0645\u0634\u0627\u0631\u064a\u0639\u0643 \u0648\u0645\u062f\u0641\u0648\u0639\u0627\u062a\u0643.",
    activeProjects: "\u0645\u0634\u0627\u0631\u064a\u0639 \u0646\u0634\u0637\u0629",
    pendingPayments: "\u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0645\u0639\u0644\u0642\u0629",
    activeSubscriptions: "\u0627\u0634\u062a\u0631\u0627\u0643\u0627\u062a \u0646\u0634\u0637\u0629",
    totalClients: "\u0627\u0644\u0639\u0645\u0644\u0627\u0621",
    completedProjects: "\u0645\u0634\u0627\u0631\u064a\u0639 \u0645\u0643\u062a\u0645\u0644\u0629",
    revenue: "\u0627\u0644\u0625\u064a\u0631\u0627\u062f\u0627\u062a",
    newClientsMonth: "\u0639\u0645\u0644\u0627\u0621 \u062c\u062f\u062f (\u0627\u0644\u0634\u0647\u0631)",
    affiliateEarnings: "\u0623\u0631\u0628\u0627\u062d \u0627\u0644\u0625\u062d\u0627\u0644\u0629",
    recentProjects: "\u0623\u062d\u062f\u062b \u0627\u0644\u0645\u0634\u0627\u0631\u064a\u0639",
    viewAll: "\u0639\u0631\u0636 \u0627\u0644\u0643\u0644",
    noProjects: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0634\u0627\u0631\u064a\u0639",
    newProject: "\u0645\u0634\u0631\u0648\u0639 \u062c\u062f\u064a\u062f",
    editProject: "\u062a\u0639\u062f\u064a\u0644 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
    projectTitle: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
    description: "\u0627\u0644\u0648\u0635\u0641",
    selectClient: "\u0627\u062e\u062a\u0631 \u0639\u0645\u064a\u0644\u0627\u064b...",
    status: "\u0627\u0644\u062d\u0627\u0644\u0629",
    budget: "\u0627\u0644\u0645\u064a\u0632\u0627\u0646\u064a\u0629",
    deadline: "تاريخ الانتهاء",
    startDate: "تاريخ البدء",
    created: "\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0625\u0646\u0634\u0627\u0621",
    deleteProject: "\u062d\u0630\u0641 \u0627\u0644\u0645\u0634\u0631\u0648\u0639\u061f",
    draft: "\u0645\u0633\u0648\u062f\u0629",
    in_progress: "\u0642\u064a\u062f \u0627\u0644\u062a\u0646\u0641\u064a\u0630",
    review: "\u0645\u0631\u0627\u062c\u0639\u0629",
    completed: "\u0645\u0643\u062a\u0645\u0644",
    on_hold: "\u0645\u0648\u0642\u0648\u0641",
    active: "\u0646\u0634\u0637",
    inactive: "\u063a\u064a\u0631 \u0646\u0634\u0637",
    pending: "\u0645\u0639\u0644\u0642",
    paid: "\u0645\u062f\u0641\u0648\u0639",
    approved: "\u0645\u0648\u0627\u0641\u0642 \u0639\u0644\u064a\u0647",
    rejected: "\u0645\u0631\u0641\u0648\u0636",
    projectChat: "\u0645\u062d\u0627\u062f\u062b\u0629 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
    writeMessage: "\u0627\u0643\u062a\u0628 \u0631\u0633\u0627\u0644\u0629...",
    noMessages: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0631\u0633\u0627\u0626\u0644 \u0628\u0639\u062f",
    attachFile: "\u0625\u0631\u0641\u0627\u0642 \u0645\u0644\u0641",
    newUser: "\u0645\u0633\u062a\u062e\u062f\u0645 \u062c\u062f\u064a\u062f",
    username: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645",
    company: "\u0627\u0644\u0634\u0631\u0643\u0629",
    phone: "\u0627\u0644\u0647\u0627\u062a\u0641",
    taxNumber: "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0636\u0631\u064a\u0628\u064a",
    vatId: "\u0631\u0642\u0645 \u0636\u0631\u064a\u0628\u0629 \u0627\u0644\u0642\u064a\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629",
    address: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
    city: "\u0627\u0644\u0645\u062f\u064a\u0646\u0629",
    country: "\u0627\u0644\u062f\u0648\u0644\u0629",
    deleteUser: "\u062d\u0630\u0641 \u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645\u061f",
    newPaymentLink: "\u0631\u0627\u0628\u0637 \u062f\u0641\u0639 \u062c\u062f\u064a\u062f",
    paymentTitle: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
    amount: "\u0627\u0644\u0645\u0628\u0644\u063a",
    currency: "\u0627\u0644\u0639\u0645\u0644\u0629",
    dueDate: "\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0627\u0633\u062a\u062d\u0642\u0627\u0642",
    paymentUrl: "\u0631\u0627\u0628\u0637 \u0627\u0644\u062f\u0641\u0639",
    markAsPaid: "\u062a\u062d\u062f\u064a\u062f \u0643\u0645\u062f\u0641\u0648\u0639",
    payNow: "\u0627\u062f\u0641\u0639 \u0627\u0644\u0622\u0646",
    noPayments: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u062f\u0641\u0648\u0639\u0627\u062a",
    newSubscription: "\u0627\u0634\u062a\u0631\u0627\u0643 \u062c\u062f\u064a\u062f",
    subscriptionName: "\u0627\u0633\u0645 \u0627\u0644\u0627\u0634\u062a\u0631\u0627\u0643",
    interval: "\u0627\u0644\u0641\u062a\u0631\u0629",
    monthly: "\u0634\u0647\u0631\u064a",
    yearly: "\u0633\u0646\u0648\u064a",
    services: "\u0627\u0644\u062e\u062f\u0645\u0627\u062a",
    nextBilling: "\u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629 \u0627\u0644\u062a\u0627\u0644\u064a\u0629",
    pause: "\u0625\u064a\u0642\u0627\u0641",
    activate: "\u062a\u0641\u0639\u064a\u0644",
    noSubscriptions: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0627\u0634\u062a\u0631\u0627\u0643\u0627\u062a",
    deleteSubscription: "حذف الاشتراك؟",
    exportClients: "تصدير العملاء",
    notFound: "الصفحة غير موجودة",
    notFoundText: "الصفحة المطلوبة غير موجودة.",
    goBack: "رجوع",
    newProposal: "\u0645\u0642\u062a\u0631\u062d \u062c\u062f\u064a\u062f",
    proposalTitle: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0642\u062a\u0631\u062d",
    proposalContent: "\u0635\u0641 \u0645\u0642\u062a\u0631\u062d\u0643 \u0623\u0648 \u0633\u0624\u0627\u0644\u0643 \u0623\u0648 \u0637\u0644\u0628 \u0627\u0644\u062a\u063a\u064a\u064a\u0631...",
    submit: "\u0625\u0631\u0633\u0627\u0644",
    approve: "\u0645\u0648\u0627\u0641\u0642\u0629",
    reject: "\u0631\u0641\u0636",
    noProposals: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0642\u062a\u0631\u062d\u0627\u062a",
    submitProposal: "\u062a\u0642\u062f\u064a\u0645 \u0645\u0642\u062a\u0631\u062d",
    affiliateProgram: "\u0628\u0631\u0646\u0627\u0645\u062c \u0627\u0644\u0625\u062d\u0627\u0644\u0629",
    affiliateDescription: "\u0623\u0648\u0635\u0650 \u0628\u0646\u0627 \u0648\u0627\u062d\u0635\u0644 \u0639\u0644\u0649 \u0639\u0645\u0648\u0644\u0629 10% \u0639\u0644\u0649 \u0643\u0644 \u0639\u0645\u064a\u0644 \u062c\u062f\u064a\u062f!",
    yourAffiliateLink: "\u0631\u0627\u0628\u0637 \u0627\u0644\u0625\u062d\u0627\u0644\u0629 \u0627\u0644\u062e\u0627\u0635 \u0628\u0643:",
    copy: "\u0646\u0633\u062e",
    linkCopied: "\u062a\u0645 \u0646\u0633\u062e \u0627\u0644\u0631\u0627\u0628\u0637!",
    profile: "\u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0634\u062e\u0635\u064a",
    changePassword: "\u062a\u063a\u064a\u064a\u0631 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
    currentPassword: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062d\u0627\u0644\u064a\u0629",
    newPassword: "\u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 \u062c\u062f\u064a\u062f\u0629",
    confirmPassword: "\u062a\u0623\u0643\u064a\u062f \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
    saveChanges: "\u062d\u0641\u0638 \u0627\u0644\u062a\u063a\u064a\u064a\u0631\u0627\u062a",
    passwordChanged: "\u062a\u0645 \u062a\u063a\u064a\u064a\u0631 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0628\u0646\u062c\u0627\u062d!",
    profileUpdated: "\u062a\u0645 \u062a\u062d\u062f\u064a\u062b \u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0634\u062e\u0635\u064a!",
    incorrectPassword: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062d\u0627\u0644\u064a\u0629 \u063a\u064a\u0631 \u0635\u062d\u064a\u062d\u0629",
    clientSummary: "\u0645\u0644\u062e\u0635 \u0627\u0644\u0639\u0645\u064a\u0644",
    totalPaid: "\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u062f\u0641\u0648\u0639",
    totalPending: "\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u0639\u0644\u0642",
    monthlySubscriptions: "\u0627\u0634\u062a\u0631\u0627\u0643\u0627\u062a \u0634\u0647\u0631\u064a\u0629",
    yearlySubscriptions: "\u0627\u0634\u062a\u0631\u0627\u0643\u0627\u062a \u0633\u0646\u0648\u064a\u0629",
    yourDetails: "\u0628\u064a\u0627\u0646\u0627\u062a\u0643",
    projectOverview: "\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629 \u0639\u0644\u0649 \u0627\u0644\u0645\u0634\u0627\u0631\u064a\u0639",
    paymentHistory: "\u0633\u062c\u0644 \u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a",
    loading: "\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...",
    save: "\u062d\u0641\u0638",
    cancel: "\u0625\u0644\u063a\u0627\u0621",
    delete: "\u062d\u0630\u0641",
    edit: "\u062a\u0639\u062f\u064a\u0644",
    details: "\u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644",
    optional: "\u0627\u062e\u062a\u064a\u0627\u0631\u064a",
    selectProject: "\u0627\u062e\u062a\u0631 \u0645\u0634\u0631\u0648\u0639\u0627\u064b...",
    projectDescription: "\u0648\u0635\u0641 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
    yourProjects: "\u0645\u0634\u0627\u0631\u064a\u0639\u0643",
    chooseProject: "\u0627\u062e\u062a\u0631 \u0645\u0634\u0631\u0648\u0639\u0627\u064b \u0644\u0641\u062a\u062d \u0627\u0644\u0645\u062d\u0627\u062f\u062b\u0629.",
    pendingPaymentsAlert: "\u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0645\u0639\u0644\u0642\u0629",
    noProjectsYet: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0634\u0627\u0631\u064a\u0639 \u0628\u0639\u062f",
    language: "\u0627\u0644\u0644\u063a\u0629",
    german: "Deutsch",
    english: "English",
    arabic: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629",
    contactInquiries: "\u0627\u0633\u062a\u0641\u0633\u0627\u0631\u0627\u062a \u0627\u0644\u062a\u0648\u0627\u0635\u0644",
    inquiryFrom: "\u0627\u0633\u062a\u0641\u0633\u0627\u0631 \u0645\u0646",
    replyToInquiry: "\u0631\u062f",
    inquiryStatus: "\u0627\u0644\u062d\u0627\u0644\u0629",
    newInquiry: "\u062c\u062f\u064a\u062f",
    inProgress: "\u0642\u064a\u062f \u0627\u0644\u062a\u0646\u0641\u064a\u0630",
    replied: "\u062a\u0645 \u0627\u0644\u0631\u062f",
    noInquiries: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0627\u0633\u062a\u0641\u0633\u0627\u0631\u0627\u062a",
    documents: "\u0627\u0644\u0645\u0633\u062a\u0646\u062f\u0627\u062a",
    newDocument: "\u0645\u0633\u062a\u0646\u062f \u062c\u062f\u064a\u062f",
    letter: "\u0631\u0633\u0627\u0644\u0629",
    offer: "\u0639\u0631\u0636",
    invoice: "\u0641\u0627\u062a\u0648\u0631\u0629",
    statement: "\u0628\u064a\u0627\u0646",
    recipient: "\u0627\u0644\u0645\u0633\u062a\u0644\u0645",
    subject: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
    content: "\u0627\u0644\u0645\u062d\u062a\u0648\u0649",
    items: "\u0627\u0644\u0628\u0646\u0648\u062f",
    generatePdf: "\u0625\u0646\u0634\u0627\u0621 PDF",
    downloadPdf: "\u062a\u062d\u0645\u064a\u0644 PDF",
    approveDocument: "\u0645\u0648\u0627\u0641\u0642\u0629",
    sendDocument: "\u0625\u0631\u0633\u0627\u0644",
    documentStatus: "\u062d\u0627\u0644\u0629 \u0627\u0644\u0645\u0633\u062a\u0646\u062f",
    emailSettings: "\u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u0628\u0631\u064a\u062f",
    smtpHost: "\u062e\u0627\u062f\u0645 SMTP",
    smtpPort: "\u0645\u0646\u0641\u0630 SMTP",
    smtpUser: "\u0645\u0633\u062a\u062e\u062f\u0645 SMTP",
    smtpPassword: "\u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 SMTP",
    smtpFromEmail: "\u0628\u0631\u064a\u062f \u0627\u0644\u0645\u0631\u0633\u0644",
    smtpFromName: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0631\u0633\u0644",
    emailSignature: "\u062a\u0648\u0642\u064a\u0639 \u0627\u0644\u0628\u0631\u064a\u062f",
    testEmail: "\u0625\u0631\u0633\u0627\u0644 \u0628\u0631\u064a\u062f \u062a\u062c\u0631\u064a\u0628\u064a",
    jobPortal: "\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0648\u0638\u0627\u0626\u0641",
    newJob: "\u0648\u0638\u064a\u0641\u0629 \u062c\u062f\u064a\u062f\u0629",
    editJob: "\u062a\u0639\u062f\u064a\u0644 \u0627\u0644\u0648\u0638\u064a\u0641\u0629",
    applications: "\u0627\u0644\u0637\u0644\u0628\u0627\u062a",
    applicant: "\u0627\u0644\u0645\u062a\u0642\u062f\u0645",
    applicationStatus: "\u062d\u0627\u0644\u0629 \u0627\u0644\u0637\u0644\u0628",
    reviewing: "\u0642\u064a\u062f \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629",
    interview: "\u0645\u0642\u0627\u0628\u0644\u0629",
    offerSent: "\u062a\u0645 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0639\u0631\u0636",
    hired: "\u062a\u0645 \u0627\u0644\u062a\u0648\u0638\u064a\u0641",
    sendEmail: "\u0625\u0631\u0633\u0627\u0644 \u0628\u0631\u064a\u062f",
    coverLetter: "\u062e\u0637\u0627\u0628 \u0627\u0644\u062a\u0642\u062f\u064a\u0645",
    resume: "\u0627\u0644\u0633\u064a\u0631\u0629 \u0627\u0644\u0630\u0627\u062a\u064a\u0629",
    exportData: "\u062a\u0635\u062f\u064a\u0631 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a",
    exportKpis: "\u062a\u0635\u062f\u064a\u0631 \u0645\u0624\u0634\u0631\u0627\u062a \u0627\u0644\u0623\u062f\u0627\u0621",
    exportPayments: "\u062a\u0635\u062f\u064a\u0631 \u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a",
    paymentReminders: "\u062a\u0630\u0643\u064a\u0631\u0627\u062a \u0627\u0644\u062f\u0641\u0639",
    sendReminders: "\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u062a\u0630\u0643\u064a\u0631\u0627\u062a",
    remindersDescription: "\u0627\u062e\u062a\u0631 \u062f\u0641\u0639\u0629 \u0645\u0639\u0644\u0642\u0629 \u0648\u0623\u0631\u0633\u0644 \u062a\u0630\u0643\u064a\u0631\u0627\u064b \u0641\u0631\u062f\u064a\u0627\u064b \u0644\u0644\u0639\u0645\u064a\u0644.",
    remindersSent: "\u062a\u0645 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u062a\u0630\u0643\u064a\u0631\u0627\u062a",
    noReminders: "\u0644\u0627 \u062a\u0648\u062c\u062f \u062a\u0630\u0643\u064a\u0631\u0627\u062a",
    overdue: "\u0645\u062a\u0623\u062e\u0631",
    upcoming: "\u0642\u0627\u062f\u0645",
    reminderHistory: "\u0633\u062c\u0644 \u0627\u0644\u062a\u0630\u0643\u064a\u0631\u0627\u062a",
    mainSite: "\u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0631\u0626\u064a\u0633\u064a",
    mailbox: "\u0645\u0631\u0643\u0632 \u0627\u0644\u0628\u0631\u064a\u062f",
    finances: "\u0627\u0644\u0645\u0627\u0644\u064a\u0629",
    catOverview: "\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629",
    catProjects: "\u0627\u0644\u0645\u0634\u0627\u0631\u064a\u0639 \u0648\u0627\u0644\u062a\u0648\u0627\u0635\u0644",
    catFinances: "\u0627\u0644\u0645\u0627\u0644\u064a\u0629",
    catHr: "\u0627\u0644\u0645\u0648\u0627\u0631\u062f \u0627\u0644\u0628\u0634\u0631\u064a\u0629 \u0648\u0627\u0644\u0627\u0633\u062a\u0641\u0633\u0627\u0631\u0627\u062a",
    catAdmin: "\u0627\u0644\u0625\u062f\u0627\u0631\u0629",
    pendingReview: "\u0628\u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629",
    sent: "\u0645\u0631\u0633\u0644",
    archived: "\u0645\u0624\u0631\u0634\u0641",
    activeItems: "\u0646\u0634\u0637",
    archivedItems: "\u0627\u0644\u0623\u0631\u0634\u064a\u0641",
    restore: "\u0627\u0633\u062a\u0639\u0627\u062f\u0629",
    noArchivedItems: "\u0644\u0627 \u062a\u0648\u062c\u062f \u0639\u0646\u0627\u0635\u0631 \u0645\u0624\u0631\u0634\u0641\u0629",
    chatSoundEnabled: "تفعيل صوت المحادثة",
    chatSoundDescription: "يشغل صوتًا ناعمًا عند وصول رسائل جديدة"
  }
};

// Language Context
const LanguageContext = createContext();
const useLanguage = () => useContext(LanguageContext);

// Auth Context
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem("portal_token"));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (error) {
      logout();
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    sessionStorage.setItem("portal_token", res.data.token);
    setToken(res.data.token);
    const userRes = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${res.data.token}` }
    });
    setUser(userRes.data);
    return res.data;
  };

  const logout = () => {
    sessionStorage.removeItem("portal_token");
    setToken(null);
    setUser(null);
  };

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const authValue = useMemo(() => ({ user, token, login, logout, loading, updateUser }), [user, token, loading, updateUser]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper
const api = {
  get: (url, token) => axios.get(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } }),
  post: (url, data, token) => axios.post(`${API}${url}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  put: (url, data, token) => axios.put(`${API}${url}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  delete: (url, token) => axios.delete(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } }),
  upload: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }
};

// Timezone-aware date formatting
const formatPortalDate = (dateStr, timezone = "Europe/Berlin", options = {}) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const defaults = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: timezone };
    // Always use de-DE locale for consistent date format (DD.MM.YYYY)
    return date.toLocaleString("de-DE", { ...defaults, ...options });
  } catch (e) {
    return new Date(dateStr).toLocaleString("de-DE");
  }
};

// Short date format: 04.12.2026 (always with dots, German style)
const formatPortalDateShort = (dateStr, timezone = "Europe/Berlin") => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    // Always use de-DE locale to ensure dots (.) not slashes (/)
    return date.toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone });
  } catch (e) {
    return new Date(dateStr).toLocaleDateString("de-DE");
  }
};

// Date with time and timezone indicator: 04.12.2026, 18:00 (Europe/Berlin)
// If time is midnight (00:00 UTC), shows only date with timezone
const formatPortalDateTime = (dateStr, timezone = "Europe/Berlin", showTimezone = true) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone });
    const timeFormatted = date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: timezone });
    const tzShort = timezone.split("/").pop().replace(/_/g, " ");
    
    // Check if original date string has time component or if it's just a date
    const hasExplicitTime = dateStr.includes("T") && !dateStr.endsWith("T00:00:00") && !dateStr.endsWith("T00:00:00.000Z");
    
    if (hasExplicitTime || (date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0)) {
      // Has time component - show full datetime
      return showTimezone ? `${dateFormatted}, ${timeFormatted} (${tzShort})` : `${dateFormatted}, ${timeFormatted}`;
    } else {
      // Date only - show date with timezone indicator
      return showTimezone ? `${dateFormatted} (${tzShort})` : dateFormatted;
    }
  } catch (e) {
    return new Date(dateStr).toLocaleString("de-DE");
  }
};

// Login Component
const LoginPage = () => {
  const { login } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  
  // Password reset with token
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Check for reset token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Simple Math CAPTCHA
  const [captchaNum1] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [captchaNum2] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verify CAPTCHA
    if (parseInt(captchaAnswer) !== captchaNum1 + captchaNum2) {
      toast.error(lang === "de" ? "Falsche CAPTCHA-Antwort" : "Incorrect CAPTCHA answer");
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success(lang === "de" ? "Erfolgreich angemeldet!" : "Successfully logged in!");
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === "de" ? "Login fehlgeschlagen" : "Login failed"));
    }
    setLoading(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset`, { email });
      toast.success(lang === "de" ? "Falls die E-Mail existiert, wird ein Reset-Link gesendet." : "If the email exists, a reset link will be sent.");
      setResetMode(false);
    } catch (error) {
      toast.error(lang === "de" ? "Fehler beim Zurücksetzen" : "Error resetting password");
    }
    setLoading(false);
  };

  // Handle setting new password with token
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error(lang === "de" ? "Passwort muss mindestens 8 Zeichen haben" : "Password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(lang === "de" ? "Passwörter stimmen nicht überein" : "Passwords do not match");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success(lang === "de" ? "Passwort erfolgreich geändert! Sie können sich jetzt anmelden." : "Password changed successfully! You can now log in.");
      setResetToken(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "Invalid or expired token") {
        toast.error(lang === "de" ? "Ungültiger oder abgelaufener Link. Bitte fordern Sie einen neuen an." : "Invalid or expired link. Please request a new one.");
      } else if (detail === "Token expired") {
        toast.error(lang === "de" ? "Der Link ist abgelaufen. Bitte fordern Sie einen neuen an." : "The link has expired. Please request a new one.");
      } else {
        toast.error(lang === "de" ? "Fehler beim Ändern des Passworts" : "Error changing password");
      }
    }
    setLoading(false);
  };

  // If we have a reset token, show the new password form
  if (resetToken) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4 gap-2">
            {[{code: "en", label: "EN"}, {code: "de", label: "DE"}, {code: "ar", label: "AR"}].map(l => (
              <button key={l.code}
                onClick={() => setLang(l.code)}
                className={`font-heading text-sm transition-colors ${lang === l.code ? "text-white" : "text-mmg-silver-chalice hover:text-white"}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a href="/" className="inline-flex items-center gap-2 text-mmg-silver-chalice hover:text-white mb-8 transition-colors">
            <ArrowLeft size={20} />
            <span className="font-heading text-sm uppercase tracking-widest">{t.backToWebsite}</span>
          </a>
          
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Mahzod Media Group" className="h-16 w-auto mx-auto mb-6 invert" />
            <h1 className="font-heading font-bold text-3xl text-white">
              {lang === "de" ? "Neues Passwort festlegen" : lang === "ar" ? "تعيين كلمة مرور جديدة" : "Set New Password"}
            </h1>
            <p className="font-body text-mmg-silver-chalice mt-2">
              {lang === "de" ? "Geben Sie Ihr neues Passwort ein" : lang === "ar" ? "أدخل كلمة المرور الجديدة" : "Enter your new password"}
            </p>
          </div>

          <form onSubmit={handleSetNewPassword} className="space-y-6">
            <div>
              <label className="block font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-2">
                {lang === "de" ? "Neues Passwort" : lang === "ar" ? "كلمة المرور الجديدة" : "New Password"}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full py-3 px-4 bg-transparent border-b border-mmg-mine-shaft text-white font-body focus:outline-none focus:border-white transition-colors"
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-mmg-silver-chalice hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-mmg-silver-chalice mt-1">
                {lang === "de" ? "Mindestens 8 Zeichen" : lang === "ar" ? "8 أحرف على الأقل" : "At least 8 characters"}
              </p>
            </div>

            <div>
              <label className="block font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-2">
                {lang === "de" ? "Passwort bestätigen" : lang === "ar" ? "تأكيد كلمة المرور" : "Confirm Password"}
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full py-3 px-4 bg-transparent border-b border-mmg-mine-shaft text-white font-body focus:outline-none focus:border-white transition-colors"
                placeholder="••••••••"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-white text-black font-heading font-bold uppercase tracking-widest hover:bg-mmg-silver-chalice transition-colors disabled:opacity-50"
              data-testid="set-password-btn"
            >
              {loading ? "..." : (lang === "de" ? "Passwort speichern" : lang === "ar" ? "حفظ كلمة المرور" : "Save Password")}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setResetToken(null)}
              className="text-mmg-silver-chalice hover:text-white text-sm"
            >
              {lang === "de" ? "Zurück zum Login" : lang === "ar" ? "العودة لتسجيل الدخول" : "Back to Login"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="w-full max-w-md">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4 gap-2">
          {[{code: "en", label: "EN"}, {code: "de", label: "DE"}, {code: "ar", label: "AR"}].map(l => (
            <button key={l.code}
              onClick={() => setLang(l.code)}
              className={`font-heading text-sm transition-colors ${lang === l.code ? "text-white" : "text-mmg-silver-chalice hover:text-white"}`}
              data-testid={`login-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <a href="/" className="inline-flex items-center gap-2 text-mmg-silver-chalice hover:text-white mb-8 transition-colors">
          <ArrowLeft size={20} />
          <span className="font-heading text-sm uppercase tracking-widest">{t.backToWebsite}</span>
        </a>
        
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Mahzod Media Group" className="h-16 w-auto mx-auto mb-6 invert" />
          <h1 className="font-heading font-bold text-3xl text-white">
            {resetMode ? t.resetPassword : t.clientPortal}
          </h1>
          <p className="font-body text-mmg-silver-chalice mt-2">
            {resetMode ? t.enterEmail : t.loginToAccount}
          </p>
        </div>
        
        {resetMode ? (
          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.email}
                required
                className="w-full py-4 px-0 font-body text-base text-white bg-transparent border-b border-mmg-mine-shaft focus:border-white outline-none transition-colors placeholder:text-mmg-silver-chalice"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full border border-white bg-white text-black py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50"
            >
              {loading ? "..." : t.sendLink}
            </button>
            <button
              type="button"
              onClick={() => setResetMode(false)}
              className="w-full text-mmg-silver-chalice hover:text-white font-body text-sm transition-colors"
            >
              {t.backToLogin}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.email}
                required
                className="w-full py-4 px-0 font-body text-base text-white bg-transparent border-b border-mmg-mine-shaft focus:border-white outline-none transition-colors placeholder:text-mmg-silver-chalice"
              />
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.password}
                required
                className="w-full py-4 px-0 pr-12 font-body text-base text-white bg-transparent border-b border-mmg-mine-shaft focus:border-white outline-none transition-colors placeholder:text-mmg-silver-chalice"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-mmg-silver-chalice hover:text-white p-2"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {/* CAPTCHA */}
            <div className="flex items-center gap-4">
              <span className="text-mmg-silver-chalice font-body text-sm whitespace-nowrap">
                {captchaNum1} + {captchaNum2} =
              </span>
              <input
                type="number"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="?"
                required
                className="w-20 py-2 px-3 font-body text-base text-white bg-transparent border border-mmg-mine-shaft focus:border-white outline-none transition-colors text-center"
                data-testid="login-captcha-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full border border-white bg-white text-black py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50"
            >
              {loading ? "..." : t.login}
            </button>
            <button
              type="button"
              onClick={() => setResetMode(true)}
              className="w-full text-mmg-silver-chalice hover:text-white font-body text-sm transition-colors"
            >
              {t.forgotPassword}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Dashboard Layout
const DashboardLayout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState({});
  
  const isAdmin = user?.role === "admin" || user?.role === "employee";

  useEffect(() => {
    if (!isAdmin || !token) return;
    const fetchCounts = async () => {
      try {
        const res = await api.get("/notifications/counts", token);
        setBadgeCounts(res.data);
      } catch (e) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, token]);

  const badgeMap = {
    "/portal/inquiries": badgeCounts.inquiries || 0,
    "/portal/applications": badgeCounts.applications || 0,
    "/portal/messages": badgeCounts.messages || 0,
  };
  
  // Check if user has permission (owner has all, or check specific permission)
  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.is_owner) return true;
    if (user.permissions?.includes("all")) return true;
    return user.permissions?.includes(perm) || false;
  };

  // Build navigation based on permissions
  const buildAdminNavigation = () => {
    const nav = [
      { category: t.catOverview, items: [
        { name: t.dashboard, href: "/portal", icon: Home },
      ]},
    ];

    // Projects & Communication - check 'projects' or 'chat' permission
    const projectItems = [];
    if (hasPermission("projects")) {
      projectItems.push({ name: t.projects, href: "/portal/projects", icon: FolderOpen });
      projectItems.push({ name: t.proposals, href: "/portal/proposals", icon: Lightbulb });
    }
    if (hasPermission("chat") || hasPermission("projects")) {
      projectItems.push({ name: t.messages, href: "/portal/messages", icon: MessageSquare });
    }
    if (projectItems.length > 0) {
      nav.push({ category: t.catProjects, items: projectItems });
    }

    // Finances - check 'finances' permission
    const financeItems = [];
    if (hasPermission("finances")) {
      financeItems.push({ name: t.finances, href: "/portal/finances", icon: CreditCard });
      financeItems.push({ name: t.paymentReminders, href: "/portal/reminders", icon: Bell });
    }
    if (hasPermission("documents")) {
      financeItems.push({ name: t.documents, href: "/portal/documents", icon: FileText });
    }
    if (hasPermission("affiliate")) {
      financeItems.push({ name: t.affiliateProgram, href: "/portal/affiliate", icon: Share2 });
    }
    if (financeItems.length > 0) {
      nav.push({ category: t.catFinances, items: financeItems });
    }

    // HR - check 'hr' or 'inquiries' permission
    const hrItems = [];
    if (hasPermission("inquiries")) {
      hrItems.push({ name: t.contactInquiries, href: "/portal/inquiries", icon: Inbox });
    }
    if (hasPermission("hr")) {
      hrItems.push({ name: t.jobPortal, href: "/portal/jobs", icon: Briefcase });
      hrItems.push({ name: t.applications, href: "/portal/applications", icon: UserCheck });
    }
    if (hrItems.length > 0) {
      nav.push({ category: t.catHr, items: hrItems });
    }

    // Admin - check 'clients' or owner permission
    const adminItems = [];
    if (hasPermission("clients") || user?.is_owner) {
      adminItems.push({ name: t.users, href: "/portal/users", icon: Users });
    }
    if (hasPermission("site_manager")) {
      adminItems.push({ name: t.mainSite, href: "/portal/site", icon: Layout });
    }
    // Mailbox for admins/employees with email permission
    if (hasPermission("documents") || hasPermission("chat") || user?.is_owner) {
      adminItems.push({ name: t.mailbox, href: "/portal/mailbox", icon: Send });
    }
    if (user?.is_owner) {
      adminItems.push({ name: lang === "de" ? "Firma" : "Company", href: "/portal/company-settings", icon: Building });
      adminItems.push({ name: t.emailSettings, href: "/portal/email-settings", icon: Mail });
      adminItems.push({ name: lang === "de" ? "E-Mail Design" : "Email Design", href: "/portal/email-template", icon: Palette });
      adminItems.push({ name: lang === "de" ? "Zahlungen" : "Payments", href: "/portal/payment-settings", icon: CreditCard });
      adminItems.push({ name: lang === "de" ? "Rechnungsnr." : "Invoice #", href: "/portal/invoice-numbering", icon: FileText });
    }
    adminItems.push({ name: t.settings, href: "/portal/settings", icon: Settings });
    
    nav.push({ category: t.catAdmin, items: adminItems });

    return nav;
  };
  
  const navigation = isAdmin ? buildAdminNavigation() : [
    { category: t.catOverview, items: [
      { name: t.dashboard, href: "/portal", icon: Home },
      { name: t.summary, href: "/portal/summary", icon: FileSpreadsheet },
    ]},
    { category: t.catProjects, items: [
      { name: t.myProjects, href: "/portal/projects", icon: FolderOpen },
    ]},
    { category: t.catFinances, items: [
      { name: t.finances, href: "/portal/finances", icon: CreditCard },
      { name: t.affiliateProgram, href: "/portal/affiliate", icon: Share2 },
    ]},
    { category: "", items: [
      { name: t.settings, href: "/portal/settings", icon: Settings },
    ]},
  ];

  const currentPath = window.location.pathname;
  const roleLabels = { admin: t.admin, employee: t.employee, client: t.client };
  const isRTL = lang === "ar";

  return (
    <div className="min-h-screen bg-black" dir={isRTL ? "rtl" : "ltr"}>
      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`lg:hidden fixed top-4 ${isRTL ? 'right-4' : 'left-4'} z-50 p-2 bg-mmg-cod-gray border border-mmg-mine-shaft text-white`}
      >
        {sidebarOpen ? <X size={24} /> : <Settings size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 ${isRTL ? 'right-0 border-l' : 'left-0 border-r'} z-40 w-64 bg-mmg-cod-gray border-mmg-mine-shaft transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full' : '-translate-x-full')} ${isRTL ? 'lg:translate-x-0' : 'lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-mmg-mine-shaft">
            <a href="/">
              <img src="/logo.png" alt="Mahzod" className="h-10 w-auto invert" />
            </a>
          </div>

          {/* Language Switcher */}
          <div className="px-4 pt-4 flex gap-1">
            {[{code: "en", label: "EN"}, {code: "de", label: "DE"}, {code: "ar", label: "AR"}].map(l => (
              <button key={l.code}
                onClick={() => setLang(l.code)}
                className={`flex-1 px-2 py-2 font-heading text-sm uppercase tracking-widest transition-colors ${lang === l.code ? "text-white bg-mmg-mine-shaft" : "text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft"}`}
                data-testid={`portal-lang-${l.code}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
            {navigation.map((group) => (
              <div key={group.category || group.items[0]?.href || 'nav-group'}>
                {group.category && (
                  <p className="px-4 pb-1 pt-3 font-heading text-[10px] uppercase tracking-[0.2em] text-mmg-boulder">{group.category}</p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`flex items-center gap-3 px-4 py-2.5 font-heading text-sm uppercase tracking-widest transition-colors ${
                        currentPath === item.href
                          ? "bg-white text-black"
                          : "text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft"
                      }`}
                    >
                      <item.icon size={16} />
                      <span className="flex-1 text-xs">{item.name}</span>
                      {badgeMap[item.href] > 0 && (
                        <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold px-1" data-testid={`badge-${item.href.split('/').pop()}`}>
                          {badgeMap[item.href]}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-mmg-mine-shaft">
            <div className="mb-3">
              <p className="font-heading text-sm text-white">{user?.username}</p>
              <p className="font-body text-xs text-mmg-silver-chalice">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 bg-mmg-mine-shaft text-xs font-heading uppercase text-mmg-silver-chalice">
                {roleLabels[user?.role]}
              </span>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-4 py-2 font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice hover:text-white hover:bg-mmg-mine-shaft transition-colors"
            >
              <LogOut size={16} />
              {t.logout}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`${isRTL ? 'lg:mr-64' : 'lg:ml-64'} min-h-screen`}>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// KPI Card
const KPICard = ({ title, value, icon: Icon, trend, color = "white" }) => (
  <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 hover:border-mmg-boulder transition-colors">
    <div className="flex items-start justify-between mb-4">
      <div className={`w-12 h-12 flex items-center justify-center border border-mmg-mine-shaft`}>
        <Icon size={24} className="text-white" />
      </div>
      {trend && (
        <span className={`font-heading text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="font-heading text-3xl text-white mb-1">{value}</p>
    <p className="font-body text-sm text-mmg-silver-chalice">{title}</p>
  </div>
);

// Status Badge
const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  
  const styles = {
    draft: "bg-mmg-boulder text-white",
    in_progress: "bg-blue-600 text-white",
    review: "bg-yellow-600 text-white",
    completed: "bg-green-600 text-white",
    on_hold: "bg-red-600 text-white",
    active: "bg-green-600 text-white",
    inactive: "bg-mmg-boulder text-white",
    pending: "bg-yellow-600 text-white",
    paid: "bg-green-600 text-white",
    approved: "bg-green-600 text-white",
    rejected: "bg-red-600 text-white"
  };

  return (
    <span className={`px-2 py-1 text-xs font-heading uppercase ${styles[status] || "bg-mmg-mine-shaft text-white"}`}>
      {t[status] || status}
    </span>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [kpis, setKpis] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [kpisRes, projectsRes] = await Promise.all([
        api.get("/kpis", token),
        api.get("/projects", token)
      ]);
      setKpis(kpisRes.data);
      setRecentProjects(projectsRes.data.slice(0, 5));
    } catch (error) {
    }
    setLoading(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.dashboard}</h1>
        <button onClick={fetchData} className="p-2 text-mmg-silver-chalice hover:text-white">
          <RefreshCw size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title={t.totalClients} value={kpis?.total_clients || 0} icon={Users} />
        <KPICard title={t.activeProjects} value={kpis?.active_projects || 0} icon={FolderOpen} />
        <KPICard title={t.revenue} value={`€${(kpis?.total_revenue || 0).toLocaleString()}`} icon={TrendingUp} />
        <KPICard title={t.pendingPayments} value={`€${(kpis?.pending_payments || 0).toLocaleString()}`} icon={CreditCard} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title={t.completedProjects} value={kpis?.completed_projects || 0} icon={CheckCircle} />
        <KPICard title={t.activeSubscriptions} value={kpis?.active_subscriptions || 0} icon={RefreshCw} />
        <KPICard title={t.newClientsMonth} value={kpis?.new_clients_month || 0} icon={UserPlus} />
        <KPICard title={t.affiliateEarnings} value={`€${(kpis?.affiliate_earnings || 0).toLocaleString()}`} icon={Share2} />
      </div>

      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-xl text-white">{t.recentProjects}</h2>
          <a href="/portal/projects" className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice hover:text-white flex items-center gap-2">
            {t.viewAll} <ChevronRight size={16} />
          </a>
        </div>
        <div className="space-y-3">
          {recentProjects.map((project) => (
            <a
              key={project.id}
              href={`/portal/projects/${project.id}`}
              className="flex items-center justify-between p-4 bg-black border border-mmg-mine-shaft hover:border-mmg-boulder transition-colors"
            >
              <div>
                <p className="font-heading text-white">{project.title}</p>
                <p className="font-body text-sm text-mmg-silver-chalice">{project.client_name}</p>
              </div>
              <StatusBadge status={project.status} />
            </a>
          ))}
          {recentProjects.length === 0 && (
            <p className="text-mmg-silver-chalice text-center py-8">{t.noProjects}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Client Dashboard
const ClientDashboard = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, paymentsRes, subsRes] = await Promise.all([
        api.get("/projects", token),
        api.get("/payment-links", token),
        api.get("/subscriptions", token)
      ]);
      setProjects(projectsRes.data);
      setPayments(paymentsRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
    }
    setLoading(false);
  };

  const pendingPayments = payments.filter(p => p.status === "pending");
  const activeProjects = projects.filter(p => ["draft", "in_progress", "review"].includes(p.status));

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading font-bold text-3xl text-white">{t.welcome}, {user?.username}</h1>
        <p className="font-body text-mmg-silver-chalice mt-2">{t.overview}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title={t.activeProjects} value={activeProjects.length} icon={FolderOpen} />
        <KPICard title={t.pendingPayments} value={pendingPayments.length} icon={CreditCard} />
        <KPICard title={t.activeSubscriptions} value={subscriptions.filter(s => s.status === "active").length} icon={RefreshCw} />
      </div>

      {pendingPayments.length > 0 && (
        <div className="bg-yellow-600/20 border border-yellow-600 p-6">
          <div className="flex items-start gap-4">
            <AlertCircle size={24} className="text-yellow-500 flex-shrink-0" />
            <div>
              <h3 className="font-heading text-white mb-2">{t.pendingPaymentsAlert}</h3>
              <div className="space-y-2">
                {pendingPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between">
                    <span className="font-body text-mmg-silver-chalice">{payment.title}</span>
                    <a 
                      href={payment.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-heading text-sm uppercase text-yellow-500 hover:text-yellow-400"
                    >
                      €{payment.amount.toLocaleString()} {lang === "de" ? "bezahlen" : "pay"} →
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-6">{t.yourProjects}</h2>
        <div className="space-y-3">
          {projects.map((project) => (
            <a
              key={project.id}
              href={`/portal/projects/${project.id}`}
              className="flex items-center justify-between p-4 bg-black border border-mmg-mine-shaft hover:border-mmg-boulder transition-colors"
            >
              <div>
                <p className="font-heading text-white">{project.title}</p>
                <p className="font-body text-sm text-mmg-silver-chalice">{project.description?.substring(0, 50)}...</p>
              </div>
              <StatusBadge status={project.status} />
            </a>
          ))}
          {projects.length === 0 && (
            <p className="text-mmg-silver-chalice text-center py-8">{t.noProjectsYet}</p>
          )}
        </div>
      </div>

      {user?.affiliate_code && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
          <h2 className="font-heading font-bold text-xl text-white mb-4">{t.affiliateProgram}</h2>
          <p className="font-body text-mmg-silver-chalice mb-4">{t.affiliateDescription}</p>
          <div className="flex items-center gap-4 p-4 bg-black border border-mmg-mine-shaft">
            <span className="font-body text-mmg-silver-chalice">{t.yourAffiliateLink}</span>
            <code className="font-mono text-white flex-1 text-sm truncate">
              {window.location.origin}?ref={user.affiliate_code}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}?ref=${user.affiliate_code}`);
                toast.success(t.linkCopied);
              }}
              className="px-4 py-2 bg-white text-black font-heading text-sm uppercase hover:bg-mmg-silver transition-colors"
            >
              {t.copy}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Projects List
const ProjectsList = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    client_id: "",
    status: "draft",
    budget: "",
    currency: "EUR",
    start_date: "",
    deadline: ""
  });

  const isAdmin = user?.role === "admin" || user?.role === "employee";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const projectsRes = await api.get("/projects", token);
      setProjects(projectsRes.data);
      if (isAdmin) {
        const usersRes = await api.get("/users", token);
        setUsers(usersRes.data.filter(u => u.role === "client"));
      }
    } catch (error) {
    }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      const data = { ...formData, budget: formData.budget ? parseFloat(formData.budget) : null };
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, data, token);
        toast.success(lang === "de" ? "Projekt aktualisiert!" : "Project updated!");
      } else {
        await api.post("/projects", data, token);
        toast.success(lang === "de" ? "Projekt erstellt!" : "Project created!");
      }
      setShowForm(false);
      setEditingProject(null);
      setFormData({ title: "", description: "", client_id: "", status: "draft", budget: "", currency: "EUR", start_date: "", deadline: "" });
      fetchData();
    } catch (error) {
      toast.error(lang === "de" ? "Fehler beim Speichern" : "Error saving");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.deleteProject)) {
      try {
        await api.delete(`/projects/${id}`, token);
        toast.success(lang === "de" ? "Projekt gelöscht!" : "Project deleted!");
        fetchData();
      } catch (error) {
        toast.error(lang === "de" ? "Fehler beim Löschen" : "Error deleting");
      }
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      client_id: project.client_id,
      status: project.status,
      budget: project.budget || "",
      currency: project.currency || "EUR",
      start_date: project.start_date || "",
      deadline: project.deadline || ""
    });
    setShowForm(true);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.projects}</h1>
        {isAdmin && (
          <button
            onClick={() => { setShowForm(true); setEditingProject(null); setFormData({ title: "", description: "", client_id: "", status: "draft", budget: "", currency: "EUR", start_date: "", deadline: "" }); }}
            className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
          >
            <Plus size={16} />
            {t.newProject}
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingProject ? t.editProject : t.newProject}</h3>
            <button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice hover:text-white">
              <X size={20} />
            </button>
          </div>
          
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder={t.projectTitle}
            className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
          />
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder={t.description}
            rows={4}
            className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body resize-none"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({...formData, client_id: e.target.value})}
              className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
            >
              <option value="">{t.selectClient}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
            </select>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
            >
              <option value="draft">{t.draft}</option>
              <option value="in_progress">{t.in_progress}</option>
              <option value="review">{t.review}</option>
              <option value="completed">{t.completed}</option>
              <option value="on_hold">{t.on_hold}</option>
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.budget}</label>
              <div className="flex">
                <select
                  value={formData.currency || "EUR"}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="py-3 px-2 bg-black border border-mmg-mine-shaft text-white font-body border-r-0"
                >
                  <option value="EUR">€</option>
                  <option value="USD">$</option>
                  <option value="GBP">£</option>
                  <option value="CHF">CHF</option>
                  <option value="AED">AED</option>
                  <option value="SAR">SAR</option>
                </select>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  placeholder="0.00"
                  className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.startDate || "Startdatum"}</label>
              <input
                type="date"
                value={formData.start_date || ""}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
              />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{t.deadline}</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
              />
            </div>
          </div>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
          >
            <Save size={18} />
            {t.save}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {projects.map((project) => (
          <div key={project.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-start justify-between">
              <a href={`/portal/projects/${project.id}`} className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-lg text-white">{project.title}</h3>
                  <StatusBadge status={project.status} />
                </div>
                <p className="font-body text-sm text-mmg-silver-chalice mb-2">{project.description?.substring(0, 100)}...</p>
                <div className="flex items-center gap-4 text-xs text-mmg-boulder">
                  {isAdmin && <span className="flex items-center gap-1"><Users size={12} /> {project.client_name}</span>}
                  {project.budget && <span className="flex items-center gap-1">€{project.budget.toLocaleString()}</span>}
                  {project.deadline && <span className="flex items-center gap-1"><Calendar size={12} /> {formatPortalDateShort(project.deadline, user?.timezone)}</span>}
                </div>
              </a>
              {isAdmin && (
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => handleEdit(project)} className="p-2 text-mmg-silver-chalice hover:text-white">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(project.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {projects.length === 0 && (
          <p className="text-mmg-silver-chalice text-center py-12">{t.noProjects}</p>
        )}
      </div>
    </div>
  );
};

// Project Detail with Chat
const ProjectDetail = ({ projectId }) => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [proposalFormData, setProposalFormData] = useState({ title: "", content: "" });
  const [selectedProposal, setSelectedProposal] = useState(null); // For discussion modal
  const chatRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const notifAudioRef = useRef(null);

  const isAdmin = user?.role === "admin" || user?.role === "employee";

  useEffect(() => {
    if (!notifAudioRef.current) {
      // Soft, gentle notification sound
      notifAudioRef.current = new Audio("/notification.wav");
      notifAudioRef.current.volume = 0.5; // 50% volume for softer sound
    }
  }, []);

  const playNotifSound = () => {
    try {
      // Only play sound if user has enabled it (default: true)
      if (user?.chat_sound_enabled === false) return;
      if (notifAudioRef.current) {
        notifAudioRef.current.currentTime = 0;
        notifAudioRef.current.play().catch(() => {});
      }
    } catch (e) {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, messagesRes, proposalsRes] = await Promise.all([
        api.get(`/projects/${projectId}`, token),
        api.get(`/projects/${projectId}/chat`, token),
        api.get(`/proposals?project_id=${projectId}`, token)
      ]);
      setProject(projectRes.data);
      setMessages(messagesRes.data);
      setProposals(proposalsRes.data || []);
      prevMsgCountRef.current = messagesRes.data.length;
      if (isAdmin) {
        api.post(`/projects/${projectId}/chat/mark-read`, {}, token).catch(() => {});
      }
    } catch (error) {
    }
    setLoading(false);
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/chat`, token);
      const newMsgs = res.data;
      if (prevMsgCountRef.current > 0 && newMsgs.length > prevMsgCountRef.current) {
        const lastMsg = newMsgs[newMsgs.length - 1];
        if (lastMsg && lastMsg.sender_id !== user?.id) {
          playNotifSound();
        }
      }
      prevMsgCountRef.current = newMsgs.length;
      setMessages(newMsgs);
      if (isAdmin) {
        api.post(`/projects/${projectId}/chat/mark-read`, {}, token).catch(() => {});
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (e, attachmentUrl = null) => {
    e?.preventDefault();
    if (!newMessage.trim() && !attachmentUrl) return;
    
    setSending(true);
    try {
      await api.post(`/projects/${projectId}/chat`, {
        project_id: projectId,
        content: newMessage || (attachmentUrl ? `📎 ${lang === "de" ? "Datei angehängt" : "File attached"}` : ""),
        attachment_url: attachmentUrl
      }, token);
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error(lang === "de" ? "Fehler beim Senden" : "Error sending");
    }
    setSending(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await api.upload(file, token);
      await handleSendMessage(null, result.url);
      toast.success(lang === "de" ? "Datei hochgeladen!" : "File uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === "de" ? "Fehler beim Hochladen" : "Upload failed"));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;
  if (!project) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Projekt nicht gefunden" : "Project not found"}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <a href="/portal/projects" className="text-mmg-silver-chalice hover:text-white">
          <ArrowLeft size={24} />
        </a>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-3xl text-white">{project.title}</h1>
            <StatusBadge status={project.status} />
          </div>
          {isAdmin && <p className="font-body text-mmg-silver-chalice">{t.client}: {project.client_name}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <h2 className="font-heading font-bold text-lg text-white mb-4">{t.projectDescription}</h2>
            <p className="font-body text-mmg-silver-chalice whitespace-pre-wrap">{project.description}</p>
          </div>

          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft">
            <div className="p-4 border-b border-mmg-mine-shaft">
              <h2 className="font-heading font-bold text-lg text-white">{t.projectChat}</h2>
            </div>
            <div ref={chatRef} className="h-96 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] ${msg.user_id === user?.id ? 'bg-white text-black' : 'bg-mmg-mine-shaft text-white'} p-3`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-heading text-xs uppercase">{msg.username}</span>
                      <span className={`text-xs ${msg.user_id === user?.id ? 'text-mmg-boulder' : 'text-mmg-silver-chalice'}`}>
                        {msg.role === "admin" ? t.admin : msg.role === "employee" ? t.employee : ""}
                      </span>
                    </div>
                    <p className="font-body text-sm">{msg.content}</p>
                    {msg.attachment_url && (
                      <a 
                        href={`${BACKEND_URL}${msg.attachment_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 mt-2 text-xs ${msg.user_id === user?.id ? 'text-blue-600' : 'text-blue-400'} hover:underline`}
                      >
                        <Download size={12} />
                        {lang === "de" ? "Datei herunterladen" : "Download file"}
                      </a>
                    )}
                    <p className={`text-xs mt-1 ${msg.user_id === user?.id ? 'text-mmg-boulder' : 'text-mmg-silver-chalice'}`}>
                      {formatPortalDate(msg.created_at, user?.timezone)}
                    </p>
                  </div>
                </div>
              ))}
              {messages.length === 0 && (
                <p className="text-mmg-silver-chalice text-center py-8">{t.noMessages}</p>
              )}
            </div>
            <form onSubmit={handleSendMessage} className="p-4 border-t border-mmg-mine-shaft flex gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="p-3 text-mmg-silver-chalice hover:text-white border border-mmg-mine-shaft hover:border-white transition-colors disabled:opacity-50"
                title={t.attachFile}
              >
                {uploading ? <RefreshCw size={18} className="animate-spin" /> : <Paperclip size={18} />}
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t.writeMessage}
                className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body"
              />
              <button
                type="submit"
                disabled={sending}
                className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-mmg-silver transition-colors disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <h2 className="font-heading font-bold text-lg text-white">{t.details}</h2>
            {project.budget && (
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.budget}</p>
                <p className="font-body text-white">€{project.budget.toLocaleString()}</p>
              </div>
            )}
            {project.deadline && (
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.deadline}</p>
                <p className="font-body text-white">{formatPortalDateTime(project.deadline, user?.timezone, true)}</p>
              </div>
            )}
            <div>
              <p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.created}</p>
              <p className="font-body text-white">{formatPortalDateShort(project.created_at, user?.timezone)}</p>
            </div>
          </div>

          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <h2 className="font-heading font-bold text-lg text-white mb-4">{t.payments}</h2>
            <div className="space-y-3">
              {project.payment_links?.map((link) => (
                <div key={link.id} className="p-3 bg-black border border-mmg-mine-shaft">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading text-sm text-white">{link.title}</span>
                    <StatusBadge status={link.status} />
                  </div>
                  <p className="font-body text-lg text-white">€{link.amount.toLocaleString()}</p>
                  {link.status === "pending" && (
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 font-heading text-xs uppercase text-mmg-silver-chalice hover:text-white"
                    >
                      {t.payNow} →
                    </a>
                  )}
                </div>
              ))}
              {(!project.payment_links || project.payment_links.length === 0) && (
                <p className="text-mmg-silver-chalice text-sm">{t.noPayments}</p>
              )}
            </div>
          </div>

          {/* Proposals Section */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg text-white flex items-center gap-2">
                <Lightbulb size={18} /> {t.proposals}
              </h2>
              {user?.role === "client" && (
                <button
                  onClick={() => setShowProposalForm(true)}
                  className="inline-flex items-center gap-2 text-sm font-heading uppercase text-mmg-silver-chalice hover:text-white"
                >
                  <Plus size={16} /> {lang === "de" ? "Vorschlag einreichen" : "Submit Proposal"}
                </button>
              )}
            </div>
            
            {showProposalForm && (
              <div className="mb-4 p-4 bg-black border border-mmg-mine-shaft space-y-3">
                <input
                  type="text"
                  value={proposalFormData.title}
                  onChange={(e) => setProposalFormData({...proposalFormData, title: e.target.value})}
                  placeholder={lang === "de" ? "Titel des Vorschlags" : "Proposal Title"}
                  className="w-full py-2 px-3 bg-mmg-cod-gray border border-mmg-mine-shaft text-white text-sm"
                />
                <textarea
                  value={proposalFormData.content}
                  onChange={(e) => setProposalFormData({...proposalFormData, content: e.target.value})}
                  placeholder={lang === "de" ? "Beschreiben Sie Ihren Vorschlag..." : "Describe your proposal..."}
                  className="w-full py-2 px-3 bg-mmg-cod-gray border border-mmg-mine-shaft text-white text-sm min-h-[100px]"
                />
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await api.post("/proposals", { ...proposalFormData, project_id: projectId }, token);
                        toast.success(lang === "de" ? "Vorschlag eingereicht!" : "Proposal submitted!");
                        setShowProposalForm(false);
                        setProposalFormData({ title: "", content: "" });
                        fetchData();
                      } catch (e) { toast.error("Error"); }
                    }}
                    className="px-4 py-2 bg-white text-black font-heading text-sm uppercase hover:bg-black hover:text-white border border-white"
                  >
                    {lang === "de" ? "Einreichen" : "Submit"}
                  </button>
                  <button
                    onClick={() => { setShowProposalForm(false); setProposalFormData({ title: "", content: "" }); }}
                    className="px-4 py-2 text-mmg-silver-chalice hover:text-white font-heading text-sm uppercase"
                  >
                    {lang === "de" ? "Abbrechen" : "Cancel"}
                  </button>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {proposals.map((proposal) => (
                <div key={proposal.id} className="p-3 bg-black border border-mmg-mine-shaft">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm text-white">{proposal.title}</span>
                      {proposal.edited && <span className="text-xs text-mmg-boulder italic">({t.edited})</span>}
                      {proposal.reply_count > 0 && (
                        <span className="text-xs bg-mmg-mine-shaft text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                          <MessageSquare size={10} /> {proposal.reply_count}
                        </span>
                      )}
                    </div>
                    <StatusBadge status={proposal.status} />
                  </div>
                  <p className="font-body text-sm text-mmg-silver-chalice mb-2">{proposal.content}</p>
                  <p className="text-xs text-mmg-boulder">
                    {formatPortalDateShort(proposal.created_at, user?.timezone)}
                    {proposal.client_name && isAdmin && <span> · {proposal.client_name}</span>}
                  </p>
                  
                  {/* View Discussion Button */}
                  <button
                    onClick={() => setSelectedProposal(proposal)}
                    className="mt-3 inline-flex items-center gap-2 text-xs font-heading uppercase text-mmg-silver-chalice hover:text-white"
                  >
                    <MessageSquare size={14} /> 
                    {lang === "de" ? "Diskussion öffnen" : "View Discussion"}
                  </button>
                  
                  {/* Admin Actions (kept for legacy) */}
                  {isAdmin && proposal.status === "pending" && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={async () => {
                          await api.put(`/proposals/${proposal.id}/status?status=approved`, {}, token);
                          toast.success(lang === "de" ? "Genehmigt!" : "Approved!");
                          fetchData();
                        }}
                        className="text-xs text-green-500 hover:text-green-400"
                      >
                        {lang === "de" ? "Genehmigen" : "Approve"}
                      </button>
                      <button
                        onClick={async () => {
                          await api.put(`/proposals/${proposal.id}/status?status=rejected`, {}, token);
                          toast.success(lang === "de" ? "Abgelehnt" : "Rejected");
                          fetchData();
                        }}
                        className="text-xs text-red-500 hover:text-red-400"
                      >
                        {lang === "de" ? "Ablehnen" : "Reject"}
                      </button>
                    </div>
                  )}
                  
                  {/* Client Edit/Delete - Only for pending proposals owned by client */}
                  {!isAdmin && proposal.client_id === user?.id && proposal.status === "pending" && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => {
                          const newTitle = prompt(lang === "de" ? "Neuer Titel:" : "New title:", proposal.title);
                          const newContent = prompt(lang === "de" ? "Neue Beschreibung:" : "New description:", proposal.content);
                          if (newTitle && newContent) {
                            api.put(`/proposals/${proposal.id}/client-edit`, { title: newTitle, content: newContent }, token)
                              .then(() => { toast.success(lang === "de" ? "Aktualisiert!" : "Updated!"); fetchData(); })
                              .catch(() => toast.error("Error"));
                          }
                        }}
                        className="text-xs text-mmg-silver-chalice hover:text-white flex items-center gap-1"
                      >
                        <Edit size={12} /> {t.editProposal}
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm(lang === "de" ? "Vorschlag wirklich löschen?" : "Really delete proposal?")) {
                            try {
                              await api.delete(`/proposals/${proposal.id}/client-delete`, token);
                              toast.success(lang === "de" ? "Gelöscht!" : "Deleted!");
                              fetchData();
                            } catch (e) { toast.error("Error"); }
                          }
                        }}
                        className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1"
                      >
                        <Trash2 size={12} /> {t.deleteProposal}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {proposals.length === 0 && (
                <p className="text-mmg-silver-chalice text-sm">{t.noProposals}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Proposal Discussion Modal */}
      {selectedProposal && (
        <ProposalDiscussion
          proposal={selectedProposal}
          token={token}
          user={user}
          lang={lang}
          onClose={() => setSelectedProposal(null)}
          onUpdate={() => {
            fetchData(); // Refresh proposals list
            setSelectedProposal(null);
          }}
        />
      )}
    </div>
  );
};

// Users Management
const UsersManagement = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "", email: "", password: "", role: "client",
    company: "", phone: "", tax_number: "", vat_id: "",
    address: "", city: "", country: "", timezone: "Europe/Berlin",
    permissions: []
  });

  const timezoneOptions = [
    "Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/London", "Europe/Paris",
    "Europe/Amsterdam", "Europe/Brussels", "Europe/Rome", "Europe/Madrid", "Europe/Istanbul",
    "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Sao_Paulo",
    "Asia/Dubai", "Asia/Riyadh", "Asia/Tokyo", "Asia/Shanghai", "Asia/Kolkata",
    "Australia/Sydney", "Africa/Cairo", "Pacific/Auckland", "UTC"
  ];

  const permissionOptions = [
    { key: "hr", label: t.permHr },
    { key: "documents", label: t.permDocuments },
    { key: "chat", label: t.permChat },
    { key: "affiliate", label: t.permAffiliate },
    { key: "inquiries", label: t.permInquiries },
    { key: "clients", label: t.permClients },
    { key: "finances", label: t.permFinances },
    { key: "projects", label: t.permProjects },
    { key: "site_manager", label: t.permSiteManager }
  ];

  const isOwner = user?.is_owner || (user?.permissions && user?.permissions.includes("all"));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try { const res = await api.get("/users", token); setUsers(res.data); } catch (error) { }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({ username: "", email: "", password: "", role: "client", company: "", phone: "", tax_number: "", vat_id: "", address: "", city: "", country: "", timezone: "Europe/Berlin", permissions: [] });
    setEditingUser(null);
    setShowForm(false);
  };

  const togglePermission = (perm) => {
    const current = formData.permissions || [];
    if (current.includes(perm)) {
      setFormData({ ...formData, permissions: current.filter(p => p !== perm) });
    } else {
      setFormData({ ...formData, permissions: [...current, perm] });
    }
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editingUser.id}`, payload, token);
        toast.success(lang === "de" ? "Benutzer aktualisiert!" : "User updated!");
      } else {
        await api.post("/auth/register", formData, token);
        toast.success(lang === "de" ? "Benutzer erstellt!" : "User created!");
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({
      username: u.username || "", email: u.email || "", password: "",
      role: u.role || "client", company: u.company || "", phone: u.phone || "",
      tax_number: u.tax_number || "", vat_id: u.vat_id || "",
      address: u.address || "", city: u.city || "", country: u.country || "",
      timezone: u.timezone || "Europe/Berlin",
      permissions: u.permissions || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.deleteUser)) {
      try {
        await api.delete(`/users/${id}`, token);
        toast.success(lang === "de" ? "Benutzer gelöscht!" : "User deleted!");
        fetchData();
      } catch (error) { toast.error("Error"); }
    }
  };

  const handleExportClients = () => {
    const clients = users.filter(u => u.role === "client");
    if (clients.length === 0) {
      toast.error(lang === "de" ? "Keine Kunden zum Exportieren" : "No clients to export");
      return;
    }
    
    const headers = ["Name", "E-Mail", "Firma", "Telefon", "Adresse", "Stadt", "Land", "Steuernummer", "USt-IdNr."];
    const rows = clients.map(c => [
      c.username || "",
      c.email || "",
      c.company || "",
      c.phone || "",
      c.address || "",
      c.city || "",
      c.country || "",
      c.tax_number || "",
      c.vat_id || ""
    ]);
    
    const csvContent = [headers.join(";"), ...rows.map(r => r.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `kunden_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(lang === "de" ? "Export erfolgreich!" : "Export successful!");
  };

  const roleLabels = { admin: t.admin, employee: t.employee, client: t.client };
  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6" data-testid="users-management">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.users}</h1>
        {user?.role === "admin" && (
          <div className="flex items-center gap-3">
            {(isOwner || user?.permissions?.includes("clients")) && (
              <button onClick={handleExportClients} className="inline-flex items-center gap-2 border border-mmg-mine-shaft text-mmg-silver-chalice px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:border-white hover:text-white transition-all duration-300" data-testid="export-clients-btn">
                <Download size={16} /> {t.exportClients}
              </button>
            )}
            <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300" data-testid="new-user-btn">
              <Plus size={16} /> {t.newUser}
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingUser ? (lang === "de" ? "Benutzer bearbeiten" : "Edit User") : t.newUser}</h3>
            <button onClick={resetForm} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} placeholder={t.username} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="user-form-name" />
            <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder={t.email} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="user-form-email" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder={editingUser ? (lang === "de" ? "Neues Passwort (leer = nicht ändern)" : "New password (empty = no change)") : t.password} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="user-form-password" />
            </div>
            <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="user-form-role">
              <option value="client">{t.client}</option>
              <option value="employee">{t.employee}</option>
              {user?.role === "admin" && <option value="admin">{t.admin}</option>}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} placeholder={`${t.company} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder={`${t.phone} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" value={formData.tax_number} onChange={(e) => setFormData({...formData, tax_number: e.target.value})} placeholder={`${t.taxNumber} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.vat_id} onChange={(e) => setFormData({...formData, vat_id: e.target.value})} placeholder={`${t.vatId} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder={`${t.address} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder={`${t.city} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} placeholder={`${t.country} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-mmg-silver-chalice text-sm mb-1 font-heading">{lang === "de" ? "Zeitzone" : "Timezone"}</label>
              <select value={formData.timezone} onChange={(e) => setFormData({...formData, timezone: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="user-form-timezone">
                {timezoneOptions.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </div>
          
          {/* Permissions Section - Only visible to owner for admin/employee users */}
          {isOwner && (formData.role === "admin" || formData.role === "employee") && !editingUser?.is_owner && (
            <div className="border border-mmg-mine-shaft p-4 mt-4">
              <h3 className="font-heading text-white mb-3 flex items-center gap-2">
                <Shield size={18} /> {t.permissions}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {permissionOptions.map(perm => (
                  <label key={perm.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions?.includes(perm.key) || false}
                      onChange={() => togglePermission(perm.key)}
                      className="w-4 h-4 accent-white"
                    />
                    <span className="text-sm text-mmg-silver-chalice hover:text-white">{perm.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-mmg-boulder mt-2">
                {lang === "de" ? "Wählen Sie die Bereiche, auf die dieser Benutzer zugreifen darf." : "Select areas this user can access."}
              </p>
            </div>
          )}
          
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300" data-testid="save-user-btn">
            <Save size={18} /> {t.save}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors" data-testid={`user-row-${u.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-mmg-mine-shaft">
                  <User size={24} className="text-mmg-silver-chalice" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-white">{u.username}</h3>
                    <span className={`px-2 py-0.5 text-xs font-heading uppercase ${u.role === "admin" ? "bg-white text-black" : u.role === "employee" ? "bg-mmg-boulder text-white" : "bg-mmg-mine-shaft text-white"}`}>
                      {roleLabels[u.role]}
                    </span>
                  </div>
                  <p className="font-body text-sm text-mmg-silver-chalice">{u.email}</p>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs text-mmg-boulder">
                    {u.company && <span className="flex items-center gap-1"><Building size={10} /> {u.company}</span>}
                    {u.vat_id && <span className="flex items-center gap-1"><Hash size={10} /> {u.vat_id}</span>}
                    {u.city && <span className="flex items-center gap-1"><MapPin size={10} /> {u.city}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user?.role === "admin" && (
                  <button onClick={() => handleEdit(u)} className="p-2 text-mmg-silver-chalice hover:text-white" data-testid={`edit-user-${u.id}`}>
                    <Edit size={18} />
                  </button>
                )}
                {user?.role === "admin" && u.id !== user?.id && (
                  <button onClick={() => handleDelete(u.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-user-${u.id}`}>
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Payments Management
const PaymentsManagement = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const defaultPayForm = { project_id: "", title: "", amount: "", currency: "EUR", url: "", due_date: "", send_email: false, payment_method: "link" };
  const [formData, setFormData] = useState({...defaultPayForm});

  const isAdmin = user?.role === "admin" || user?.role === "employee";

  // Payment method presets
  const paymentMethods = {
    link: { label: lang === "de" ? "Zahlungslink" : "Payment Link", placeholder: "https://pay.example.com/..." },
    stripe: { label: "Stripe", placeholder: "https://checkout.stripe.com/..." },
    paypal: { label: "PayPal", placeholder: "https://paypal.me/..." },
    wise: { label: "Wise (TransferWise)", placeholder: "https://wise.com/pay/..." },
    bank: { label: lang === "de" ? "Banküberweisung" : "Bank Transfer", placeholder: "" },
    klarna: { label: "Klarna", placeholder: "https://..." },
    other: { label: lang === "de" ? "Sonstiges" : "Other", placeholder: "https://..." }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, projectsRes] = await Promise.all([
        api.get("/payment-links", token),
        api.get("/projects", token)
      ]);
      setPayments(paymentsRes.data);
      setProjects(projectsRes.data);
    } catch (error) { }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      // P0 Fix: Ensure URL has protocol
      let urlToSave = formData.url;
      if (urlToSave && !urlToSave.startsWith("http://") && !urlToSave.startsWith("https://")) {
        urlToSave = "https://" + urlToSave;
      }
      const dataToSave = { ...formData, url: urlToSave, amount: parseFloat(formData.amount) };
      
      if (editingPayment) {
        await api.put(`/payment-links/${editingPayment.id}/edit`, dataToSave, token);
        toast.success(lang === "de" ? "Zahlung aktualisiert!" : "Payment updated!");
      } else {
        await api.post("/payment-links", dataToSave, token);
        toast.success(lang === "de" ? "Zahlungslink erstellt!" : "Payment link created!");
      }
      setShowForm(false);
      setEditingPayment(null);
      setFormData({...defaultPayForm});
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler" : "Error"); }
  };

  const handleEditPayment = (p) => {
    setEditingPayment(p);
    setFormData({ project_id: p.project_id || "", title: p.title || "", amount: p.amount || "", currency: p.currency || "EUR", url: p.url || "", due_date: p.due_date || "", send_email: false, payment_method: p.payment_method || "link" });
    setShowForm(true);
  };

  const handleNewPayment = () => { setEditingPayment(null); setFormData({...defaultPayForm}); setShowForm(true); };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/payment-links/${id}?status=${status}`, {}, token);
      toast.success(lang === "de" ? "Status aktualisiert!" : "Status updated!");
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler beim Aktualisieren" : "Error updating"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm(lang === "de" ? "Zahlungslink wirklich löschen?" : "Delete payment link?")) {
      try {
        await api.delete(`/payment-links/${id}`, token);
        toast.success(lang === "de" ? "Zahlungslink gelöscht!" : "Payment link deleted!");
        fetchData();
      } catch (error) { toast.error(lang === "de" ? "Fehler beim Löschen" : "Error deleting"); }
    }
  };

  const handleStripeCheckout = async (paymentId) => {
    try {
      const originUrl = window.location.origin;
      const res = await api.post("/checkout/create-session", { payment_id: paymentId, origin_url: originUrl }, token);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "de" ? "Stripe-Checkout fehlgeschlagen" : "Stripe checkout failed"));
    }
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.payments}</h1>
        {isAdmin && (
          <button onClick={handleNewPayment} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Plus size={16} /> {t.newPaymentLink}
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingPayment ? (lang === "de" ? "Zahlung bearbeiten" : "Edit Payment") : t.newPaymentLink}</h3>
            <button onClick={() => { setShowForm(false); setEditingPayment(null); }} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          
          <select value={formData.project_id} onChange={(e) => setFormData({...formData, project_id: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body">
            <option value="">{t.selectProject}</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder={t.paymentTitle} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} placeholder={t.amount} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
            <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body">
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CHF">CHF</option>
            </select>
            <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          
          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-2">{lang === "de" ? "Zahlungsmethode" : "Payment Method"}</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              {Object.entries(paymentMethods).map(([key, method]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFormData({...formData, payment_method: key, url: key === "bank" ? "" : formData.url})}
                  className={`p-3 border text-sm font-heading uppercase transition-all ${formData.payment_method === key ? "border-white bg-white text-black" : "border-mmg-mine-shaft text-mmg-silver-chalice hover:border-white hover:text-white"}`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* URL or Bank Details */}
          {formData.payment_method === "bank" ? (
            <div className="space-y-3 p-4 border border-mmg-mine-shaft bg-black">
              <p className="text-sm text-mmg-silver-chalice">{lang === "de" ? "Bankverbindung wird dem Kunden per E-Mail mitgeteilt" : "Bank details will be sent to client via email"}</p>
              <textarea 
                value={formData.url} 
                onChange={(e) => setFormData({...formData, url: e.target.value})} 
                placeholder={lang === "de" ? "Bankverbindung (IBAN, BIC, Kontoinhaber...)" : "Bank details (IBAN, BIC, Account holder...)"}
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body h-24"
              />
            </div>
          ) : (
            <input 
              type="url" 
              value={formData.url} 
              onChange={(e) => setFormData({...formData, url: e.target.value})} 
              placeholder={paymentMethods[formData.payment_method]?.placeholder || t.paymentUrl} 
              className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" 
            />
          )}
          
          {/* Send email option - only for new payments */}
          {!editingPayment && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.send_email} 
                onChange={(e) => setFormData({...formData, send_email: e.target.checked})}
                className="w-5 h-5 bg-black border border-mmg-mine-shaft accent-white"
                data-testid="send-email-checkbox"
              />
              <span className="text-mmg-silver-chalice font-body">
                {lang === "de" ? "Kunde per E-Mail benachrichtigen" : "Notify client via email"}
              </span>
            </label>
          )}
          
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300" data-testid="save-payment-btn">
            <Save size={18} /> {editingPayment ? (lang === "de" ? "Aktualisieren" : "Update") : t.save}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {payments.map((payment) => (
          <div key={payment.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-lg text-white">{payment.title}</h3>
                  <StatusBadge status={payment.status} />
                  {payment.payment_method && payment.payment_method !== "link" && (
                    <span className="px-2 py-1 text-xs uppercase bg-mmg-mine-shaft text-mmg-silver-chalice">
                      {paymentMethods[payment.payment_method]?.label || payment.payment_method}
                    </span>
                  )}
                </div>
                <p className="font-body text-2xl text-white mb-2">
                  {payment.currency === "EUR" ? "€" : payment.currency === "GBP" ? "£" : payment.currency === "CHF" ? "CHF " : "$"}{payment.amount.toLocaleString()}
                </p>
                {payment.due_date && <p className="text-xs text-mmg-boulder">{t.dueDate}: {formatPortalDateTime(payment.due_date, user?.timezone, true)}</p>}
                {/* Verwendungszweck for bank transfers */}
                {payment.payment_method === "bank" && payment.payment_reference && (
                  <div className="mt-2 p-2 bg-black border border-mmg-mine-shaft inline-block">
                    <p className="text-xs text-mmg-silver-chalice">{lang === "de" ? "Verwendungszweck:" : "Reference:"}</p>
                    <p className="font-mono text-white font-bold">{payment.payment_reference}</p>
                  </div>
                )}
                {/* Payment completed info */}
                {payment.status === "paid" && payment.paid_via && (
                  <p className="text-xs text-green-500 mt-1">{lang === "de" ? `Bezahlt via ${payment.paid_via}` : `Paid via ${payment.paid_via}`}</p>
                )}
                {payment.services && payment.services.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {payment.services.map((svc) => (
                      <span key={svc} className="text-xs bg-mmg-mine-shaft px-2 py-0.5 text-mmg-silver-chalice">{svc}</span>
                    ))}
                  </div>
                )}
                {payment.payment_type === "subscription" && (
                  <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-mmg-mine-shaft text-mmg-silver-chalice uppercase">{lang === "de" ? "Abo" : "Subscription"}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* PDF Download Button */}
                <button 
                  onClick={() => window.open(`${API}/payment-links/${payment.id}/pdf`, '_blank')}
                  className="p-2 text-mmg-silver-chalice hover:text-white" 
                  title={lang === "de" ? "PDF herunterladen" : "Download PDF"}
                  data-testid={`pdf-payment-${payment.id}`}>
                  <FileDown size={18} />
                </button>
                {payment.status === "pending" && (
                  <button onClick={() => handleStripeCheckout(payment.id)}
                    className="px-3 py-1.5 bg-white text-black text-xs font-heading font-bold uppercase hover:bg-black hover:text-white border border-white transition-colors flex items-center gap-1"
                    data-testid={`stripe-pay-${payment.id}`}>
                    <CreditCard size={14} />{lang === "de" ? "Bezahlen" : "Pay"}
                  </button>
                )}
                {payment.status === "pending" && payment.url && payment.payment_method !== "bank" && (
                  <a href={payment.url} target="_blank" rel="noopener noreferrer" className="p-2 text-mmg-silver-chalice hover:text-white" title={lang === "de" ? "Externer Link" : "External link"}>
                    <ExternalLink size={18} />
                  </a>
                )}
                {isAdmin && (
                  <>
                    <button onClick={() => handleEditPayment(payment)} className="p-2 text-mmg-silver-chalice hover:text-blue-400" title={lang === "de" ? "Bearbeiten" : "Edit"} data-testid={`edit-payment-${payment.id}`}>
                      <Edit2 size={18} />
                    </button>
                    {payment.status === "pending" && (
                      <button onClick={() => handleStatusUpdate(payment.id, "paid")} className="p-2 text-mmg-silver-chalice hover:text-green-500" title={t.markAsPaid}>
                        <CheckCircle size={18} />
                      </button>
                    )}
                    <button onClick={() => handleDelete(payment.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-payment-${payment.id}`}>
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {payments.length === 0 && <p className="text-mmg-silver-chalice text-center py-12">{t.noPayments}</p>}
      </div>
    </div>
  );
};

// Subscriptions Management
const SubscriptionsManagement = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const defaultSubForm = { client_id: "", name: "", description: "", amount: "", currency: "EUR", interval: "monthly", services: [] };
  const [formData, setFormData] = useState({...defaultSubForm});

  const isAdmin = user?.role === "admin" || user?.role === "employee";
  const serviceOptions = ["Domain", "Webhosting", "E-Mail", "Branding", lang === "de" ? "Wartung" : "Maintenance", "Support"];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const subsRes = await api.get("/subscriptions", token);
      setSubscriptions(subsRes.data);
      if (isAdmin) {
        const usersRes = await api.get("/users", token);
        setUsers(usersRes.data.filter(u => u.role === "client"));
      }
    } catch (error) { }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      if (editingSub) {
        await api.put(`/subscriptions/${editingSub.id}/edit`, { ...formData, amount: parseFloat(formData.amount) }, token);
        toast.success(lang === "de" ? "Abonnement aktualisiert!" : "Subscription updated!");
      } else {
        await api.post("/subscriptions", { ...formData, amount: parseFloat(formData.amount) }, token);
        toast.success(lang === "de" ? "Abonnement erstellt!" : "Subscription created!");
      }
      setShowForm(false);
      setEditingSub(null);
      setFormData({...defaultSubForm});
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler" : "Error"); }
  };

  const handleEditSub = (sub) => {
    setEditingSub(sub);
    setFormData({ client_id: sub.client_id || "", name: sub.name || "", description: sub.description || "", amount: sub.amount || "", currency: sub.currency || "EUR", interval: sub.interval || "monthly", services: sub.services || [] });
    setShowForm(true);
  };

  const handleNewSub = () => { setEditingSub(null); setFormData({...defaultSubForm}); setShowForm(true); };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/subscriptions/${id}?status=${status}`, {}, token);
      toast.success(lang === "de" ? "Status aktualisiert!" : "Status updated!");
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler beim Aktualisieren" : "Error updating"); }
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm(t.deleteSubscription)) return;
    try {
      await api.delete(`/subscriptions/${id}`, token);
      toast.success(lang === "de" ? "Abonnement gelöscht!" : "Subscription deleted!");
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler beim Löschen" : "Error deleting"); }
  };

  const toggleService = (service) => {
    const newServices = formData.services.includes(service) ? formData.services.filter(s => s !== service) : [...formData.services, service];
    setFormData({...formData, services: newServices});
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.subscriptions}</h1>
        {isAdmin && (
          <button onClick={handleNewSub} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Plus size={16} /> {t.newSubscription}
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingSub ? (lang === "de" ? "Abonnement bearbeiten" : "Edit Subscription") : t.newSubscription}</h3>
            <button onClick={() => { setShowForm(false); setEditingSub(null); }} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          
          <select value={formData.client_id} onChange={(e) => setFormData({...formData, client_id: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body">
            <option value="">{t.selectClient}</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
          </select>
          <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder={t.subscriptionName} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder={t.description} rows={2} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} placeholder={t.amount} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
            <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body">
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
            <select value={formData.interval} onChange={(e) => setFormData({...formData, interval: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body">
              <option value="monthly">{t.monthly}</option>
              <option value="yearly">{t.yearly}</option>
            </select>
          </div>
          <div>
            <p className="font-heading text-sm text-mmg-silver-chalice mb-2">{t.services}</p>
            <div className="flex flex-wrap gap-2">
              {serviceOptions.map((service) => (
                <button key={service} type="button" onClick={() => toggleService(service)} className={`px-3 py-1 text-sm font-heading uppercase transition-colors ${formData.services.includes(service) ? "bg-white text-black" : "bg-mmg-mine-shaft text-mmg-silver-chalice hover:text-white"}`}>
                  {service}
                </button>
              ))}
            </div>
          </div>
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300" data-testid="save-sub-btn">
            <Save size={18} /> {editingSub ? (lang === "de" ? "Aktualisieren" : "Update") : t.save}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {subscriptions.map((sub) => (
          <div key={sub.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-lg text-white">{sub.name}</h3>
                  <StatusBadge status={sub.status} />
                  {sub.paused_reason && <span className="text-xs text-yellow-500">({sub.paused_reason})</span>}
                </div>
                <p className="font-body text-mmg-silver-chalice mb-2">{sub.description}</p>
                <p className="font-body text-2xl text-white mb-2">{sub.currency === "EUR" ? "€" : "$"}{sub.amount.toLocaleString()}/{sub.interval === "monthly" ? t.monthly : t.yearly}</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {sub.services.map((s) => (
                    <span key={s} className="px-2 py-0.5 bg-mmg-mine-shaft text-xs font-heading uppercase text-mmg-silver-chalice">{s}</span>
                  ))}
                </div>
                <div className="flex items-center gap-4 text-xs text-mmg-boulder">
                  {isAdmin && <span>{sub.client_name}</span>}
                  <span>{t.nextBilling}: {formatPortalDateTime(sub.next_billing, user?.timezone, true)}</span>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  {/* PDF Download Button */}
                  <button 
                    onClick={() => window.open(`${API}/subscriptions/${sub.id}/pdf`, '_blank')}
                    className="p-2 text-mmg-silver-chalice hover:text-white" 
                    title={lang === "de" ? "PDF herunterladen" : "Download PDF"}
                    data-testid={`pdf-sub-${sub.id}`}>
                    <FileDown size={18} />
                  </button>
                  <button onClick={() => handleEditSub(sub)} className="p-2 text-mmg-silver-chalice hover:text-blue-400" title={lang === "de" ? "Bearbeiten" : "Edit"} data-testid={`edit-sub-${sub.id}`}>
                    <Edit2 size={18} />
                  </button>
                  {sub.status === "active" ? (
                    <button onClick={() => handleStatusUpdate(sub.id, "inactive")} className="p-2 text-mmg-silver-chalice hover:text-yellow-500" title={t.pause}>
                      <PauseCircle size={18} />
                    </button>
                  ) : (
                    <button onClick={() => handleStatusUpdate(sub.id, "active")} className="p-2 text-mmg-silver-chalice hover:text-green-500" title={t.activate}>
                      <CheckCircle size={18} />
                    </button>
                  )}
                  <button onClick={() => handleDeleteSub(sub.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500" title={t.delete} data-testid={`delete-sub-${sub.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {subscriptions.length === 0 && <p className="text-mmg-silver-chalice text-center py-12">{t.noSubscriptions}</p>}
      </div>
    </div>
  );
};

// Affiliate Overview
const AffiliateOverview = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin" || user?.role === "employee";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStats(); }, []);
  const fetchStats = async () => {
    try { const res = await api.get("/affiliate/stats", token); setStats(res.data); } catch (e) { }
    setLoading(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;
  if (!stats) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Keine Daten" : "No data"}</div>;

  return (
    <div className="space-y-6" data-testid="affiliate-overview">
      <h1 className="font-heading font-bold text-3xl text-white">{lang === "de" ? "Affiliate-Programm" : "Affiliate Program"}</h1>

      {isAdmin && stats.is_admin ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <p className="font-body text-mmg-silver-chalice text-sm mb-2">{lang === "de" ? "Aktive Affiliates" : "Active Affiliates"}</p>
              <p className="font-heading text-3xl text-white">{stats.total_affiliates}</p>
            </div>
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <p className="font-body text-mmg-silver-chalice text-sm mb-2">{lang === "de" ? "Gesamte Empfehlungen" : "Total Referrals"}</p>
              <p className="font-heading text-3xl text-white">{stats.total_referrals}</p>
            </div>
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <p className="font-body text-mmg-silver-chalice text-sm mb-2">{lang === "de" ? "Gesamte Klicks" : "Total Clicks"}</p>
              <p className="font-heading text-3xl text-white">{stats.total_clicks}</p>
            </div>
          </div>
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <h2 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Affiliates" : "Affiliates"}</h2>
            {stats.affiliates.length === 0 ? (
              <p className="text-mmg-silver-chalice">{lang === "de" ? "Noch keine Affiliates" : "No affiliates yet"}</p>
            ) : (
              <div className="space-y-3">
                {stats.affiliates.map((aff) => (
                  <div key={aff.user_id} className="flex items-center justify-between py-3 border-b border-mmg-mine-shaft last:border-b-0">
                    <div>
                      <p className="font-heading text-white">{aff.username}</p>
                      <p className="text-sm text-mmg-silver-chalice">{aff.email}</p>
                      <p className="text-xs text-mmg-boulder mt-1">Code: <span className="text-white font-mono">{aff.affiliate_code}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-xl text-white">{aff.referral_count}</p>
                      <p className="text-xs text-mmg-silver-chalice">{lang === "de" ? "Empfehlungen" : "Referrals"}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <p className="font-body text-mmg-silver-chalice text-sm mb-2">{lang === "de" ? "Dein Affiliate-Code" : "Your Affiliate Code"}</p>
              <div className="flex items-center gap-2">
                <p className="font-heading text-xl text-white font-mono">{stats.affiliate_code || "-"}</p>
                {stats.affiliate_code && (
                  <button onClick={() => { navigator.clipboard.writeText(stats.affiliate_code); toast.success(t.linkCopied); }} className="p-1 text-mmg-silver-chalice hover:text-white"><Copy size={14} /></button>
                )}
              </div>
            </div>
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <p className="font-body text-mmg-silver-chalice text-sm mb-2">{lang === "de" ? "Empfehlungen" : "Referrals"}</p>
              <p className="font-heading text-3xl text-white">{stats.total_referrals}</p>
            </div>
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <p className="font-body text-mmg-silver-chalice text-sm mb-2">{lang === "de" ? "Klicks" : "Clicks"}</p>
              <p className="font-heading text-3xl text-white">{stats.total_clicks}</p>
            </div>
          </div>
          {stats.referrals && stats.referrals.length > 0 && (
            <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
              <h2 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Deine Empfehlungen" : "Your Referrals"}</h2>
              <div className="space-y-2">
                {stats.referrals.map((ref) => (
                  <div key={ref.username || ref.created_at} className="flex justify-between py-2 border-b border-mmg-mine-shaft last:border-b-0">
                    <span className="text-white font-body">{ref.username}</span>
                    <span className="text-mmg-silver-chalice text-sm">{ref.created_at ? formatPortalDateShort(ref.created_at, user?.timezone) : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Combined Finances Page (Payments + Subscriptions)
const FinancesPage = () => {
  const { t, lang } = useLanguage();
  const [activeTab, setActiveTab] = useState("payments");
  const tabLabel = (key) => key === "payments" ? t.payments : t.subscriptions;
  return (
    <div className="space-y-6" data-testid="finances-page">
      <h1 className="font-heading font-bold text-3xl text-white">{t.finances}</h1>
      <div className="flex gap-1 border-b border-mmg-mine-shaft">
        {["payments", "subscriptions"].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-heading text-sm uppercase tracking-widest transition-colors ${activeTab === tab ? "text-white border-b-2 border-white" : "text-mmg-silver-chalice hover:text-white"}`}
            data-testid={`tab-${tab}`}>
            {tabLabel(tab)}
          </button>
        ))}
      </div>
      {activeTab === "payments" ? <PaymentsManagement /> : <SubscriptionsManagement />}
    </div>
  );
};

// Proposals Management
const ProposalsManagement = () => {
  const { user, token } = useAuth();
  const { t, lang } = useLanguage();
  const [proposals, setProposals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ project_id: "", title: "", content: "" });
  const [viewArchived, setViewArchived] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "employee";
  const searchParams = new URLSearchParams(window.location.search);
  const preselectedProject = searchParams.get("project");

  useEffect(() => {
    fetchData();
    if (preselectedProject) {
      setFormData(prev => ({...prev, project_id: preselectedProject}));
      setShowForm(true);
    }
  }, []);

  useEffect(() => { fetchData(); }, [viewArchived]);

  const fetchData = async () => {
    try {
      const archiveParam = viewArchived ? "?archived=true" : "";
      const [proposalsRes, projectsRes] = await Promise.all([
        api.get(`/proposals${archiveParam}`, token),
        api.get("/projects", token)
      ]);
      setProposals(proposalsRes.data);
      setProjects(projectsRes.data);
    } catch (error) { }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      await api.post("/proposals", formData, token);
      toast.success(lang === "de" ? "Vorschlag eingereicht!" : "Proposal submitted!");
      setShowForm(false);
      setFormData({ project_id: "", title: "", content: "" });
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler beim Einreichen" : "Error submitting"); }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/proposals/${id}/status?status=${status}`, {}, token);
      toast.success(lang === "de" ? "Status aktualisiert!" : "Status updated!");
      fetchData();
    } catch (error) { toast.error(lang === "de" ? "Fehler beim Aktualisieren" : "Error updating"); }
  };

  const handleDeleteProposal = async (id) => {
    if (window.confirm(lang === "de" ? "Vorschlag löschen?" : "Delete proposal?")) {
      try { await api.delete(`/proposals/${id}`, token); toast.success(lang === "de" ? "Gelöscht!" : "Deleted!"); fetchData(); } catch (e) { toast.error("Error"); }
    }
  };
  const handleArchiveProposal = async (id) => {
    try { await api.put(`/proposals/${id}/archive`, {}, token); toast.success(lang === "de" ? "Archiviert!" : "Archived!"); fetchData(); } catch (e) { toast.error("Error"); }
  };
  const handleRestoreProposal = async (id) => {
    try { await api.put(`/proposals/${id}/restore`, {}, token); toast.success(lang === "de" ? "Wiederhergestellt!" : "Restored!"); fetchData(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.proposals}</h1>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex border border-mmg-mine-shaft" data-testid="proposals-archive-toggle">
              <button onClick={() => setViewArchived(false)} className={`px-4 py-2 text-sm font-heading font-bold uppercase tracking-wider transition-colors ${!viewArchived ? "bg-white text-black" : "bg-transparent text-mmg-silver-chalice hover:text-white"}`} data-testid="proposals-active-tab">{t.activeItems}</button>
              <button onClick={() => setViewArchived(true)} className={`px-4 py-2 text-sm font-heading font-bold uppercase tracking-wider transition-colors ${viewArchived ? "bg-white text-black" : "bg-transparent text-mmg-silver-chalice hover:text-white"}`} data-testid="proposals-archived-tab">{t.archivedItems}</button>
            </div>
          )}
          {!isAdmin && !viewArchived && (
            <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
              <Plus size={16} /> {t.newProposal}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{t.newProposal}</h3>
            <button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          
          <select value={formData.project_id} onChange={(e) => setFormData({...formData, project_id: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body">
            <option value="">{t.selectProject}</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder={t.proposalTitle} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} placeholder={t.proposalContent} rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Send size={18} /> {t.submit}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {proposals.map((proposal) => (
          <div key={proposal.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-lg text-white">{proposal.title}</h3>
                  <StatusBadge status={proposal.status} />
                </div>
                <p className="font-body text-mmg-silver-chalice mb-3 whitespace-pre-wrap">{proposal.content}</p>
                <div className="flex items-center gap-4 text-xs text-mmg-boulder">
                  {isAdmin && <span>{proposal.client_name}</span>}
                  <span>{formatPortalDateShort(proposal.created_at, user?.timezone)}</span>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 ml-4">
                  {!viewArchived && proposal.status === "pending" && (
                    <>
                      <button onClick={() => handleStatusUpdate(proposal.id, "approved")} className="p-2 text-mmg-silver-chalice hover:text-green-500" title={t.approve}>
                        <CheckCircle size={18} />
                      </button>
                      <button onClick={() => handleStatusUpdate(proposal.id, "rejected")} className="p-2 text-mmg-silver-chalice hover:text-red-500" title={t.reject}>
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {viewArchived ? (
                    <button onClick={() => handleRestoreProposal(proposal.id)} className="p-2 text-mmg-silver-chalice hover:text-green-400" title={t.restore} data-testid={`restore-proposal-${proposal.id}`}>
                      <RotateCcw size={18} />
                    </button>
                  ) : (
                    <button onClick={() => handleArchiveProposal(proposal.id)} className="p-2 text-mmg-silver-chalice hover:text-yellow-400" title={lang === "de" ? "Archivieren" : "Archive"} data-testid={`archive-proposal-${proposal.id}`}>
                      <Archive size={18} />
                    </button>
                  )}
                  <button onClick={() => handleDeleteProposal(proposal.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500" title={lang === "de" ? "Löschen" : "Delete"} data-testid={`delete-proposal-${proposal.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {proposals.length === 0 && <p className="text-mmg-silver-chalice text-center py-12">{viewArchived ? t.noArchivedItems : t.noProposals}</p>}
      </div>
    </div>
  );
};

// Messages Overview (Admin)
const MessagesOverview = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get("/projects", token);
      setProjects(res.data);
    } catch (error) { }
    setLoading(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <h1 className="font-heading font-bold text-3xl text-white">{t.messages}</h1>
      <p className="font-body text-mmg-silver-chalice">{t.chooseProject}</p>
      
      <div className="space-y-3">
        {projects.map((project) => (
          <a key={project.id} href={`/portal/projects/${project.id}`} className="flex items-center justify-between bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-mmg-mine-shaft">
                <MessageSquare size={24} className="text-mmg-silver-chalice" />
              </div>
              <div>
                <h3 className="font-heading text-white">{project.title}</h3>
                <p className="font-body text-sm text-mmg-silver-chalice">{project.client_name}</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-mmg-silver-chalice" />
          </a>
        ))}
        {projects.length === 0 && <p className="text-mmg-silver-chalice text-center py-12">{t.noProjects}</p>}
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user, token, updateUser } = useAuth();
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    username: user?.username || "",
    company: user?.company || "",
    phone: user?.phone || "",
    tax_number: user?.tax_number || "",
    vat_id: user?.vat_id || "",
    address: user?.address || "",
    city: user?.city || "",
    country: user?.country || "",
    timezone: user?.timezone || "Europe/Berlin",
    chat_sound_enabled: user?.chat_sound_enabled !== false
  });
  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const timezoneOptions = [
    "Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/London", "Europe/Paris",
    "Europe/Amsterdam", "Europe/Brussels", "Europe/Rome", "Europe/Madrid", "Europe/Istanbul",
    "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Sao_Paulo",
    "Asia/Dubai", "Asia/Riyadh", "Asia/Tokyo", "Asia/Shanghai", "Asia/Kolkata",
    "Australia/Sydney", "Africa/Cairo", "Pacific/Auckland", "UTC"
  ];

  const handleProfileSave = async () => {
    setLoading(true);
    try {
      const res = await api.put("/auth/me", profileData, token);
      updateUser(res.data);
      toast.success(t.profileUpdated);
    } catch (error) { toast.error(lang === "de" ? "Fehler beim Speichern" : "Error saving"); }
    setLoading(false);
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(lang === "de" ? "Passwörter stimmen nicht überein" : "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, token);
      toast.success(t.passwordChanged);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail === "Current password is incorrect" ? t.incorrectPassword : (lang === "de" ? "Fehler beim Ändern" : "Error changing password"));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-8">
      <h1 className="font-heading font-bold text-3xl text-white">{t.settings}</h1>
      
      {/* Profile Section */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2"><User size={20} /> {t.profile}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" value={profileData.username} onChange={(e) => setProfileData({...profileData, username: e.target.value})} placeholder={t.username} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <input type="text" value={profileData.company} onChange={(e) => setProfileData({...profileData, company: e.target.value})} placeholder={`${t.company} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="tel" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} placeholder={`${t.phone} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <input type="text" value={profileData.tax_number} onChange={(e) => setProfileData({...profileData, tax_number: e.target.value})} placeholder={`${t.taxNumber} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" value={profileData.vat_id} onChange={(e) => setProfileData({...profileData, vat_id: e.target.value})} placeholder={`${t.vatId} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <input type="text" value={profileData.address} onChange={(e) => setProfileData({...profileData, address: e.target.value})} placeholder={`${t.address} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" value={profileData.city} onChange={(e) => setProfileData({...profileData, city: e.target.value})} placeholder={`${t.city} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <input type="text" value={profileData.country} onChange={(e) => setProfileData({...profileData, country: e.target.value})} placeholder={`${t.country} (${t.optional})`} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-mmg-silver-chalice text-sm mb-1 font-heading">{lang === "de" ? "Zeitzone" : "Timezone"}</label>
            <select value={profileData.timezone} onChange={(e) => setProfileData({...profileData, timezone: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="timezone-select">
              {timezoneOptions.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleProfileSave} disabled={loading} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50">
          <Save size={18} /> {t.saveChanges}
        </button>
      </div>

      {/* Notification Settings Section */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2"><Bell size={20} /> {lang === "de" ? "Benachrichtigungen" : "Notifications"}</h2>
        
        <div className="flex items-center justify-between py-3 border-b border-mmg-mine-shaft">
          <div>
            <p className="font-heading text-white">{t.chatSoundEnabled}</p>
            <p className="font-body text-sm text-mmg-silver-chalice">{t.chatSoundDescription}</p>
          </div>
          <button 
            onClick={() => setProfileData({...profileData, chat_sound_enabled: !profileData.chat_sound_enabled})}
            className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${profileData.chat_sound_enabled ? "bg-green-500" : "bg-mmg-mine-shaft"}`}
            data-testid="chat-sound-toggle"
          >
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform duration-200 ${profileData.chat_sound_enabled ? "translate-x-7" : ""}`} />
          </button>
        </div>
        <p className="text-mmg-silver-chalice text-sm">{lang === "de" ? "Änderungen werden beim Speichern des Profils übernommen." : "Changes will be applied when you save your profile."}</p>
      </div>

      {/* Password Section */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2"><Lock size={20} /> {t.changePassword}</h2>
        
        <input type="password" value={passwordData.current_password} onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})} placeholder={t.currentPassword} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="password" value={passwordData.new_password} onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})} placeholder={t.newPassword} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
          <input type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})} placeholder={t.confirmPassword} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" />
        </div>
        <button onClick={handlePasswordChange} disabled={loading || !passwordData.current_password || !passwordData.new_password} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50">
          <Lock size={18} /> {t.changePassword}
        </button>
      </div>
    </div>
  );
};

// Client Summary Page
const ClientSummary = () => {
  const { token } = useAuth();
  const { t, lang } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get("/client-summary", token);
      setSummary(res.data);
    } catch (error) { }
    setLoading(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;
  if (!summary) return <div className="text-mmg-silver-chalice">Error loading summary</div>;

  return (
    <div className="space-y-8">
      <h1 className="font-heading font-bold text-3xl text-white">{t.clientSummary}</h1>
      
      {/* User Info */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-4">{t.yourDetails}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.username}</p><p className="font-body text-white">{summary.user.username}</p></div>
          <div><p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.email}</p><p className="font-body text-white">{summary.user.email}</p></div>
          {summary.user.company && <div><p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.company}</p><p className="font-body text-white">{summary.user.company}</p></div>}
          {summary.user.tax_number && <div><p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.taxNumber}</p><p className="font-body text-white">{summary.user.tax_number}</p></div>}
          {summary.user.vat_id && <div><p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.vatId}</p><p className="font-body text-white">{summary.user.vat_id}</p></div>}
          {summary.user.city && <div><p className="font-heading text-xs text-mmg-silver-chalice uppercase">{t.city}</p><p className="font-body text-white">{summary.user.city}, {summary.user.country}</p></div>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title={t.totalPaid} value={`€${summary.payments.total_paid.toLocaleString()}`} icon={CheckCircle} />
        <KPICard title={t.totalPending} value={`€${summary.payments.total_pending.toLocaleString()}`} icon={AlertCircle} />
        <KPICard title={t.monthlySubscriptions} value={`€${summary.subscriptions.monthly_total.toLocaleString()}`} icon={RefreshCw} />
        <KPICard title={t.yearlySubscriptions} value={`€${summary.subscriptions.yearly_total.toLocaleString()}`} icon={Calendar} />
      </div>

      {/* Projects */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-4">{t.projectOverview}</h2>
        <div className="flex gap-6 mb-4 text-sm">
          <span className="text-mmg-silver-chalice">{lang === "de" ? "Gesamt" : "Total"}: <span className="text-white">{summary.projects.total}</span></span>
          <span className="text-mmg-silver-chalice">{t.active}: <span className="text-blue-500">{summary.projects.active}</span></span>
          <span className="text-mmg-silver-chalice">{t.completed}: <span className="text-green-500">{summary.projects.completed}</span></span>
        </div>
        <div className="space-y-2">
          {summary.projects.list.map((p) => (
            <a key={p.id} href={`/portal/projects/${p.id}`} className="flex items-center justify-between p-3 bg-black border border-mmg-mine-shaft hover:border-mmg-boulder transition-colors">
              <span className="font-heading text-white">{p.title}</span>
              <StatusBadge status={p.status} />
            </a>
          ))}
        </div>
      </div>

      {/* Payments */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-4">{t.paymentHistory}</h2>
        <div className="space-y-2">
          {summary.payments.list.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-3 bg-black border border-mmg-mine-shaft">
              <span className="font-heading text-white">{p.title}</span>
              <div className="flex items-center gap-4">
                <span className="font-body text-white">€{p.amount.toLocaleString()}</span>
                <StatusBadge status={p.status} />
              </div>
            </div>
          ))}
          {summary.payments.list.length === 0 && <p className="text-mmg-silver-chalice text-center py-4">{t.noPayments}</p>}
        </div>
      </div>

      {/* Subscriptions */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-4">{t.subscriptions}</h2>
        <div className="space-y-2">
          {summary.subscriptions.list.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-black border border-mmg-mine-shaft">
              <div>
                <span className="font-heading text-white">{s.name}</span>
                <div className="flex gap-2 mt-1">
                  {s.services.map((svc) => <span key={svc} className="text-xs bg-mmg-mine-shaft px-2 py-0.5 text-mmg-silver-chalice">{svc}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-body text-white">€{s.amount.toLocaleString()}/{s.interval === "monthly" ? t.monthly : t.yearly}</span>
                <StatusBadge status={s.status} />
              </div>
            </div>
          ))}
          {summary.subscriptions.list.length === 0 && <p className="text-mmg-silver-chalice text-center py-4">{t.noSubscriptions}</p>}
        </div>
      </div>
    </div>
  );
};

// Contact Inquiries Management
const ContactInquiries = () => {
  const { token, user } = useAuth();
  const { t, lang } = useLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [replyData, setReplyData] = useState({ subject: "", message: "", language: "de" });
  const [showReply, setShowReply] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchData(); }, [viewArchived]);

  const fetchData = async () => {
    try {
      const archiveParam = viewArchived ? "?archived=true" : "";
      const res = await api.get(`/contact-inquiries${archiveParam}`, token);
      setInquiries(res.data);
    } catch (error) { }
    setLoading(false);
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/contact-inquiries/${id}?status=${status}`, {}, token);
      toast.success(lang === "de" ? "Status aktualisiert!" : "Status updated!");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleReply = async () => {
    try {
      await api.post(`/contact-inquiries/${selectedInquiry.id}/reply`, replyData, token);
      toast.success(lang === "de" ? "Antwort gesendet!" : "Reply sent!");
      setShowReply(false);
      setSelectedInquiry(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm(lang === "de" ? "Anfrage löschen?" : "Delete inquiry?")) {
      try { await api.delete(`/admin/contact/${id}`, token); toast.success(lang === "de" ? "Gelöscht!" : "Deleted!"); fetchData(); } catch (e) { toast.error("Error"); }
    }
  };
  const handleArchive = async (id) => {
    try { await api.put(`/admin/contact/${id}/archive`, {}, token); toast.success(lang === "de" ? "Archiviert!" : "Archived!"); fetchData(); } catch (e) { toast.error("Error"); }
  };
  const handleRestore = async (id) => {
    try { await api.put(`/admin/contact/${id}/restore`, {}, token); toast.success(lang === "de" ? "Wiederhergestellt!" : "Restored!"); fetchData(); } catch (e) { toast.error("Error"); }
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6" data-testid="contact-inquiries">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.contactInquiries}</h1>
        <div className="flex border border-mmg-mine-shaft" data-testid="inquiries-archive-toggle">
          <button onClick={() => setViewArchived(false)} className={`px-4 py-2 text-sm font-heading font-bold uppercase tracking-wider transition-colors ${!viewArchived ? "bg-white text-black" : "bg-transparent text-mmg-silver-chalice hover:text-white"}`} data-testid="inquiries-active-tab">{t.activeItems}</button>
          <button onClick={() => setViewArchived(true)} className={`px-4 py-2 text-sm font-heading font-bold uppercase tracking-wider transition-colors ${viewArchived ? "bg-white text-black" : "bg-transparent text-mmg-silver-chalice hover:text-white"}`} data-testid="inquiries-archived-tab">{t.archivedItems}</button>
        </div>
      </div>
      {showReply && selectedInquiry && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg text-white">{t.replyToInquiry}: {selectedInquiry.firstName} {selectedInquiry.lastName}</h3>
            <button onClick={() => setShowReply(false)} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setReplyData({...replyData, language: "de"})} className={`px-3 py-1 text-sm ${replyData.language === "de" ? "bg-white text-black" : "bg-mmg-mine-shaft text-white"}`}>DE</button>
            <button onClick={() => setReplyData({...replyData, language: "en"})} className={`px-3 py-1 text-sm ${replyData.language === "en" ? "bg-white text-black" : "bg-mmg-mine-shaft text-white"}`}>EN</button>
          </div>
          <input type="text" value={replyData.subject} onChange={(e) => setReplyData({...replyData, subject: e.target.value})} placeholder={t.subject} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          <textarea value={replyData.message} onChange={(e) => setReplyData({...replyData, message: e.target.value})} placeholder={lang === "de" ? "Ihre Antwort..." : "Your reply..."} rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" />
          <button onClick={handleReply} className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"><Send size={18} /> {t.sendEmail}</button>
        </div>
      )}
      <div className="space-y-3">
        {inquiries.map((inq) => (
          <div key={inq.id || inq._id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-white">{inq.firstName} {inq.lastName}</h3>
                  <span className={`px-2 py-1 text-xs uppercase ${inq.status === "replied" ? "bg-green-600" : inq.status === "in_progress" ? "bg-yellow-600" : "bg-blue-600"} text-white`}>{inq.status || "new"}</span>
                </div>
                <p className="text-sm text-mmg-silver-chalice">{inq.email}</p>
                <p className="text-mmg-silver-chalice mt-2">{inq.message}</p>
                <p className="text-xs text-mmg-boulder mt-2">{formatPortalDate(inq.createdAt, user?.timezone)}</p>
              </div>
              <div className="flex flex-col gap-2">
                {!viewArchived && inq.status !== "replied" && (
                  <button onClick={() => { setSelectedInquiry(inq); setShowReply(true); }} className="p-2 text-mmg-silver-chalice hover:text-white" data-testid={`reply-inquiry-${inq.id}`}><Reply size={18} /></button>
                )}
                {viewArchived ? (
                  <button onClick={() => handleRestore(inq.id)} className="p-2 text-mmg-silver-chalice hover:text-green-400" title={t.restore} data-testid={`restore-inquiry-${inq.id}`}><RotateCcw size={18} /></button>
                ) : (
                  <button onClick={() => handleArchive(inq.id)} className="p-2 text-mmg-silver-chalice hover:text-yellow-400" title={lang === "de" ? "Archivieren" : "Archive"} data-testid={`archive-inquiry-${inq.id}`}><Archive size={18} /></button>
                )}
                <button onClick={() => handleDelete(inq.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500" title={lang === "de" ? "Löschen" : "Delete"} data-testid={`delete-inquiry-${inq.id}`}><Trash2 size={18} /></button>
              </div>
            </div>
          </div>
        ))}
        {inquiries.length === 0 && <p className="text-mmg-silver-chalice text-center py-12">{viewArchived ? t.noArchivedItems : t.noInquiries}</p>}
      </div>
    </div>
  );
};

// Documents Management
const DocumentsManagement = () => {
  const { token, user } = useAuth();
  const { t, lang } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  
  // Build default sender data from logged-in user
  const getDefaultFormData = () => ({
    type: "letter", language: "de", 
    recipient_name: "", recipient_company: "", recipient_address: "", recipient_zip: "", recipient_city: "", recipient_country: "",
    sender_company: user?.company || "Mahzod Media Group", 
    sender_address: user?.address || "", 
    sender_city: user?.city || "", 
    sender_country: user?.country || "", 
    sender_email: user?.email || "info@mahzod.com", 
    sender_web: "www.mahzod.com", 
    sender_phone: user?.phone || "", 
    sender_tax_number: user?.tax_number || "", 
    sender_vat_id: user?.vat_id || "",
    subject: "", content: "", items: [], notes: "", valid_until: "",
    invoice_number: "", delivery_date: "", tax_rate: 19, payment_terms: "", bank_details: "",
    signer_name: user?.username || "", signer_title: "", greeting: ""
  });
  
  const defaultFormData = getDefaultFormData();
  const [formData, setFormData] = useState({...defaultFormData});
  const [newItem, setNewItem] = useState({ description: "", quantity: 1, unit_price: 0 });
  const [paymentSettings, setPaymentSettings] = useState(null);

  const itemsSubtotal = useMemo(() => formData.items.reduce((s, it) => s + it.quantity * it.unit_price, 0), [formData.items]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  useEffect(() => { setFormData(getDefaultFormData()); }, [user]);
  
  const fetchData = async () => { 
    try { 
      const res = await api.get("/documents", token); 
      setDocuments(res.data); 
      // Also fetch clients for recipient selection
      const usersRes = await api.get("/users", token);
      setClients(usersRes.data.filter(u => u.role === "client"));
      // Fetch payment settings for bank details
      try {
        const payRes = await api.get("/settings/payment", token);
        setPaymentSettings(payRes.data);
      } catch (e) { }
    } catch (e) {} 
    setLoading(false); 
  };

  // Build bank details string from payment settings
  const getBankDetailsFromSettings = () => {
    if (!paymentSettings) return "";
    const parts = [];
    if (paymentSettings.bank_account_holder) parts.push(`Kontoinhaber: ${paymentSettings.bank_account_holder}`);
    if (paymentSettings.bank_iban) parts.push(`IBAN: ${paymentSettings.bank_iban}`);
    if (paymentSettings.bank_bic) parts.push(`BIC: ${paymentSettings.bank_bic}`);
    if (paymentSettings.bank_name) parts.push(`Bank: ${paymentSettings.bank_name}`);
    return parts.join(" | ");
  };

  // Handle client selection to auto-fill recipient fields
  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    if (!clientId) return;
    
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        recipient_name: client.username || "",
        recipient_company: client.company || "",
        recipient_address: client.address || "",
        recipient_city: client.city || "",
        recipient_country: client.country || ""
      });
    }
  };

  const handleGenerate = async () => {
    try {
      if (editingDoc) {
        await api.put(`/documents/${editingDoc.id}/edit`, formData, token);
        toast.success(lang === "de" ? "Dokument aktualisiert!" : "Document updated!");
      } else {
        await api.post("/documents/generate", formData, token);
        toast.success(lang === "de" ? "PDF erstellt!" : "PDF generated!");
      }
      setShowForm(false);
      setEditingDoc(null);
      setFormData(getDefaultFormData());
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  // Fetch next invoice number when switching to invoice type
  const fetchNextInvoiceNumber = async () => {
    try {
      const res = await api.get("/invoice-number/preview", token);
      return res.data.preview;
    } catch (e) {
      return "";
    }
  };

  // Fetch next offer number when switching to offer type
  const fetchNextOfferNumber = async () => {
    try {
      const res = await api.get("/offer-number/preview", token);
      return res.data.preview;
    } catch (e) {
      return "";
    }
  };

  const handleTypeChange = async (newType) => {
    const updates = { type: newType };
    
    // Auto-fill invoice number when switching to invoice and not editing
    if (newType === "invoice" && !editingDoc && !formData.invoice_number) {
      const nextNum = await fetchNextInvoiceNumber();
      if (nextNum) {
        updates.invoice_number = nextNum;
      }
    }
    
    // Auto-fill offer number when switching to offer and not editing
    if (newType === "offer" && !editingDoc && !formData.invoice_number) {
      const nextNum = await fetchNextOfferNumber();
      if (nextNum) {
        updates.invoice_number = nextNum;
      }
    }
    
    setFormData({...formData, ...updates});
  };

  const handleEdit = (doc) => {
    if (doc.form_data) {
      setFormData({ ...getDefaultFormData(), ...doc.form_data });
    } else {
      setFormData({
        ...defaultFormData,
        type: doc.type || "letter",
        language: doc.language || "de",
        recipient_name: doc.recipient_name || "",
        recipient_company: doc.recipient_company || "",
        subject: doc.subject || "",
        invoice_number: doc.invoice_number || "",
        content: doc.content || "",
        items: doc.items || [],
        notes: doc.notes || ""
      });
    }
    setEditingDoc(doc);
    setShowForm(true);
  };

  const handleNewDoc = () => {
    setEditingDoc(null);
    setSelectedClientId("");
    setFormData(getDefaultFormData());
    setShowForm(true);
  };

  const addItem = () => { if (newItem.description) { setFormData({...formData, items: [...formData.items, {...newItem}]}); setNewItem({ description: "", quantity: 1, unit_price: 0 }); } };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.documents}</h1>
        <div className="flex gap-2">
          <button onClick={async () => { try { const res = await fetch(`${BACKEND_URL}/api/export/kpis?format=csv`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'kpis.csv'; a.click(); } catch (e) { toast.error('Export failed'); } }} className="px-4 py-2 border border-mmg-mine-shaft text-mmg-silver-chalice hover:text-white" data-testid="export-kpis-btn"><FileDown size={16} className="inline mr-2" />{t.exportKpis}</button>
          <button onClick={handleNewDoc} className="px-4 py-2 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"><Plus size={16} className="inline mr-2" />{t.newDocument}</button>
        </div>
      </div>
      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex justify-between"><h3 className="font-heading text-lg text-white">{editingDoc ? (lang === "de" ? "Dokument bearbeiten" : "Edit Document") : t.newDocument}</h3><button onClick={() => { setShowForm(false); setEditingDoc(null); }} className="text-mmg-silver-chalice"><X size={20} /></button></div>
          
          {/* Doc type + Language */}
          <div className="grid grid-cols-2 gap-4">
            <select value={formData.type} onChange={(e) => handleTypeChange(e.target.value)} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" data-testid="doc-type-select">
              <option value="letter">{t.letter}</option><option value="offer">{t.offer}</option><option value="invoice">{t.invoice}</option><option value="statement">{t.statement}</option>
            </select>
            <select value={formData.language} onChange={(e) => setFormData({...formData, language: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white">
              <option value="de">Deutsch</option><option value="en">English</option>
            </select>
          </div>

          {/* Sender info (collapsible) */}
          <details className="border border-mmg-mine-shaft p-4">
            <summary className="text-sm text-mmg-silver-chalice cursor-pointer font-heading uppercase">{lang === "de" ? "Absender-Daten" : "Sender Info"}</summary>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input type="text" value={formData.sender_company} onChange={(e) => setFormData({...formData, sender_company: e.target.value})} placeholder={lang === "de" ? "Firma" : "Company"} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_address} onChange={(e) => setFormData({...formData, sender_address: e.target.value})} placeholder={lang === "de" ? "Straße + Hausnr." : "Street + No."} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_city} onChange={(e) => setFormData({...formData, sender_city: e.target.value})} placeholder={lang === "de" ? "PLZ + Stadt" : "ZIP + City"} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_country} onChange={(e) => setFormData({...formData, sender_country: e.target.value})} placeholder={lang === "de" ? "Land" : "Country"} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_email} onChange={(e) => setFormData({...formData, sender_email: e.target.value})} placeholder="E-Mail" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_phone} onChange={(e) => setFormData({...formData, sender_phone: e.target.value})} placeholder={lang === "de" ? "Telefon" : "Phone"} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_tax_number} onChange={(e) => setFormData({...formData, sender_tax_number: e.target.value})} placeholder={lang === "de" ? "Steuernummer" : "Tax Number"} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_vat_id} onChange={(e) => setFormData({...formData, sender_vat_id: e.target.value})} placeholder={lang === "de" ? "USt-IdNr." : "VAT ID"} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
              <input type="text" value={formData.sender_web} onChange={(e) => setFormData({...formData, sender_web: e.target.value})} placeholder="Website" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
          </details>

          {/* Recipient */}
          <p className="text-xs text-mmg-silver-chalice uppercase font-heading">{lang === "de" ? "Empfänger" : "Recipient"}</p>
          
          {/* Client Selection */}
          {clients.length > 0 && (
            <select 
              value={selectedClientId} 
              onChange={(e) => handleClientSelect(e.target.value)} 
              className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body mb-3"
              data-testid="doc-client-select"
            >
              <option value="">{lang === "de" ? "-- Kunde auswählen (optional) --" : "-- Select client (optional) --"}</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.username} {c.company ? `(${c.company})` : ""}</option>
              ))}
            </select>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={formData.recipient_company} onChange={(e) => setFormData({...formData, recipient_company: e.target.value})} placeholder={lang === "de" ? "Firma" : "Company"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <input type="text" value={formData.recipient_name} onChange={(e) => setFormData({...formData, recipient_name: e.target.value})} placeholder={lang === "de" ? "Name" : "Name"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <input type="text" value={formData.recipient_address} onChange={(e) => setFormData({...formData, recipient_address: e.target.value})} placeholder={lang === "de" ? "Straße + Hausnr." : "Street + No."} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <div className="flex gap-2">
              <input type="text" value={formData.recipient_zip} onChange={(e) => setFormData({...formData, recipient_zip: e.target.value})} placeholder="PLZ" className="w-24 py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
              <input type="text" value={formData.recipient_city} onChange={(e) => setFormData({...formData, recipient_city: e.target.value})} placeholder={lang === "de" ? "Stadt" : "City"} className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            </div>
            <input type="text" value={formData.recipient_country} onChange={(e) => setFormData({...formData, recipient_country: e.target.value})} placeholder={lang === "de" ? "Land" : "Country"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>

          {/* Invoice/Offer specific fields */}
          {(formData.type === "invoice" || formData.type === "offer") && (
            <div className="grid grid-cols-3 gap-3">
              <div className="flex gap-2">
                <input type="text" value={formData.invoice_number} onChange={(e) => setFormData({...formData, invoice_number: e.target.value})} placeholder={lang === "de" ? (formData.type === "offer" ? "Angebotsnr." : "Rechnungsnr.") : (formData.type === "offer" ? "Offer No." : "Invoice No.")} className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white" data-testid="invoice-number-input" />
                {!editingDoc && (
                  <button 
                    type="button"
                    onClick={async () => {
                      const nextNum = formData.type === "offer" ? await fetchNextOfferNumber() : await fetchNextInvoiceNumber();
                      if (nextNum) setFormData({...formData, invoice_number: nextNum});
                    }}
                    className="px-3 py-2 border border-mmg-mine-shaft text-mmg-silver-chalice hover:text-white hover:border-white text-xs whitespace-nowrap"
                    title={lang === "de" ? "Nächste Nummer generieren" : "Generate next number"}
                  >
                    #
                  </button>
                )}
              </div>
              <input type="text" value={formData.delivery_date} onChange={(e) => setFormData({...formData, delivery_date: e.target.value})} placeholder={lang === "de" ? "Lieferdatum / Leistungszeitraum" : "Delivery Date / Period"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-mmg-silver-chalice whitespace-nowrap">{lang === "de" ? "MwSt.%" : "VAT%"}</span>
                <input type="number" value={formData.tax_rate} onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})} className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
              </div>
            </div>
          )}

          {/* Subject (for letters/statements) */}
          {(formData.type === "letter" || formData.type === "statement") && (
            <input type="text" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} placeholder={lang === "de" ? "Betreff" : "Subject"} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          )}

          {/* Content */}
          <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} placeholder={lang === "de" ? "Inhalt / Freitext" : "Content"} rows={5} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" />

          {/* Items table for offer/invoice */}
          {(formData.type === "offer" || formData.type === "invoice") && (
            <div className="border border-mmg-mine-shaft p-4 space-y-2">
              <h4 className="text-sm text-white font-heading uppercase">{lang === "de" ? "Positionen" : "Line Items"}</h4>
              {formData.items.map((item, i) => (
                <div key={item.description + '-' + i} className="flex justify-between items-center text-sm border-b border-mmg-mine-shaft pb-1">
                  <span className="text-mmg-silver-chalice flex-1">{i+1}. {item.description}</span>
                  <span className="text-white w-32 text-right">{item.quantity} x €{Number(item.unit_price).toFixed(2)}</span>
                  <span className="text-white w-28 text-right font-bold">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                  <button onClick={() => setFormData({...formData, items: formData.items.filter((_, idx) => idx !== i)})} className="ml-2 text-mmg-silver-chalice hover:text-red-500"><X size={14} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input type="text" value={newItem.description} onChange={(e) => setNewItem({...newItem, description: e.target.value})} placeholder={lang === "de" ? "Beschreibung" : "Description"} className="flex-1 py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
                <input type="number" value={newItem.quantity} onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 1})} placeholder={lang === "de" ? "Menge" : "Qty"} className="w-20 py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
                <input type="number" step="0.01" value={newItem.unit_price} onChange={(e) => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})} placeholder="€ Preis" className="w-28 py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
                <button onClick={addItem} className="px-3 bg-mmg-mine-shaft text-white hover:bg-white hover:text-black"><Plus size={16} /></button>
              </div>
              {formData.items.length > 0 && (
                <div className="text-right text-sm pt-2 border-t border-mmg-mine-shaft space-y-1">
                  <div className="text-mmg-silver-chalice">{lang === "de" ? "Netto" : "Net"}: €{itemsSubtotal.toFixed(2)}</div>
                  <div className="text-mmg-silver-chalice">{lang === "de" ? "USt." : "VAT"} {formData.tax_rate}%: €{(itemsSubtotal * formData.tax_rate / 100).toFixed(2)}</div>
                  <div className="text-white font-bold">{lang === "de" ? "Gesamt" : "Total"}: €{(itemsSubtotal * (1 + formData.tax_rate / 100)).toFixed(2)}</div>
                </div>
              )}
            </div>
          )}

          {/* Payment terms + Bank (invoice) */}
          {formData.type === "invoice" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input type="text" value={formData.payment_terms} onChange={(e) => setFormData({...formData, payment_terms: e.target.value})} placeholder={lang === "de" ? "Zahlungsbedingungen (z.B. 14 Tage netto)" : "Payment Terms"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
                <div className="flex gap-2">
                  <input type="text" value={formData.bank_details} onChange={(e) => setFormData({...formData, bank_details: e.target.value})} placeholder={lang === "de" ? "Bankverbindung (IBAN, BIC)" : "Bank Details (IBAN, BIC)"} className="flex-1 py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
                  {paymentSettings && (paymentSettings.bank_iban || paymentSettings.bank_account_holder) && (
                    <button 
                      type="button"
                      onClick={() => setFormData({...formData, bank_details: getBankDetailsFromSettings()})}
                      className="px-3 py-2 border border-mmg-mine-shaft text-mmg-silver-chalice hover:text-white hover:border-white text-xs whitespace-nowrap"
                      title={lang === "de" ? "Aus Einstellungen laden" : "Load from settings"}
                    >
                      {lang === "de" ? "Auto" : "Auto"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Valid until (offer) */}
          {formData.type === "offer" && (
            <input type="text" value={formData.valid_until} onChange={(e) => setFormData({...formData, valid_until: e.target.value})} placeholder={lang === "de" ? "Gültig bis (z.B. 31.03.2026)" : "Valid Until"} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          )}

          {/* Notes */}
          <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder={lang === "de" ? "Hinweise / Anmerkungen" : "Notes"} rows={2} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" />

          {/* Signer */}
          <div className="grid grid-cols-2 gap-3">
            <input type="text" value={formData.signer_name} onChange={(e) => setFormData({...formData, signer_name: e.target.value})} placeholder={lang === "de" ? "Unterzeichner Name" : "Signer Name"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <input type="text" value={formData.signer_title} onChange={(e) => setFormData({...formData, signer_title: e.target.value})} placeholder={lang === "de" ? "Position / Titel" : "Title / Position"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>

          <button onClick={handleGenerate} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white" data-testid="generate-pdf-btn"><FileText size={18} className="inline mr-2" />{editingDoc ? (lang === "de" ? "Aktualisieren" : "Update") : t.generatePdf}</button>
        </div>
      )}
      <div className="space-y-3">
        {documents.map((doc) => (
          <div key={doc.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4">
            <div className="flex justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-1 text-xs bg-mmg-mine-shaft text-white uppercase">{doc.type}</span>
                  <h3 className="font-heading text-white">{doc.subject}</h3>
                  <StatusBadge status={doc.status} />
                </div>
                <p className="text-sm text-mmg-silver-chalice">{doc.recipient_name}</p>
              </div>
              <div className="flex gap-2 items-center">
                {doc.status === "draft" && (
                  <button onClick={async () => {
                    try {
                      await api.put(`/documents/${doc.id}/approve`, {}, token);
                      toast.success(lang === "de" ? "Dokument genehmigt!" : "Document approved!");
                      fetchData();
                    } catch (e) { toast.error("Error"); }
                  }} className="px-3 py-1 text-xs bg-green-700 text-white hover:bg-green-600 uppercase" data-testid={`approve-doc-${doc.id}`}>{t.approveDocument}</button>
                )}
                {doc.status === "approved" && (
                  <button onClick={() => {
                    const email = window.prompt(lang === "de" ? "E-Mail-Adresse des Empfängers:" : "Recipient email address:");
                    if (!email) return;
                    api.post(`/documents/${doc.id}/send?recipient_email=${encodeURIComponent(email)}`, {}, token)
                      .then(() => { toast.success(lang === "de" ? "Dokument gesendet!" : "Document sent!"); fetchData(); })
                      .catch((e) => toast.error(e.response?.data?.detail || "Error"));
                  }} className="px-3 py-1 text-xs bg-blue-700 text-white hover:bg-blue-600 uppercase" data-testid={`send-doc-${doc.id}`}>{t.sendDocument}</button>
                )}
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${BACKEND_URL}${doc.file_url}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) throw new Error("Download failed");
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = `${doc.type}_${doc.recipient_name || "document"}.pdf`; a.click();
                    window.URL.revokeObjectURL(url);
                  } catch (e) { toast.error(lang === "de" ? "Download fehlgeschlagen" : "Download failed"); }
                }} className="p-2 text-mmg-silver-chalice hover:text-white" data-testid={`download-doc-${doc.id}`}><Download size={18} /></button>
                <button onClick={() => handleEdit(doc)} className="p-2 text-mmg-silver-chalice hover:text-blue-400" title={lang === "de" ? "Bearbeiten" : "Edit"} data-testid={`edit-doc-${doc.id}`}><Edit2 size={18} /></button>
                <button onClick={async () => {
                  if (!window.confirm(lang === "de" ? "Dokument wirklich löschen?" : "Delete this document?")) return;
                  try {
                    await api.delete(`/documents/${doc.id}`, token);
                    toast.success(lang === "de" ? "Dokument gelöscht!" : "Document deleted!");
                    fetchData();
                  } catch (e) { toast.error("Error"); }
                }} className="p-2 text-mmg-silver-chalice hover:text-red-500" data-testid={`delete-doc-${doc.id}`}><Trash2 size={18} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Jobs Management
const JobsManagement = () => {
  const { token } = useAuth();
  const { t, lang } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({ title: "", office: "", function: "", type: "Full-time", description: "", responsibilities: "", requirements: "", featured: false });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => { try { const res = await api.get("/public/jobs", token); setJobs(res.data); } catch (e) {} setLoading(false); };

  const handleSave = async () => {
    try {
      if (editingJob) {
        await api.put(`/jobs/${editingJob.id}`, formData, token);
      } else {
        await api.post("/jobs", formData, token);
      }
      toast.success(lang === "de" ? "Gespeichert!" : "Saved!");
      setShowForm(false);
      setEditingJob(null);
      fetchData();
    } catch (e) { toast.error("Error"); }
  };

  const handleDelete = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/jobs/${id}`, token); fetchData(); } };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.jobPortal}</h1>
        <button onClick={() => { setShowForm(true); setEditingJob(null); setFormData({ title: "", office: "", function: "", type: "Full-time", description: "", responsibilities: "", requirements: "", featured: false }); }} className="px-4 py-2 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"><Plus size={16} className="inline mr-2" />{t.newJob}</button>
      </div>
      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex justify-between"><h3 className="font-heading text-lg text-white">{editingJob ? t.editJob : t.newJob}</h3><button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice"><X size={20} /></button></div>
          <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder={lang === "de" ? "Stellentitel" : "Job Title"} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" data-testid="job-title-input" />
          <div className="grid grid-cols-3 gap-4">
            <input type="text" value={formData.office} onChange={(e) => setFormData({...formData, office: e.target.value})} placeholder={lang === "de" ? "Standort" : "Location"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <input type="text" value={formData.function} onChange={(e) => setFormData({...formData, function: e.target.value})} placeholder={lang === "de" ? "Bereich" : "Department"} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="py-3 px-4 bg-black border border-mmg-mine-shaft text-white">
              <option value="Full-time">Full-time</option><option value="Part-time">Part-time</option><option value="Contract">Contract</option><option value="Freelance">Freelance</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-mmg-silver-chalice text-sm cursor-pointer">
            <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({...formData, featured: e.target.checked})} className="accent-white" />
            {lang === "de" ? "Als empfohlen markieren" : "Mark as featured"}
          </label>
          <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder={lang === "de" ? "Beschreibung" : "Description"} rows={4} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" data-testid="job-description-input" />
          <textarea value={formData.responsibilities} onChange={(e) => setFormData({...formData, responsibilities: e.target.value})} placeholder={lang === "de" ? "Aufgaben (eine pro Zeile)" : "Responsibilities (one per line)"} rows={4} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" data-testid="job-responsibilities-input" />
          <textarea value={formData.requirements} onChange={(e) => setFormData({...formData, requirements: e.target.value})} placeholder={lang === "de" ? "Anforderungen (eine pro Zeile)" : "Requirements (one per line)"} rows={4} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" data-testid="job-requirements-input" />
          <button onClick={handleSave} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"><Save size={18} className="inline mr-2" />{t.save}</button>
        </div>
      )}
      <div className="space-y-3">
        {jobs.map((job) => (
          <div key={job.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex justify-between">
            <div>
              <h3 className="font-heading text-lg text-white">{job.title}</h3>
              <p className="text-sm text-mmg-silver-chalice">{job.office} • {job.function} • {job.type}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditingJob(job); setFormData(job); setShowForm(true); }} className="p-2 text-mmg-silver-chalice hover:text-white"><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(job.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Applications Management
const ApplicationsManagement = () => {
  const { token, user } = useAuth();
  const { t, lang } = useLanguage();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailApp, setDetailApp] = useState(null);
  const [emailData, setEmailData] = useState({ subject: "", message: "", language: "de" });
  const [showEmail, setShowEmail] = useState(false);
  const [notesEdit, setNotesEdit] = useState("");
  const [viewArchived, setViewArchived] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchData(); }, [viewArchived]);
  const fetchData = async () => {
    try {
      const archiveParam = viewArchived ? "?archived=true" : "";
      const res = await api.get(`/applications${archiveParam}`, token);
      setApplications(res.data);
    } catch (e) {} setLoading(false);
  };

  const handleStatusUpdate = async (id, status) => { await api.put(`/applications/${id}?status=${status}`, {}, token); fetchData(); if (detailApp?.id === id) setDetailApp(prev => ({...prev, status})); };

  const handleSaveNotes = async (id) => {
    try {
      await api.put(`/applications/${id}?notes=${encodeURIComponent(notesEdit)}`, {}, token);
      toast.success(lang === "de" ? "Notizen gespeichert!" : "Notes saved!");
      fetchData();
      if (detailApp?.id === id) setDetailApp(prev => ({...prev, notes: notesEdit}));
    } catch (e) { toast.error("Error"); }
  };

  const handleSendEmail = async () => {
    try {
      await api.post(`/applications/${selectedApp.id}/email?subject=${encodeURIComponent(emailData.subject)}&message=${encodeURIComponent(emailData.message)}&language=${emailData.language}`, {}, token);
      toast.success(lang === "de" ? "E-Mail gesendet!" : "Email sent!");
      setShowEmail(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm(lang === "de" ? "Bewerbung löschen?" : "Delete application?")) {
      try { await api.delete(`/applications/${id}`, token); toast.success(lang === "de" ? "Gelöscht!" : "Deleted!"); if (detailApp?.id === id) setDetailApp(null); fetchData(); } catch (e) { toast.error("Error"); }
    }
  };
  const handleArchive = async (id) => {
    try { await api.put(`/applications/${id}/archive`, {}, token); toast.success(lang === "de" ? "Archiviert!" : "Archived!"); if (detailApp?.id === id) setDetailApp(null); fetchData(); } catch (e) { toast.error("Error"); }
  };
  const handleRestoreApp = async (id) => {
    try { await api.put(`/applications/${id}/restore`, {}, token); toast.success(lang === "de" ? "Wiederhergestellt!" : "Restored!"); if (detailApp?.id === id) setDetailApp(null); fetchData(); } catch (e) { toast.error("Error"); }
  };

  const openDetail = (app) => { setDetailApp(app); setNotesEdit(app.notes || ""); setShowEmail(false); };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  // Detail View
  if (detailApp) {
    const statusColors = { new: "bg-blue-600", reviewing: "bg-yellow-600", interview: "bg-purple-600", hired: "bg-green-600", rejected: "bg-red-600" };
    return (
      <div className="space-y-6" data-testid="application-detail-view">
        <button onClick={() => setDetailApp(null)} className="flex items-center gap-2 text-mmg-silver-chalice hover:text-white" data-testid="back-to-applications-btn">
          <ArrowLeft size={18} /> {lang === "de" ? "Zurück zu Bewerbungen" : "Back to Applications"}
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading font-bold text-3xl text-white">{detailApp.first_name} {detailApp.last_name}</h1>
            <span className={`px-3 py-1 text-xs uppercase text-white ${statusColors[detailApp.status] || "bg-blue-600"}`}>{detailApp.status}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setSelectedApp(detailApp); setShowEmail(true); }} className="px-4 py-2 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white text-sm" data-testid="detail-send-email-btn"><Mail size={14} className="inline mr-2" />{t.sendEmail}</button>
            {viewArchived ? (
              <button onClick={() => handleRestoreApp(detailApp.id)} className="px-4 py-2 bg-mmg-mine-shaft text-white font-heading font-bold uppercase text-sm hover:bg-green-600 border border-mmg-mine-shaft" data-testid="detail-restore-btn"><RotateCcw size={14} className="inline mr-2" />{t.restore}</button>
            ) : (
              <button onClick={() => handleArchive(detailApp.id)} className="px-4 py-2 bg-mmg-mine-shaft text-white font-heading font-bold uppercase text-sm hover:bg-yellow-600 border border-mmg-mine-shaft" data-testid="detail-archive-btn"><Archive size={14} className="inline mr-2" />{lang === "de" ? "Archiv" : "Archive"}</button>
            )}
            <button onClick={() => handleDelete(detailApp.id)} className="px-4 py-2 bg-mmg-mine-shaft text-white font-heading font-bold uppercase text-sm hover:bg-red-600 border border-mmg-mine-shaft" data-testid="detail-delete-btn"><Trash2 size={14} className="inline mr-2" />{t.delete}</button>
            <select value={detailApp.status} onChange={(e) => handleStatusUpdate(detailApp.id, e.target.value)} className="bg-black border border-mmg-mine-shaft text-white text-sm py-2 px-3" data-testid="detail-status-select">
              <option value="new">{lang === "de" ? "Neu" : "New"}</option><option value="reviewing">{lang === "de" ? "In Prüfung" : "Reviewing"}</option><option value="interview">Interview</option><option value="hired">{lang === "de" ? "Eingestellt" : "Hired"}</option><option value="rejected">{lang === "de" ? "Abgelehnt" : "Rejected"}</option>
            </select>
          </div>
        </div>
        {showEmail && (
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <div className="flex justify-between"><h3 className="font-heading text-lg text-white">{t.sendEmail}: {detailApp.first_name}</h3><button onClick={() => setShowEmail(false)} className="text-mmg-silver-chalice"><X size={20} /></button></div>
            <input type="text" value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} placeholder={t.subject} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
            <textarea value={emailData.message} onChange={(e) => setEmailData({...emailData, message: e.target.value})} placeholder="Message" rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" />
            <button onClick={handleSendEmail} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"><Send size={18} className="inline mr-2" />{t.sendEmail}</button>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <h2 className="font-heading text-lg text-white border-b border-mmg-mine-shaft pb-2">{lang === "de" ? "Kontaktdaten" : "Contact Info"}</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3"><Mail size={16} className="text-mmg-silver-chalice" /><span className="text-white" data-testid="detail-email">{detailApp.email}</span></div>
              {detailApp.phone && <div className="flex items-center gap-3"><Phone size={16} className="text-mmg-silver-chalice" /><span className="text-white" data-testid="detail-phone">{detailApp.phone}</span></div>}
              {detailApp.linkedin_url && <div className="flex items-center gap-3"><LinkIcon size={16} className="text-mmg-silver-chalice" /><a href={detailApp.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline" data-testid="detail-linkedin">LinkedIn</a></div>}
              {detailApp.resume_url && <div className="flex items-center gap-3"><FileText size={16} className="text-mmg-silver-chalice" /><a href={detailApp.resume_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline" data-testid="detail-resume">{t.resume}</a></div>}
            </div>
          </div>
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <h2 className="font-heading text-lg text-white border-b border-mmg-mine-shaft pb-2">{lang === "de" ? "Stellendetails" : "Job Details"}</h2>
            <div className="space-y-3">
              <div><span className="text-mmg-silver-chalice text-sm">{lang === "de" ? "Stelle" : "Position"}</span><p className="text-white" data-testid="detail-job-title">{detailApp.job_title}</p></div>
              <div><span className="text-mmg-silver-chalice text-sm">{lang === "de" ? "Beworben am" : "Applied on"}</span><p className="text-white">{formatPortalDate(detailApp.created_at, user?.timezone, { year: 'numeric', month: 'long', day: 'numeric', hour: undefined, minute: undefined })}</p></div>
            </div>
          </div>
        </div>
        {detailApp.cover_letter && (
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-3">
            <h2 className="font-heading text-lg text-white border-b border-mmg-mine-shaft pb-2">{t.coverLetter}</h2>
            <p className="text-mmg-silver-chalice whitespace-pre-wrap" data-testid="detail-cover-letter">{detailApp.cover_letter}</p>
          </div>
        )}
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-3">
          <h2 className="font-heading text-lg text-white border-b border-mmg-mine-shaft pb-2">{lang === "de" ? "Interne Notizen" : "Internal Notes"}</h2>
          <textarea value={notesEdit} onChange={(e) => setNotesEdit(e.target.value)} placeholder={lang === "de" ? "Notizen zum Bewerber..." : "Notes about applicant..."} rows={3} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" data-testid="detail-notes-input" />
          <button onClick={() => handleSaveNotes(detailApp.id)} className="px-4 py-2 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white text-sm" data-testid="save-notes-btn"><Save size={14} className="inline mr-2" />{t.save}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="applications-list">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{t.applications}</h1>
        <div className="flex border border-mmg-mine-shaft" data-testid="applications-archive-toggle">
          <button onClick={() => setViewArchived(false)} className={`px-4 py-2 text-sm font-heading font-bold uppercase tracking-wider transition-colors ${!viewArchived ? "bg-white text-black" : "bg-transparent text-mmg-silver-chalice hover:text-white"}`} data-testid="applications-active-tab">{t.activeItems}</button>
          <button onClick={() => setViewArchived(true)} className={`px-4 py-2 text-sm font-heading font-bold uppercase tracking-wider transition-colors ${viewArchived ? "bg-white text-black" : "bg-transparent text-mmg-silver-chalice hover:text-white"}`} data-testid="applications-archived-tab">{t.archivedItems}</button>
        </div>
      </div>
      {showEmail && selectedApp && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex justify-between"><h3 className="font-heading text-lg text-white">{t.sendEmail}: {selectedApp.first_name}</h3><button onClick={() => setShowEmail(false)} className="text-mmg-silver-chalice"><X size={20} /></button></div>
          <input type="text" value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} placeholder={t.subject} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          <textarea value={emailData.message} onChange={(e) => setEmailData({...emailData, message: e.target.value})} placeholder="Message" rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white resize-none" />
          <button onClick={handleSendEmail} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white"><Send size={18} className="inline mr-2" />{t.sendEmail}</button>
        </div>
      )}
      <div className="space-y-3">
        {applications.map((app) => (
          <div key={app.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 cursor-pointer hover:border-white/30 transition-colors" onClick={() => openDetail(app)} data-testid={`application-row-${app.id}`}>
            <div className="flex justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-white">{app.first_name} {app.last_name}</h3>
                  <span className={`px-2 py-1 text-xs uppercase text-white ${app.status === "hired" ? "bg-green-600" : app.status === "rejected" ? "bg-red-600" : "bg-blue-600"}`}>{app.status}</span>
                </div>
                <p className="text-sm text-mmg-silver-chalice">{app.email} • {app.job_title}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); setSelectedApp(app); setShowEmail(true); }} className="p-2 text-mmg-silver-chalice hover:text-white" data-testid={`email-btn-${app.id}`}><Mail size={18} /></button>
                <select value={app.status} onClick={(e) => e.stopPropagation()} onChange={(e) => handleStatusUpdate(app.id, e.target.value)} className="bg-black border border-mmg-mine-shaft text-white text-xs py-1 px-2">
                  <option value="new">{lang === "de" ? "Neu" : "New"}</option><option value="reviewing">{lang === "de" ? "In Prüfung" : "Reviewing"}</option><option value="interview">Interview</option><option value="hired">{lang === "de" ? "Eingestellt" : "Hired"}</option><option value="rejected">{lang === "de" ? "Abgelehnt" : "Rejected"}</option>
                </select>
                <ChevronRight size={18} className="text-mmg-silver-chalice" />
              </div>
            </div>
          </div>
        ))}
        {applications.length === 0 && <p className="text-mmg-silver-chalice text-center py-12">{viewArchived ? t.noArchivedItems : (lang === "de" ? "Keine Bewerbungen" : lang === "ar" ? "\u0644\u0627 \u062a\u0648\u062c\u062f \u0637\u0644\u0628\u0627\u062a" : "No applications")}</p>}
      </div>
    </div>
  );
};

// Payment Reminders Management
const PaymentReminders = () => {
  const { token, user } = useAuth();
  const { t, lang } = useLanguage();
  const [reminders, setReminders] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [remindersRes, pendingRes] = await Promise.all([
        api.get("/payment-reminders", token),
        api.get("/payment-reminders/pending", token)
      ]);
      setReminders(remindersRes.data);
      setPendingPayments(pendingRes.data);
    } catch (e) { }
    setLoading(false);
  };

  const handleSendSingle = async (paymentId) => {
    setSendingId(paymentId);
    try {
      const res = await api.post(`/payment-reminders/send-single?payment_id=${paymentId}`, {}, token);
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSendingId(null);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  const pendingLabel = lang === "de" ? "Ausstehende Zahlungen" : lang === "ar" ? "\u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0627\u0644\u0645\u0639\u0644\u0642\u0629" : "Pending Payments";
  const sendLabel = lang === "de" ? "Erinnerung senden" : lang === "ar" ? "\u0625\u0631\u0633\u0627\u0644 \u062a\u0630\u0643\u064a\u0631" : "Send Reminder";
  const daysLabel = (d) => d === null ? "" : d < 0 ? (lang === "de" ? `${Math.abs(d)} Tage überfällig` : `${Math.abs(d)} days overdue`) : d === 0 ? (lang === "de" ? "Heute fällig" : "Due today") : (lang === "de" ? `In ${d} Tagen fällig` : `Due in ${d} days`);

  return (
    <div className="space-y-6" data-testid="payment-reminders-page">
      <h1 className="font-heading font-bold text-3xl text-white">{t.paymentReminders}</h1>
      <p className="text-mmg-silver-chalice">{lang === "de" ? "Wählen Sie eine ausstehende Zahlung und senden Sie eine individuelle Erinnerung an den Kunden." : lang === "ar" ? "\u0627\u062e\u062a\u0631 \u062f\u0641\u0639\u0629 \u0645\u0639\u0644\u0642\u0629 \u0648\u0623\u0631\u0633\u0644 \u062a\u0630\u0643\u064a\u0631\u0627\u064b \u0641\u0631\u062f\u064a\u0627\u064b \u0644\u0644\u0639\u0645\u064a\u0644." : "Select a pending payment and send an individual reminder to the client."}</p>

      <div className="space-y-3">
        <h2 className="font-heading text-lg text-white">{pendingLabel}</h2>
        {pendingPayments.length === 0 && <p className="text-mmg-silver-chalice text-center py-8">{lang === "de" ? "Keine ausstehenden Zahlungen" : "No pending payments"}</p>}
        {pendingPayments.map((p) => (
          <div key={p.payment_id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex justify-between items-center" data-testid={`pending-payment-${p.payment_id}`}>
            <div>
              <p className="text-white font-heading">{p.client_name} — {p.title || p.project_title}</p>
              <p className="text-sm text-mmg-silver-chalice">{p.client_email} • €{p.amount}</p>
              {p.due_date && <p className="text-xs mt-1">
                <span className={p.days_until !== null && p.days_until < 0 ? "text-red-400" : p.days_until !== null && p.days_until <= 3 ? "text-yellow-400" : "text-mmg-silver-chalice"}>
                  {daysLabel(p.days_until)} {p.due_date ? `(${formatPortalDateShort(p.due_date, user?.timezone)})` : ""}
                </span>
              </p>}
            </div>
            <button onClick={() => handleSendSingle(p.payment_id)} disabled={sendingId === p.payment_id} className="px-4 py-2 bg-white text-black font-heading font-bold uppercase text-sm hover:bg-black hover:text-white border border-white disabled:opacity-50 whitespace-nowrap" data-testid={`send-reminder-btn-${p.payment_id}`}>
              <Send size={14} className="inline mr-2" />{sendingId === p.payment_id ? "..." : sendLabel}
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <h2 className="font-heading text-lg text-white">{t.reminderHistory}</h2>
        {reminders.map((r) => (
          <div key={r.id} className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4 flex justify-between items-center">
            <div>
              <p className="text-white">{r.email}</p>
              <p className="text-xs text-mmg-silver-chalice">{formatPortalDate(r.sent_at, user?.timezone)}</p>
            </div>
            <span className={`px-2 py-1 text-xs uppercase text-white ${r.type === "overdue" ? "bg-red-600" : "bg-yellow-600"}`}>
              {r.type === "overdue" ? t.overdue : t.upcoming}
            </span>
          </div>
        ))}
        {reminders.length === 0 && <p className="text-mmg-silver-chalice text-center py-8">{t.noReminders}</p>}
      </div>
    </div>
  );
};

// Email Settings Management
const EmailSettingsPage = () => {
  const { token } = useAuth();
  const { t, lang } = useLanguage();
  const [settings, setSettings] = useState({ 
    smtp_host: "", smtp_port: 465, smtp_user: "", smtp_password: "", 
    smtp_from_email: "", smtp_from_name: "", smtp_use_tls: false, smtp_use_ssl: true,
    email_signature_de: "", email_signature_en: "", email_signature_ar: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Provider presets for quick setup
  const providerPresets = {
    ovh: { smtp_host: "ssl0.ovh.net", smtp_port: 465, smtp_use_ssl: true, smtp_use_tls: false },
    gmail: { smtp_host: "smtp.gmail.com", smtp_port: 465, smtp_use_ssl: true, smtp_use_tls: false },
    outlook: { smtp_host: "smtp.office365.com", smtp_port: 587, smtp_use_ssl: false, smtp_use_tls: true },
    ionos: { smtp_host: "smtp.ionos.de", smtp_port: 465, smtp_use_ssl: true, smtp_use_tls: false },
    strato: { smtp_host: "smtp.strato.de", smtp_port: 465, smtp_use_ssl: true, smtp_use_tls: false },
    custom: null
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/email", token);
      if (res.data) setSettings({ ...settings, ...res.data });
    } catch (e) { }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings/email", settings, token);
      toast.success(lang === "de" ? "Einstellungen gespeichert!" : "Settings saved!");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api.post("/settings/email/test", {}, token);
      toast.success(res.data?.message || (lang === "de" ? "Test-E-Mail gesendet!" : "Test email sent!"));
    } catch (e) { toast.error(e.response?.data?.detail || (lang === "de" ? "Test fehlgeschlagen" : "Test failed")); }
    setTesting(false);
  };

  // Apply provider preset
  const handleProviderSelect = (provider) => {
    if (provider && providerPresets[provider]) {
      setSettings({ ...settings, ...providerPresets[provider] });
    }
  };

  // Update encryption based on port
  const handlePortChange = (port) => {
    const portNum = parseInt(port) || 465;
    setSettings({
      ...settings, 
      smtp_port: portNum,
      smtp_use_ssl: portNum === 465 || portNum === 995,
      smtp_use_tls: portNum === 587
    });
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t.loading}</div>;

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <h1 className="font-heading font-bold text-3xl text-white">{t.emailSettings}</h1>
      
      {/* Provider Presets */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-4">
        <label className="block text-sm text-mmg-silver-chalice mb-2">{lang === "de" ? "Schnellauswahl Anbieter" : "Quick Select Provider"}</label>
        <div className="flex flex-wrap gap-2">
          {Object.keys(providerPresets).filter(k => k !== "custom").map(provider => {
            const labels = { ovh: "OVH", gmail: "Gmail", outlook: "Outlook/Microsoft 365", ionos: "IONOS", strato: "Strato" };
            return (
            <button 
              key={provider}
              onClick={() => handleProviderSelect(provider)}
              className="px-4 py-2 border border-mmg-mine-shaft text-mmg-silver-chalice hover:border-white hover:text-white transition-all uppercase text-sm font-heading"
            >
              {labels[provider] || provider}
            </button>
            );
          })}
        </div>
      </div>
      
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">{t.smtpHost}</label>
            <input data-testid="smtp-host-input" type="text" value={settings.smtp_host} onChange={(e) => setSettings({...settings, smtp_host: e.target.value})} placeholder="ssl0.ovh.net" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">{t.smtpPort}</label>
            <input data-testid="smtp-port-input" type="number" value={settings.smtp_port} onChange={(e) => handlePortChange(e.target.value)} placeholder="465" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">{t.smtpUser}</label>
            <input data-testid="smtp-user-input" type="text" value={settings.smtp_user} onChange={(e) => setSettings({...settings, smtp_user: e.target.value})} placeholder="ihre-email@domain.com" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">{t.smtpPassword}</label>
            <input data-testid="smtp-password-input" type="password" value={settings.smtp_password} onChange={(e) => setSettings({...settings, smtp_password: e.target.value})} placeholder="********" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">{t.smtpFromEmail}</label>
            <input data-testid="smtp-from-email-input" type="email" value={settings.smtp_from_email} onChange={(e) => setSettings({...settings, smtp_from_email: e.target.value})} placeholder="info@company.com" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
          <div>
            <label className="block text-sm text-mmg-silver-chalice mb-1">{t.smtpFromName}</label>
            <input data-testid="smtp-from-name-input" type="text" value={settings.smtp_from_name} onChange={(e) => setSettings({...settings, smtp_from_name: e.target.value})} placeholder="Mahzod Media Group" className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white" />
          </div>
        </div>
        
        {/* Encryption settings */}
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-3 cursor-pointer p-3 border border-mmg-mine-shaft hover:border-white transition-all">
            <input 
              type="radio" 
              name="encryption" 
              checked={settings.smtp_use_ssl} 
              onChange={() => setSettings({...settings, smtp_use_ssl: true, smtp_use_tls: false, smtp_port: 465})}
              className="accent-white"
            />
            <div>
              <span className="text-white font-heading text-sm">SSL/TLS (Port 465)</span>
              <p className="text-xs text-mmg-silver-chalice">OVH, Gmail, IONOS, Strato</p>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer p-3 border border-mmg-mine-shaft hover:border-white transition-all">
            <input 
              type="radio" 
              name="encryption" 
              checked={settings.smtp_use_tls && !settings.smtp_use_ssl} 
              onChange={() => setSettings({...settings, smtp_use_ssl: false, smtp_use_tls: true, smtp_port: 587})}
              className="accent-white"
            />
            <div>
              <span className="text-white font-heading text-sm">STARTTLS (Port 587)</span>
              <p className="text-xs text-mmg-silver-chalice">Outlook, Microsoft 365</p>
            </div>
          </label>
        </div>
        
        {/* Connection status */}
        <div className="p-3 bg-black border border-mmg-mine-shaft text-sm flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${settings.smtp_host ? "bg-yellow-500" : "bg-red-500"}`}></div>
          <p className="text-mmg-silver-chalice">
            {settings.smtp_host ? (
              <>
                <span className="text-white">{settings.smtp_host}:{settings.smtp_port}</span>
                <span className="ml-2">({settings.smtp_use_ssl ? "SSL" : settings.smtp_use_tls ? "STARTTLS" : "Plain"})</span>
              </>
            ) : (lang === "de" ? "Nicht konfiguriert" : "Not configured")}
          </p>
        </div>

        {/* Email Signature Section */}
        <div className="mt-6 pt-6 border-t border-mmg-mine-shaft">
          <h3 className="font-heading text-lg text-white mb-3">
            {lang === "de" ? "E-Mail-Signatur" : lang === "ar" ? "توقيع البريد الإلكتروني" : "Email Signature"}
          </h3>
          <p className="text-xs text-mmg-silver-chalice mb-4">
            {lang === "de" ? "Diese Signatur wird automatisch an alle ausgehenden E-Mails angehängt." : "This signature will be automatically appended to all outgoing emails."}
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-mmg-silver-chalice mb-1">
                {lang === "de" ? "Signatur (Deutsch)" : "Signature (German)"}
              </label>
              <textarea
                value={settings.email_signature_de || ""}
                onChange={(e) => setSettings({...settings, email_signature_de: e.target.value})}
                placeholder="Mit freundlichen Grüßen,&#10;Ihr Mahzod Media Group Team&#10;&#10;Tel: +49 123 456789&#10;Web: www.mahzodmediagroup.com"
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body text-sm resize-none"
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm text-mmg-silver-chalice mb-1">
                {lang === "de" ? "Signatur (Englisch)" : "Signature (English)"}
              </label>
              <textarea
                value={settings.email_signature_en || ""}
                onChange={(e) => setSettings({...settings, email_signature_en: e.target.value})}
                placeholder="Best regards,&#10;Your Mahzod Media Group Team&#10;&#10;Phone: +49 123 456789&#10;Web: www.mahzodmediagroup.com"
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body text-sm resize-none"
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm text-mmg-silver-chalice mb-1">
                {lang === "de" ? "Signatur (Arabisch)" : "Signature (Arabic)"}
              </label>
              <textarea
                value={settings.email_signature_ar || ""}
                onChange={(e) => setSettings({...settings, email_signature_ar: e.target.value})}
                placeholder="مع أطيب التحيات،&#10;فريق Mahzod Media Group"
                className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body text-sm resize-none"
                rows={4}
                dir="rtl"
              />
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 pt-4">
          <button data-testid="save-email-settings-btn" onClick={handleSave} disabled={saving} className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50">
            <Save size={18} className="inline mr-2" />{saving ? "..." : t.save}
          </button>
          <button data-testid="test-email-btn" onClick={handleTest} disabled={testing || !settings.smtp_host} className="px-6 py-3 bg-mmg-mine-shaft text-white font-heading font-bold uppercase hover:bg-white hover:text-black border border-mmg-mine-shaft disabled:opacity-50">
            <Send size={18} className="inline mr-2" />{testing ? "..." : t.testEmail}
          </button>
        </div>
      </div>
    </div>
  );
};

// Email Template Editor
const EmailTemplateEditor = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [settings, setSettings] = useState({
    bg_color: "#000000", text_color: "#ffffff", accent_color: "#333333",
    font_family: "Helvetica Neue, Arial, sans-serif", logo_url: "", logo_position: "center",
    footer_text_de: "Diese E-Mail wurde über Mahzod Media Group versendet.",
    footer_text_en: "This email was sent via Mahzod Media Group.",
    footer_company: "Mahzod Media Group", footer_address: "", footer_phone: "", footer_website: "",
    button_color: "#ffffff", button_text_color: "#000000", button_style: "sharp",
    show_social_links: false, social_instagram: "", social_linkedin: "", social_facebook: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/email-template", token);
      if (res.data) setSettings(prev => ({ ...prev, ...res.data }));
    } catch (e) { }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings/email-template", settings, token);
      toast.success(lang === "de" ? "Design gespeichert!" : "Design saved!");
    } catch (e) { toast.error("Error"); }
    setSaving(false);
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const result = await api.upload(file, token);
      const logoUrl = `${BACKEND_URL}${result.url}`;
      updateField("logo_url", logoUrl);
      toast.success(lang === "de" ? "Logo hochgeladen!" : "Logo uploaded!");
    } catch (err) {
      toast.error(lang === "de" ? "Fehler beim Hochladen" : "Upload failed");
    }
    setUploadingLogo(false);
  };

  const removeLogo = () => {
    updateField("logo_url", "");
  };

  const fonts = [
    { value: "Helvetica Neue, Arial, sans-serif", label: "Helvetica Neue" },
    { value: "Georgia, serif", label: "Georgia" },
    { value: "Courier New, monospace", label: "Courier New" },
    { value: "Verdana, sans-serif", label: "Verdana" },
    { value: "Trebuchet MS, sans-serif", label: "Trebuchet MS" }
  ];

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  return (
    <div className="space-y-8" data-testid="email-template-editor">
      <div>
        <h1 className="font-heading font-bold text-3xl text-white">
          {lang === "de" ? "E-Mail Design" : "Email Design"}
        </h1>
        <p className="text-mmg-silver-chalice mt-1">
          {lang === "de" ? "Gestalten Sie das Erscheinungsbild Ihrer E-Mails" : "Customize the look and feel of your emails"}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings Column */}
        <div className="space-y-6">
          {/* Logo */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <h3 className="font-heading text-lg text-white mb-4">Logo</h3>
            <div className="space-y-3">
              {settings.logo_url ? (
                <div className="flex items-center gap-4">
                  <div className="w-48 h-16 bg-black border border-mmg-mine-shaft flex items-center justify-center p-2">
                    <img src={settings.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" style={{ filter: "invert(1) brightness(1.2)" }} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                      className="text-xs px-3 py-1.5 text-mmg-silver-chalice hover:text-white border border-mmg-mine-shaft"
                      data-testid="change-logo-btn">
                      {lang === "de" ? "Ändern" : "Change"}
                    </button>
                    <button onClick={removeLogo}
                      className="text-xs px-3 py-1.5 text-red-400 hover:text-red-300 border border-mmg-mine-shaft"
                      data-testid="remove-logo-btn">
                      {lang === "de" ? "Entfernen" : "Remove"}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                  className="w-full py-6 border-2 border-dashed border-mmg-mine-shaft text-mmg-silver-chalice hover:text-white hover:border-white/30 transition-colors"
                  data-testid="upload-logo-btn">
                  <Upload size={24} className="mx-auto mb-2" />
                  <span className="text-sm">{uploadingLogo ? "..." : (lang === "de" ? "Logo hochladen" : "Upload logo")}</span>
                </button>
              )}
              <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
              <p className="text-xs text-mmg-boulder">{lang === "de" ? "Empfohlen: PNG oder SVG, wird auf dunklem Hintergrund invertiert" : "Recommended: PNG or SVG, will be inverted on dark background"}</p>
            </div>
          </div>

          {/* Colors */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <h3 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Farben" : "Colors"}</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: "bg_color", label: lang === "de" ? "Hintergrund" : "Background" },
                { key: "text_color", label: lang === "de" ? "Text" : "Text" },
                { key: "accent_color", label: lang === "de" ? "Akzent / Linien" : "Accent / Lines" },
                { key: "button_color", label: lang === "de" ? "Button" : "Button" },
                { key: "button_text_color", label: lang === "de" ? "Button-Text" : "Button Text" }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <input type="color" value={settings[key]} onChange={(e) => updateField(key, e.target.value)}
                    className="w-10 h-10 border border-mmg-mine-shaft bg-transparent cursor-pointer" data-testid={`color-${key}`} />
                  <div>
                    <p className="text-xs text-mmg-silver-chalice">{label}</p>
                    <input type="text" value={settings[key]} onChange={(e) => updateField(key, e.target.value)}
                      className="w-20 text-xs bg-transparent text-white border-b border-mmg-mine-shaft" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typography */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <h3 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Schriftart" : "Font"}</h3>
            <select value={settings.font_family} onChange={(e) => updateField("font_family", e.target.value)}
              className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" data-testid="font-select">
              {fonts.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>

          {/* Footer */}
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-3">
            <h3 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Footer" : "Footer"}</h3>
            {[
              { key: "footer_company", label: lang === "de" ? "Firma" : "Company" },
              { key: "footer_address", label: lang === "de" ? "Adresse" : "Address" },
              { key: "footer_phone", label: lang === "de" ? "Telefon" : "Phone" },
              { key: "footer_website", label: "Website" },
              { key: "footer_text_de", label: lang === "de" ? "Footer-Text (DE)" : "Footer Text (DE)" },
              { key: "footer_text_en", label: lang === "de" ? "Footer-Text (EN)" : "Footer Text (EN)" }
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs text-mmg-silver-chalice mb-1">{label}</label>
                <input type="text" value={settings[key] || ""} onChange={(e) => updateField(key, e.target.value)}
                  className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" data-testid={`footer-${key}`} />
              </div>
            ))}
          </div>

          <button onClick={handleSave} disabled={saving} data-testid="save-template-settings-btn"
            className="px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white disabled:opacity-50">
            <Save size={18} className="inline mr-2" />{saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
          </button>
        </div>

        {/* Live Preview Column */}
        <div>
          <h3 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Vorschau" : "Preview"}</h3>
          <div className="border border-mmg-mine-shaft overflow-hidden" data-testid="email-preview">
            <div style={{
              fontFamily: settings.font_family,
              maxWidth: "100%",
              background: settings.bg_color,
              color: settings.text_color,
              padding: "30px"
            }}>
              <div style={{ textAlign: settings.logo_position, marginBottom: "20px", borderBottom: `1px solid ${settings.accent_color}`, paddingBottom: "15px" }}>
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" style={{ height: "50px", maxWidth: "250px", objectFit: "contain", filter: "invert(1) brightness(1.2)", display: "inline-block" }} />
                ) : (
                  <span style={{ fontWeight: 700, letterSpacing: "2px", fontSize: "18px" }}>{settings.footer_company || "MAHZOD MEDIA GROUP"}</span>
                )}
              </div>
              <div style={{ lineHeight: 1.8, fontSize: "14px" }}>
                <p>{lang === "de" ? "Sehr geehrte/r Kunde," : "Dear Customer,"}</p>
                <p style={{ marginTop: "10px" }}>{lang === "de" ? "Dies ist eine Vorschau Ihres E-Mail-Designs. So werden Ihre E-Mails an Kunden aussehen." : "This is a preview of your email design. Your emails to clients will look like this."}</p>
                <div style={{ margin: "20px 0" }}>
                  <a href="#" style={{
                    display: "inline-block",
                    background: settings.button_color,
                    color: settings.button_text_color,
                    padding: "12px 24px",
                    textDecoration: "none",
                    fontWeight: "bold",
                    fontSize: "13px",
                    letterSpacing: "1px",
                    borderRadius: settings.button_style === "rounded" ? "6px" : "0"
                  }}>{lang === "de" ? "BEISPIEL-BUTTON" : "EXAMPLE BUTTON"}</a>
                </div>
              </div>
              <div style={{ marginTop: "30px", paddingTop: "15px", borderTop: `1px solid ${settings.accent_color}`, fontSize: "11px", color: "#888" }}>
                <p style={{ margin: 0, color: settings.text_color }}>{settings.footer_company}</p>
                {settings.footer_address && <p style={{ margin: "3px 0", color: "#888" }}>{settings.footer_address}</p>}
                {settings.footer_phone && <p style={{ margin: "3px 0", color: "#888" }}>{settings.footer_phone}</p>}
                {settings.footer_website && <p style={{ margin: "3px 0", color: "#888" }}>{settings.footer_website}</p>}
                <p style={{ margin: "5px 0 0 0", color: "#888" }}>{settings[`footer_text_${lang}`] || settings.footer_text_de}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page (after Stripe checkout)
const PaymentSuccessPage = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      pollStatus(sessionId, 0);
    } else {
      setStatus("error");
    }
  }, []);

  const pollStatus = async (sessionId, attempt) => {
    if (attempt >= 5) { setStatus("timeout"); return; }
    try {
      const res = await api.get(`/checkout/status/${sessionId}`, token);
      if (res.data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(res.data);
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        setStatus("processing");
        setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
      }
    } catch (e) {
      setStatus("error");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[400px]" data-testid="payment-success-page">
      <div className="text-center space-y-6 max-w-md">
        {status === "checking" || status === "processing" ? (
          <>
            <RefreshCw size={48} className="mx-auto text-white animate-spin" />
            <h2 className="font-heading text-2xl text-white">{lang === "de" ? "Zahlung wird verarbeitet..." : "Processing payment..."}</h2>
          </>
        ) : status === "success" ? (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500" />
            <h2 className="font-heading text-2xl text-white">{lang === "de" ? "Zahlung erfolgreich!" : "Payment successful!"}</h2>
            <p className="text-mmg-silver-chalice">
              {paymentData && `${(paymentData.amount_total / 100).toFixed(2)} ${paymentData.currency?.toUpperCase()}`}
            </p>
            <a href="/portal" className="inline-block px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white">
              {lang === "de" ? "Zum Dashboard" : "Go to Dashboard"}
            </a>
          </>
        ) : (
          <>
            <AlertCircle size={64} className="mx-auto text-red-500" />
            <h2 className="font-heading text-2xl text-white">
              {status === "expired" ? (lang === "de" ? "Sitzung abgelaufen" : "Session expired") : (lang === "de" ? "Fehler bei der Zahlung" : "Payment error")}
            </h2>
            <a href="/portal/finances" className="inline-block px-6 py-3 bg-white text-black font-heading font-bold uppercase hover:bg-black hover:text-white border border-white">
              {lang === "de" ? "Zurück zu Finanzen" : "Back to Finances"}
            </a>
          </>
        )}
      </div>
    </div>
  );
};

// Company Settings Page (Firmeneinstellungen für PDF Footer etc.)
const CompanySettingsPage = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [settings, setSettings] = useState({
    name: "", legal_form: "Einzelunternehmen", show_legal_form: false, ceo_name: "", hrb_number: "", registry_court: "",
    street: "", zip: "", city: "", country: "Deutschland",
    email: "", phone: "", website: "", tax_number: "", vat_id: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/company", token);
      if (res.data) setSettings({ ...settings, ...res.data });
    } catch (e) { }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings/company", settings, token);
      toast.success(lang === "de" ? "Firmeneinstellungen gespeichert!" : "Company settings saved!");
    } catch (e) { toast.error(lang === "de" ? "Fehler beim Speichern" : "Error saving"); }
    setSaving(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-white">{lang === "de" ? "Firmeneinstellungen" : "Company Settings"}</h1>
      </div>
      
      <p className="text-sm text-mmg-silver-chalice">
        {lang === "de" 
          ? "Diese Daten werden für Impressum, SEO (Schema.org) und PDF-Dokumente verwendet."
          : "This data is used for imprint, SEO (Schema.org) and PDF documents."}
      </p>

      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
        <h2 className="font-heading font-bold text-lg text-white">{lang === "de" ? "Firma & Rechtsform" : "Company & Legal Form"}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Firmenname" : "Company Name"}</label>
            <input type="text" value={settings.name} onChange={(e) => setSettings({...settings, name: e.target.value})}
              placeholder="Mahzod Media Group" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Rechtsform" : "Legal Form"}</label>
            <select value={settings.legal_form} onChange={(e) => setSettings({...settings, legal_form: e.target.value})}
              className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
              <option value="Einzelunternehmen">Einzelunternehmen</option>
              <option value="GmbH">GmbH</option>
              <option value="UG">UG (haftungsbeschränkt)</option>
              <option value="GbR">GbR</option>
              <option value="OHG">OHG</option>
              <option value="KG">KG</option>
              <option value="AG">AG</option>
            </select>
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input type="checkbox" checked={settings.show_legal_form} onChange={(e) => setSettings({...settings, show_legal_form: e.target.checked})} className="accent-white" />
              <span className="text-xs text-mmg-silver-chalice">{lang === "de" ? "Im Impressum anzeigen" : "Show in Imprint"}</span>
            </label>
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Inhaber / Geschäftsführer (CEO)" : "Owner / CEO"}</label>
            <input type="text" value={settings.ceo_name} onChange={(e) => setSettings({...settings, ceo_name: e.target.value})}
              placeholder="Mohab Hoffmann-Yassin" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Handelsregister-Nr. (optional)" : "Trade Register No. (optional)"}</label>
            <input type="text" value={settings.hrb_number} onChange={(e) => setSettings({...settings, hrb_number: e.target.value})}
              placeholder="HRB 12345" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          {settings.hrb_number && (
            <div className="md:col-span-2">
              <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Registergericht" : "Registry Court"}</label>
              <input type="text" value={settings.registry_court} onChange={(e) => setSettings({...settings, registry_court: e.target.value})}
                placeholder="Amtsgericht Osnabrück" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
          )}
        </div>
      </div>

      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
        <h2 className="font-heading font-bold text-lg text-white">{lang === "de" ? "Adresse" : "Address"}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Straße" : "Street"}</label>
            <input type="text" value={settings.street} onChange={(e) => setSettings({...settings, street: e.target.value})}
              placeholder="Musterstraße 1" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "PLZ" : "Postal Code"}</label>
            <input type="text" value={settings.zip} onChange={(e) => setSettings({...settings, zip: e.target.value})}
              placeholder="49497" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Stadt" : "City"}</label>
            <input type="text" value={settings.city} onChange={(e) => setSettings({...settings, city: e.target.value})}
              placeholder="Mettingen" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Land" : "Country"}</label>
            <input type="text" value={settings.country} onChange={(e) => setSettings({...settings, country: e.target.value})}
              placeholder="Deutschland" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
        </div>
      </div>

      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
        <h2 className="font-heading font-bold text-lg text-white">{lang === "de" ? "Kontakt" : "Contact"}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "E-Mail (für Dokumente)" : "Email (for documents)"}</label>
            <input type="email" value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})}
              placeholder="info@mahzod.com" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Telefon" : "Phone"}</label>
            <input type="tel" value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})}
              placeholder="+49 123 456789" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Website" : "Website"}</label>
            <input type="url" value={settings.website} onChange={(e) => setSettings({...settings, website: e.target.value})}
              placeholder="www.mahzod.com" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
        </div>
      </div>

      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-6">
        <h2 className="font-heading font-bold text-lg text-white">{lang === "de" ? "Steuer" : "Tax"}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Steuernummer" : "Tax Number"}</label>
            <input type="text" value={settings.tax_number} onChange={(e) => setSettings({...settings, tax_number: e.target.value})}
              placeholder="12/345/67890" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "USt-IdNr." : "VAT ID"}</label>
            <input type="text" value={settings.vat_id} onChange={(e) => setSettings({...settings, vat_id: e.target.value})}
              placeholder="DE123456789" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50">
        <Save size={18} /> {saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
      </button>
    </div>
  );
};

// Payment Settings Page (Stripe, PayPal, Bank)
const PaymentSettingsPage = () => {
  const { token } = useAuth();
  const { t, lang } = useLanguage();
  const [settings, setSettings] = useState({
    stripe_enabled: false, stripe_publishable_key: "", stripe_secret_key: "", stripe_mode: "test",
    paypal_enabled: false, paypal_client_id: "", paypal_client_secret: "", paypal_mode: "sandbox",
    bank_enabled: true, bank_account_holder: "", bank_iban: "", bank_bic: "", bank_name: "",
    default_payment_method: "link"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/payment", token);
      if (res.data) setSettings({ ...settings, ...res.data });
    } catch (e) { }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings/payment", settings, token);
      toast.success(lang === "de" ? "Zahlungseinstellungen gespeichert!" : "Payment settings saved!");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  if (loading) return <div className="text-mmg-silver-chalice">{t?.loading || "Loading..."}</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading font-bold text-3xl text-white">
          {lang === "de" ? "Zahlungseinstellungen" : "Payment Settings"}
        </h1>
        <p className="text-mmg-silver-chalice mt-1">
          {lang === "de" ? "Konfigurieren Sie Ihre Zahlungsintegrationen" : "Configure your payment integrations"}
        </p>
      </div>

      {/* Default Payment Method */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h3 className="font-heading text-lg text-white mb-4">{lang === "de" ? "Standard-Zahlungsmethode" : "Default Payment Method"}</h3>
        <select
          value={settings.default_payment_method}
          onChange={(e) => setSettings({...settings, default_payment_method: e.target.value})}
          className="w-full max-w-md py-3 px-4 bg-black border border-mmg-mine-shaft text-white"
        >
          <option value="link">{lang === "de" ? "Manueller Zahlungslink" : "Manual Payment Link"}</option>
          <option value="stripe">Stripe</option>
          <option value="paypal">PayPal</option>
          <option value="bank">{lang === "de" ? "Banküberweisung" : "Bank Transfer"}</option>
        </select>
      </div>

      {/* Stripe */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg text-white">Stripe</h3>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.stripe_enabled} onChange={(e) => setSettings({...settings, stripe_enabled: e.target.checked})} className="h-4 w-4" />
            <span className="text-sm text-mmg-silver-chalice">{lang === "de" ? "Aktiviert" : "Enabled"}</span>
          </label>
        </div>
        {settings.stripe_enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">Publishable Key</label>
              <input type="text" value={settings.stripe_publishable_key || ""} onChange={(e) => setSettings({...settings, stripe_publishable_key: e.target.value})}
                placeholder="pk_test_..." className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">Secret Key</label>
              <input type="password" value={settings.stripe_secret_key || ""} onChange={(e) => setSettings({...settings, stripe_secret_key: e.target.value})}
                placeholder="sk_test_..." className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">Mode</label>
              <select value={settings.stripe_mode} onChange={(e) => setSettings({...settings, stripe_mode: e.target.value})}
                className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* PayPal */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg text-white">PayPal</h3>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.paypal_enabled} onChange={(e) => setSettings({...settings, paypal_enabled: e.target.checked})} className="h-4 w-4" />
            <span className="text-sm text-mmg-silver-chalice">{lang === "de" ? "Aktiviert" : "Enabled"}</span>
          </label>
        </div>
        {settings.paypal_enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">Client ID</label>
              <input type="text" value={settings.paypal_client_id || ""} onChange={(e) => setSettings({...settings, paypal_client_id: e.target.value})}
                placeholder="AX..." className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">Client Secret</label>
              <input type="password" value={settings.paypal_client_secret || ""} onChange={(e) => setSettings({...settings, paypal_client_secret: e.target.value})}
                placeholder="..." className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">Mode</label>
              <select value={settings.paypal_mode} onChange={(e) => setSettings({...settings, paypal_mode: e.target.value})}
                className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
                <option value="sandbox">Sandbox (Test)</option>
                <option value="live">Live</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bank Transfer */}
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg text-white">{lang === "de" ? "Banküberweisung" : "Bank Transfer"}</h3>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.bank_enabled} onChange={(e) => setSettings({...settings, bank_enabled: e.target.checked})} className="h-4 w-4" />
            <span className="text-sm text-mmg-silver-chalice">{lang === "de" ? "Aktiviert" : "Enabled"}</span>
          </label>
        </div>
        {settings.bank_enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Kontoinhaber" : "Account Holder"}</label>
              <input type="text" value={settings.bank_account_holder || ""} onChange={(e) => setSettings({...settings, bank_account_holder: e.target.value})}
                placeholder="Mahzod Media Group" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">IBAN</label>
              <input type="text" value={settings.bank_iban || ""} onChange={(e) => setSettings({...settings, bank_iban: e.target.value})}
                placeholder="DE89..." className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">BIC/SWIFT</label>
              <input type="text" value={settings.bank_bic || ""} onChange={(e) => setSettings({...settings, bank_bic: e.target.value})}
                placeholder="COBADEFFXXX" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Bankname" : "Bank Name"}</label>
              <input type="text" value={settings.bank_name || ""} onChange={(e) => setSettings({...settings, bank_name: e.target.value})}
                placeholder="Commerzbank" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
            </div>
          </div>
        )}
      </div>

      <button onClick={handleSave} disabled={saving}
        className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50">
        <Save size={18} /> {saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
      </button>
    </div>
  );
};

// Invoice & Offer Numbering Settings Component
const InvoiceNumberingSettings = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [activeTab, setActiveTab] = useState("invoice"); // invoice, offer, reference
  const [invoiceSettings, setInvoiceSettings] = useState({
    prefix: "RE", separator: "-", include_year: true, year_format: "full", number_digits: 4, reset_yearly: true, current_number: 0, next_number_preview: ""
  });
  const [offerSettings, setOfferSettings] = useState({
    prefix: "AN", separator: "-", include_year: true, year_format: "full", number_digits: 4, reset_yearly: true, current_number: 0, next_number_preview: ""
  });
  const [refPrefix, setRefPrefix] = useState("MMG");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetValue, setResetValue] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAllSettings(); }, []);

  const fetchAllSettings = async () => {
    try {
      const [invRes, offRes, refRes] = await Promise.all([
        api.get("/settings/invoice-numbering", token),
        api.get("/settings/offer-numbering", token).catch(() => ({ data: offerSettings })),
        api.get("/settings/payment-reference", token).catch(() => ({ data: { prefix: "MMG" } }))
      ]);
      setInvoiceSettings(invRes.data);
      setOfferSettings(offRes.data);
      setRefPrefix(refRes.data?.prefix || "MMG");
    } catch (e) { }
    setLoading(false);
  };

  const handleSaveInvoice = async () => {
    setSaving(true);
    try {
      await api.put("/settings/invoice-numbering", {
        prefix: invoiceSettings.prefix, separator: invoiceSettings.separator, include_year: invoiceSettings.include_year,
        year_format: invoiceSettings.year_format, number_digits: invoiceSettings.number_digits, reset_yearly: invoiceSettings.reset_yearly
      }, token);
      toast.success(lang === "de" ? "Rechnungs-Einstellungen gespeichert!" : "Invoice settings saved!");
      fetchAllSettings();
    } catch (e) { toast.error(lang === "de" ? "Fehler" : "Error"); }
    setSaving(false);
  };

  const handleSaveOffer = async () => {
    setSaving(true);
    try {
      await api.put("/settings/offer-numbering", {
        prefix: offerSettings.prefix, separator: offerSettings.separator, include_year: offerSettings.include_year,
        year_format: offerSettings.year_format, number_digits: offerSettings.number_digits, reset_yearly: offerSettings.reset_yearly
      }, token);
      toast.success(lang === "de" ? "Angebots-Einstellungen gespeichert!" : "Offer settings saved!");
      fetchAllSettings();
    } catch (e) { toast.error(lang === "de" ? "Fehler" : "Error"); }
    setSaving(false);
  };

  const handleSaveRef = async () => {
    setSaving(true);
    try {
      await api.put(`/settings/payment-reference?prefix=${refPrefix}`, {}, token);
      toast.success(lang === "de" ? "Verwendungszweck-Prefix gespeichert!" : "Payment reference prefix saved!");
    } catch (e) { toast.error(lang === "de" ? "Fehler" : "Error"); }
    setSaving(false);
  };

  const handleReset = async (type) => {
    const val = parseInt(resetValue);
    if (isNaN(val) || val < 1) { toast.error(lang === "de" ? "Bitte gültige Zahl" : "Invalid number"); return; }
    if (!window.confirm(lang === "de" ? `Zähler auf ${val} zurücksetzen?` : `Reset to ${val}?`)) return;
    try {
      await api.post(`/settings/${type}-numbering/reset?new_start=${val}`, {}, token);
      toast.success(lang === "de" ? "Zurückgesetzt!" : "Reset!");
      setResetValue("");
      fetchAllSettings();
    } catch (e) { toast.error("Error"); }
  };

  const generatePreview = (s) => {
    const year = new Date().getFullYear();
    const yearStr = s.year_format === "full" ? year : String(year).slice(-2);
    const numStr = String((s.current_number || 0) + 1).padStart(s.number_digits, "0");
    const parts = [s.prefix];
    if (s.include_year) parts.push(yearStr);
    parts.push(numStr);
    return parts.join(s.separator);
  };

  const renderNumberSettings = (settings, setSettings, type, handleSave) => (
    <div className="space-y-6">
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <p className="text-xs text-mmg-silver-chalice uppercase mb-2">{lang === "de" ? "Nächste Nummer" : "Next Number"}</p>
        <p className="font-heading font-bold text-3xl text-white">{settings.next_number_preview || generatePreview(settings)}</p>
        <p className="text-xs text-mmg-boulder mt-2">{lang === "de" ? `Zählerstand: ${settings.current_number}` : `Counter: ${settings.current_number}`}</p>
      </div>
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Präfix" : "Prefix"}</label>
            <input type="text" value={settings.prefix} onChange={(e) => setSettings({...settings, prefix: e.target.value.toUpperCase()})} maxLength={10} className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm uppercase" />
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Trennzeichen" : "Separator"}</label>
            <select value={settings.separator} onChange={(e) => setSettings({...settings, separator: e.target.value})} className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
              <option value="-">-</option><option value="/">/</option><option value="_">_</option><option value=".">.</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Ziffern" : "Digits"}</label>
            <select value={settings.number_digits} onChange={(e) => setSettings({...settings, number_digits: parseInt(e.target.value)})} className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm">
              <option value={3}>3 (001)</option><option value={4}>4 (0001)</option><option value={5}>5 (00001)</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={settings.include_year} onChange={(e) => setSettings({...settings, include_year: e.target.checked})} className="accent-white" />
            <span className="text-sm text-white">{lang === "de" ? "Jahr einbeziehen" : "Include year"}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={settings.reset_yearly} onChange={(e) => setSettings({...settings, reset_yearly: e.target.checked})} className="accent-white" />
            <span className="text-sm text-white">{lang === "de" ? "Jährlich zurücksetzen" : "Reset yearly"}</span>
          </label>
          {settings.include_year && (
            <div className="flex gap-3">
              <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={settings.year_format === "full"} onChange={() => setSettings({...settings, year_format: "full"})} className="accent-white" /><span className="text-sm text-white">2026</span></label>
              <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={settings.year_format === "short"} onChange={() => setSettings({...settings, year_format: "short"})} className="accent-white" /><span className="text-sm text-white">26</span></label>
            </div>
          )}
        </div>
        <div className="p-3 border border-dashed border-mmg-mine-shaft">
          <span className="text-xs text-mmg-silver-chalice mr-2">{lang === "de" ? "Vorschau:" : "Preview:"}</span>
          <span className="font-heading font-bold text-xl text-white">{generatePreview(settings)}</span>
        </div>
        <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 border border-white bg-white text-black px-5 py-2 font-heading font-bold uppercase text-sm hover:bg-black hover:text-white transition-colors disabled:opacity-50">
          <Save size={16} /> {saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
        </button>
      </div>
      <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
        <h3 className="font-heading text-sm text-white mb-3">{lang === "de" ? "Zähler zurücksetzen" : "Reset Counter"}</h3>
        <div className="flex gap-2">
          <input type="number" value={resetValue} onChange={(e) => setResetValue(e.target.value)} placeholder={lang === "de" ? "Neue Nummer" : "New number"} min="1" className="flex-1 py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm" />
          <button onClick={() => handleReset(type)} className="px-3 py-2 border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black text-sm transition-colors">{lang === "de" ? "Reset" : "Reset"}</button>
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="text-mmg-silver-chalice">{lang === "de" ? "Laden..." : "Loading..."}</div>;

  return (
    <div className="space-y-6">
      <h1 className="font-heading font-bold text-3xl text-white">{lang === "de" ? "Dokumenten-Nummerierung" : "Document Numbering"}</h1>
      
      {/* Tabs */}
      <div className="flex gap-1 border-b border-mmg-mine-shaft">
        <button onClick={() => setActiveTab("invoice")} className={`px-4 py-2 font-heading text-sm uppercase ${activeTab === "invoice" ? "text-white border-b-2 border-white" : "text-mmg-silver-chalice hover:text-white"}`}>
          {lang === "de" ? "Rechnungen" : "Invoices"}
        </button>
        <button onClick={() => setActiveTab("offer")} className={`px-4 py-2 font-heading text-sm uppercase ${activeTab === "offer" ? "text-white border-b-2 border-white" : "text-mmg-silver-chalice hover:text-white"}`}>
          {lang === "de" ? "Angebote" : "Offers"}
        </button>
        <button onClick={() => setActiveTab("reference")} className={`px-4 py-2 font-heading text-sm uppercase ${activeTab === "reference" ? "text-white border-b-2 border-white" : "text-mmg-silver-chalice hover:text-white"}`}>
          {lang === "de" ? "Verwendungszweck" : "Reference"}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "invoice" && renderNumberSettings(invoiceSettings, setInvoiceSettings, "invoice", handleSaveInvoice)}
      {activeTab === "offer" && renderNumberSettings(offerSettings, setOfferSettings, "offer", handleSaveOffer)}
      {activeTab === "reference" && (
        <div className="space-y-6">
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <p className="text-xs text-mmg-silver-chalice uppercase mb-2">{lang === "de" ? "Beispiel Verwendungszweck" : "Example Reference"}</p>
            <p className="font-heading font-bold text-3xl text-white">{refPrefix}-A1B2C3D4</p>
            <p className="text-xs text-mmg-boulder mt-2">{lang === "de" ? "Wird automatisch bei Überweisungen generiert" : "Auto-generated for bank transfers"}</p>
          </div>
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
            <div>
              <label className="block text-xs text-mmg-silver-chalice mb-1">{lang === "de" ? "Prefix (z.B. Firmenname)" : "Prefix (e.g. company name)"}</label>
              <input type="text" value={refPrefix} onChange={(e) => setRefPrefix(e.target.value.toUpperCase())} maxLength={10} placeholder="MMG" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white text-sm uppercase" />
            </div>
            <p className="text-xs text-mmg-silver-chalice">{lang === "de" ? "Der Kunde gibt diesen Verwendungszweck bei der Überweisung an, damit die Zahlung automatisch zugeordnet werden kann." : "Customers use this reference for bank transfers to enable automatic matching."}</p>
            <button onClick={handleSaveRef} disabled={saving} className="inline-flex items-center gap-2 border border-white bg-white text-black px-5 py-2 font-heading font-bold uppercase text-sm hover:bg-black hover:text-white transition-colors disabled:opacity-50">
              <Save size={16} /> {saving ? "..." : (lang === "de" ? "Speichern" : "Save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Portal 404 Not Found Component
const PortalNotFound = () => {
  const { t, lang } = useLanguage();
  
  const texts = {
    de: { title: "Seite nicht gefunden", text: "Die angeforderte Seite existiert nicht.", back: "Zurück" },
    en: { title: "Page not found", text: "The requested page does not exist.", back: "Go back" },
    ar: { title: "الصفحة غير موجودة", text: "الصفحة المطلوبة غير موجودة.", back: "رجوع" }
  };
  const txt = texts[lang] || texts.de;
  
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center max-w-md">
        <AlertCircle size={64} className="text-mmg-silver-chalice mx-auto mb-6" />
        <h1 className="font-heading font-bold text-4xl text-white mb-4">404</h1>
        <h2 className="font-heading font-bold text-2xl text-white mb-4">{txt.title}</h2>
        <p className="font-body text-mmg-silver-chalice mb-8">{txt.text}</p>
        <button 
          onClick={() => window.history.back()} 
          className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
          data-testid="portal-notfound-back-btn"
        >
          <ArrowLeft size={18} /> {txt.back}
        </button>
      </div>
    </div>
  );
};

// Main Portal Component with Routing
const Portal = () => {
  const { user, loading, token } = useAuth();
  const { t, lang } = useLanguage();
  const path = window.location.pathname;
  const projectId = path.match(/\/portal\/projects\/([^\/]+)/)?.[1];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white font-heading">{t.loading}</div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const isAdmin = user?.role === "admin" || user?.role === "employee";

  // Valid portal routes
  const validRoutes = [
    "/portal",
    "/portal/projects",
    "/portal/users",
    "/portal/payments",
    "/portal/subscriptions",
    "/portal/finances",
    "/portal/proposals",
    "/portal/inquiries",
    "/portal/documents",
    "/portal/jobs",
    "/portal/applications",
    "/portal/company-settings",
    "/portal/email-settings",
    "/portal/email-template",
    "/portal/payment-settings",
    "/portal/invoice-numbering",
    "/portal/payment-success",
    "/portal/reminders",
    "/portal/affiliate",
    "/portal/site",
    "/portal/mailbox",
    "/portal/messages",
    "/portal/settings",
    "/portal/summary"
  ];

  const isValidRoute = validRoutes.includes(path) || 
    path.startsWith("/portal/projects/") || 
    path.startsWith("/portal/site/");

  const renderContent = () => {
    // Check for invalid routes first
    if (!isValidRoute && path !== "/portal") {
      return <PortalNotFound />;
    }
    
    if (projectId) return <ProjectDetail projectId={projectId} />;
    if (path === "/portal/projects") return <ProjectsList />;
    if (path === "/portal/users" && isAdmin) return <UsersManagement />;
    if (path === "/portal/payments") return <FinancesPage />;
    if (path === "/portal/subscriptions") return <FinancesPage />;
    if (path === "/portal/finances") return <FinancesPage />;
    if (path === "/portal/proposals") return <ProposalsManagement />;
    if (path === "/portal/inquiries" && isAdmin) return <ContactInquiries />;
    if (path === "/portal/documents" && isAdmin) return <DocumentsManagement />;
    if (path === "/portal/jobs" && isAdmin) return <JobsManagement />;
    if (path === "/portal/applications" && isAdmin) return <ApplicationsManagement />;
    if (path === "/portal/email-settings" && isAdmin) return <EmailSettingsPage />;
    if (path === "/portal/email-template" && isAdmin) return <EmailTemplateEditor />;
    if (path === "/portal/company-settings" && isAdmin) return <CompanySettingsPage />;
    if (path === "/portal/payment-settings" && isAdmin) return <PaymentSettingsPage />;
    if (path === "/portal/invoice-numbering" && isAdmin) return <InvoiceNumberingSettings />;
    if (path === "/portal/payment-success") return <PaymentSuccessPage />;
    if (path === "/portal/reminders" && isAdmin) return <PaymentReminders />;
    if (path === "/portal/affiliate") return <AffiliateOverview />;
    if ((path === "/portal/site" || path.startsWith("/portal/site/")) && isAdmin) return <SiteManager token={token} lang={lang} />;
    if (path === "/portal/mailbox" && isAdmin) return <Mailbox token={token} lang={lang} />;
    if (path === "/portal/messages" && isAdmin) return <MessagesOverview />;
    if (path === "/portal/settings") return <SettingsPage />;
    if (path === "/portal/summary" && !isAdmin) return <ClientSummary />;
    return isAdmin ? <AdminDashboard /> : <ClientDashboard />;
  };

  return (
    <DashboardLayout>
      {renderContent()}
    </DashboardLayout>
  );
};

// Export with Providers
const PortalWrapper = () => {
  const [lang, setLang] = useState(sessionStorage.getItem("portal_lang") || "de");
  const t = translations[lang];

  const handleSetLang = useCallback((newLang) => {
    sessionStorage.setItem("portal_lang", newLang);
    setLang(newLang);
  }, []);

  const langValue = useMemo(() => ({ t, lang, setLang: handleSetLang }), [t, lang, handleSetLang]);

  return (
    <LanguageContext.Provider value={langValue}>
      <AuthProvider>
        <Portal />
      </AuthProvider>
    </LanguageContext.Provider>
  );
};

export default PortalWrapper;
